// src/Sourcing/BorrowerSourcing.jsx
import React from "react";
import { createPortal } from "react-dom";
import {
  ChevronDown,
  X,
  Bookmark,
  BookmarkCheck,
  ShoppingCart,
  Trash2,
  SlidersHorizontal,
  Route,
  Check,
  AlertTriangle,
  Copy,
  RotateCcw,
  Loader2,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  MessageSquareHeart,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import TopNav from "../../components/TopNav.jsx";
import CartMascotNudge from "../../components/CartMascotNudge.jsx";
import { calculateMortgageProducts } from "../../api/sourcing-api/mortgageCalculation.js";
import { trackProductClick } from "../../api/clickTracking.js";
import { STORAGE_KEYS } from "../../utils/constants.js";
import { rememberSourcingCategory, shortlistPathForCategory } from "../../utils/sourcingCategory.js";
import { isComplexBtlProduct, pickComplexBtlPropertyFields } from "../../utils/complexBtlProduct.js";
import {
  amortisedPayment,
  arrangementFee as calculateArrangementFee,
  calculateDsc,
  interestOnlyPayment,
  isWeeksBasedPropertyType,
  ltvForChecks,
  mortgageFromLtv,
  passesLoanSizeCheck,
  passesLtvCheck,
  passesPropertySizeCheck,
  repaymentOverProductTerm,
  termMonths,
} from "../../utils/btlCalculator.js";
import { isAuthenticated, getBorrowerEmail } from "../../utils/auth.js";
import ReviewPromptModal from "../../components/reviews/ReviewPromptModal.jsx";
import { useSourcingEngagement, useReviewPrompt, REVIEW_PROMPT_DELAY_MS, SOURCING_FEEDBACK_BUTTON_CLASS, SOURCING_FEEDBACK_MOBILE_BAR_BUTTON_CLASS } from "../../hooks/useSourcingEngagement.js";
import SourcingSignupPrompt from "../../components/auth/SourcingSignupPrompt.jsx";
import { isMobileViewport } from "../../components/sourcing/MobileQuickValidationDialog.jsx";
import SourcingLiveCount from "../../components/sourcing/SourcingLiveCount.jsx";
import {
  getShortlistedProducts,
  addShortlistedProduct,
  removeShortlistedProductByMortgageProduct,
  deleteAllShortlistedProducts,
} from "../../api/borrowerPortal/shortlistedModal.js";

/** Request cap for mortgage sourcing API (fetch full catalog; backend may enforce its own maximum). */
const SOURCING_PRODUCTS_FETCH_LIMIT = 100000;

/** Number of product cards a guest can see before the sign-up gate. */
const GUEST_FREE_PRODUCTS = 1;

/* =============================================================================
   Error infra
============================================================================= */

const ErrorCtx = React.createContext({ reportError: (e, meta) => { } });
function useError() {
  return React.useContext(ErrorCtx);
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    this.setState({ info });
    if (this.props.onError) this.props.onError(error, info);
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }
  render() {
    if (!this.state.hasError) return this.props.children;

    const details =
      (this.state.error?.stack || String(this.state.error || "")) +
      (this.state.info?.componentStack || "");

    return (
      <div className="fixed bottom-4 left-1/2 z-[1000] -translate-x-1/2 w-[min(680px,95vw)] rounded-2xl border border-red-200 bg-white shadow-2xl">
        <div className="flex items-start gap-3 p-4">
          <div className="mt-0.5">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-red-700">Something went wrong</div>
            <div className="mt-1 text-sm text-slate-700">
              An unexpected error occurred. You can copy the details and reload the page.
            </div>
            <details className="mt-2 text-xs text-slate-600 whitespace-pre-wrap max-h-40 overflow-auto">
              <summary className="cursor-pointer select-none">Error details</summary>
              {details}
            </details>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 p-3">
          <button
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(details);
              } catch { }
            }}
          >
            <Copy className="h-4 w-4" /> Copy details
          </button>
          <button
            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
            onClick={() => window.location.reload()}
          >
            <RotateCcw className="h-4 w-4" /> Reload
          </button>
        </div>
      </div>
    );
  }
}

function ErrorBanner({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed bottom-4 left-1/2 z-[900] -translate-x-1/2 w-[min(560px,95vw)] rounded-2xl border border-amber-200 bg-white shadow-xl">
      <div className="flex items-start gap-3 p-3">
        <div className="min-w-0">
          <div className="text-sm text-slate-800">{message}</div>
        </div>
      </div>
      <div className="flex justify-end border-t border-slate-200 p-2">
        <button
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50"
          onClick={onClose}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function PolarBearOnboarding({ open, onStartTour, onSkip, productLabel = "BTL" }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[950] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" aria-hidden="true" />

      <div className="relative z-10 w-[min(480px,100%)] rounded-3xl bg-white shadow-2xl border border-sky-100 p-6 md:p-7">
        <div className="flex items-start">
          <div className="min-w-0 pr-8">
            <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-1.5">
              Get started with {productLabel} sourcing
            </h2>
            <p className="text-sm text-slate-600 mb-3">
              A quick 5-step tour of the key controls so you can find the right product with confidence.
            </p>

            <ul className="space-y-1.5 text-sm text-slate-700 mb-4">
              <li>
                <span className="font-semibold text-slate-900">Quick bar at the top</span>{" "}
                – enter <span className="font-medium">property value, deposit, mortgage</span> and{" "}
                <span className="font-medium">rent</span> to drive all calculations.
              </li>
              <li>
                <span className="font-semibold text-slate-900 md:hidden">Filters button</span>
                <span className="font-semibold text-slate-900 hidden md:inline">Left-hand filters</span>{" "}
                – narrow products by <span className="font-medium">LTV, term, product type, EPC</span>{" "}
                and more to match your case.
              </li>
              <li>
                <span className="font-semibold text-slate-900">Results panel</span>{" "}
                – compare <span className="font-medium">monthly payment, total cost, fees</span> and{" "}
                <span className="font-medium">ICR</span> before shortlisting.
              </li>
            </ul>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={onStartTour}
                className="inline-flex items-center justify-center rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1"
              >
                Start guided tour
              </button>
              <button
                type="button"
                onClick={onSkip}
                className="text-xs text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline"
              >
                I&apos;ll explore on my own
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={onSkip}
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200"
            aria-label="Close onboarding"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

const BTL_SIGNUP_PROMPT_KEY = "btl_sourcing_signup_prompt_seen";
const BTL_CART_MASCOT_SEEN_KEY = "btl_cart_mascot_seen";

const SHORTLIST_APPLY_LABEL = (
  <>
    Shortlist & <span className="font-bold">Apply</span>
  </>
);

function isMobileTourViewport() {
  return typeof window !== "undefined" && window.innerWidth < 768;
}

function resolveTourTargetElement(stepId, { cartOpen } = {}) {
  if (stepId === "filters" && isMobileTourViewport()) {
    return (
      document.getElementById("mobile-filters-drawer") ||
      document.getElementById("mobile-filters-trigger")
    );
  }
  if (stepId === "shortlist-cart-window" && isMobileTourViewport()) {
    if (cartOpen) {
      return document.getElementById("shortlist-cart-window");
    }
    return document.getElementById("mobile-shortlist-bar");
  }
  return document.getElementById(stepId);
}

function padTourRect(rect, inset = 8) {
  return {
    top: rect.top - inset,
    left: rect.left - inset,
    width: rect.width + inset * 2,
    height: rect.height + inset * 2,
    bottom: rect.bottom + inset,
    right: rect.right + inset,
  };
}

function clampTour(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

const TOUR_TOP_CLEARANCE_MOBILE = 84;
const TOUR_TOP_CLEARANCE_DESKTOP = 16;
const TOUR_BUBBLE_GAP = 16;

function estimateTourBubbleSize(windowW, isMobile) {
  const width = isMobile ? Math.min(500, Math.max(300, windowW - 16)) : 480;
  return { width, height: isMobile ? 148 : 140 };
}

function getTourRects(stepId, cartOpen) {
  const el = resolveTourTargetElement(stepId, { cartOpen });
  if (!el) return null;

  const highlight = padTourRect(el.getBoundingClientRect());
  let anchor = highlight;

  if (isMobileTourViewport()) {
    if (stepId === "filters" && el.id === "mobile-filters-drawer") {
      const trigger = document.getElementById("mobile-filters-trigger");
      anchor = trigger
        ? padTourRect(trigger.getBoundingClientRect())
        : { ...highlight, top: highlight.top - 1, height: 1, bottom: highlight.top };
    } else if (stepId === "results-panel") {
      const anchorHeight = Math.min(96, highlight.height);
      anchor = {
        ...highlight,
        height: anchorHeight,
        bottom: highlight.top + anchorHeight,
      };
    } else if (stepId === "shortlist-cart-window" && cartOpen && el.id === "shortlist-cart-window") {
      const anchorHeight = Math.min(72, highlight.height);
      anchor = {
        ...highlight,
        height: anchorHeight,
        bottom: highlight.top + anchorHeight,
      };
    } else if (stepId === "shortlist-cart-window" && !cartOpen) {
      anchor = highlight;
    }
  }

  return { highlight, anchor };
}

function computeTourBubblePlacement({
  anchor,
  windowW,
  windowH,
  bubbleW,
  bubbleH,
  stepId,
  isMobile,
}) {
  const pad = isMobile ? 8 : 20;
  const minTop = isMobile ? TOUR_TOP_CLEARANCE_MOBILE : TOUR_TOP_CLEARANCE_DESKTOP;
  const gap = TOUR_BUBBLE_GAP;
  const centerLeft = clampTour(
    anchor.left + anchor.width / 2 - bubbleW / 2,
    pad,
    windowW - bubbleW - pad
  );

  const candidates = [];

  if (!isMobile) {
    if (stepId === "results-panel") {
      candidates.push({
        top: clampTour(anchor.top + anchor.height / 2 - bubbleH / 2, minTop, windowH - bubbleH - pad),
        left: anchor.left - bubbleW - gap,
      });
    }
    if (stepId === "filters") {
      candidates.push({
        top: clampTour(anchor.top + anchor.height / 2 - bubbleH / 2, minTop, windowH - bubbleH - pad),
        left: anchor.right + gap,
      });
    }
  }

  if (isMobile && stepId === "filters") {
    candidates.push({
      top: minTop,
      left: centerLeft,
    });
  }

  if (isMobile && stepId === "shortlist-cart-window") {
    candidates.push({
      top: minTop,
      left: centerLeft,
    });
  }

  candidates.push(
    { top: anchor.bottom + gap, left: centerLeft },
    { top: anchor.top - gap - bubbleH, left: centerLeft },
    {
      top: clampTour((windowH - bubbleH) / 2, minTop, windowH - bubbleH - pad),
      left: clampTour((windowW - bubbleW) / 2, pad, windowW - bubbleW - pad),
    },
    { top: minTop, left: centerLeft }
  );

  for (const candidate of candidates) {
    const left = clampTour(candidate.left, pad, windowW - bubbleW - pad);
    const top = clampTour(candidate.top, minTop, windowH - bubbleH - pad);
    if (
      top >= minTop &&
      left >= pad &&
      top + bubbleH <= windowH - pad &&
      left + bubbleW <= windowW - pad
    ) {
      return { top, left };
    }
  }

  return {
    top: clampTour(anchor.bottom + gap, minTop, windowH - bubbleH - pad),
    left: centerLeft,
  };
}

function BtlTour({
  open,
  stepIndex,
  onNext,
  onPrev,
  onClose,
  onEnsureStep,
  canNext,
  nextHint,
  cartOpen,
  layoutKey,
}) {
  const [tourRects, setTourRects] = React.useState(null);
  const [windowSize, setWindowSize] = React.useState({ width: 0, height: 0 });
  const [measuredBubble, setMeasuredBubble] = React.useState(null);
  const [scrollSyncing, setScrollSyncing] = React.useState(false);
  const bubbleRef = React.useRef(null);
  const scrollIdleTimerRef = React.useRef(null);
  const scrollRafRef = React.useRef(0);

  const steps = [
    {
      id: "quickbar",
      title: "Set your numbers",
      body: "Enter property value, deposit, mortgage and rent in the quick bar.",
      mobileBody: "Enter property value, deposit, mortgage and rent in the quick bar.",
      mascot: "/Smiling.png",
    },
    {
      id: "results-panel",
      title: "Browse products",
      body: "Matching products appear here. Compare payments, cost, fees and ICR.",
      mobileBody: "Products appear here in compact cards. Compare payments, cost, fees and ICR.",
      mascot: "/Waving.png",
    },
    {
      id: "filters",
      title: "Narrow with filters",
      body: "Use the left panel to filter by LTV, term, type, EPC and more.",
      mobileBody: "Tap Filters, adjust your options, then tap Show products.",
      mascot: "/Smiling.png",
    },
    {
      id: "add-to-shortlist",
      title: "Shortlist favourites",
      body: "Click Add to shortlist on any card — up to 5 products.",
      mobileBody: "Tap Add to shortlist on any card — up to 5 products.",
      mascot: "/Waving.png",
    },
    {
      id: "shortlist-cart-window",
      title: "Continue to apply",
      body: "Open your shortlist cart and click Continue with shortlisted products.",
      mobileBody: "Tap Apply with Shortlist, then Continue with shortlisted products.",
      mascot: "/Smiling.png",
    },
  ];

  const step = steps[stepIndex] ?? steps[0];
  const isMobile = windowSize.width > 0 ? windowSize.width < 768 : isMobileTourViewport();
  const stepBody = isMobile && step.mobileBody ? step.mobileBody : step.body;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const updateRect = React.useCallback(() => {
    if (!open) {
      setTourRects(null);
      return;
    }
    const rects = getTourRects(step.id, cartOpen);
    if (!rects) {
      setTourRects(null);
      return;
    }
    setTourRects(rects);
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, [open, step.id, cartOpen]);

  const handleScrollOrResize = React.useCallback(() => {
    setScrollSyncing(true);
    if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
    scrollIdleTimerRef.current = setTimeout(() => setScrollSyncing(false), 140);
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = 0;
      updateRect();
    });
  }, [updateRect]);

  React.useEffect(() => {
    if (open) {
      onEnsureStep?.(step.id);

      const el = resolveTourTargetElement(step.id, { cartOpen });
      const drawerOpen = step.id === "filters" && document.getElementById("mobile-filters-drawer");
      if (el && !drawerOpen) {
        try {
          const block =
            step.id === "quickbar"
              ? "start"
              : isMobileTourViewport()
                ? "nearest"
                : "center";
          el.scrollIntoView({ behavior: "smooth", block });
        } catch {}
      }

      const measure = () => {
        updateRect();
        requestAnimationFrame(updateRect);
      };

      const timer1 = setTimeout(measure, 50);
      const timer2 = setTimeout(measure, 300);
      const timer3 = layoutKey ? setTimeout(measure, 500) : null;

      window.addEventListener("resize", handleScrollOrResize);
      window.addEventListener("scroll", handleScrollOrResize, true);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        if (timer3) clearTimeout(timer3);
        if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
        if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
        window.removeEventListener("resize", handleScrollOrResize);
        window.removeEventListener("scroll", handleScrollOrResize, true);
      };
    } else {
      setTourRects(null);
      setMeasuredBubble(null);
      setScrollSyncing(false);
    }
  }, [open, step.id, updateRect, handleScrollOrResize, onEnsureStep, cartOpen, layoutKey]);

  const estimatedBubble = estimateTourBubbleSize(
    windowSize.width || (typeof window !== "undefined" ? window.innerWidth : 360),
    isMobile
  );

  const bubblePlacement = React.useMemo(() => {
    if (!tourRects?.anchor || !windowSize.width || !windowSize.height) return null;
    return computeTourBubblePlacement({
      anchor: tourRects.anchor,
      windowW: windowSize.width,
      windowH: windowSize.height,
      bubbleW: measuredBubble?.width ?? estimatedBubble.width,
      bubbleH: measuredBubble?.height ?? estimatedBubble.height,
      stepId: step.id,
      isMobile,
    });
  }, [tourRects, windowSize, measuredBubble, estimatedBubble, step.id, isMobile]);

  React.useLayoutEffect(() => {
    if (!open || !bubbleRef.current) {
      setMeasuredBubble(null);
      return;
    }
    const el = bubbleRef.current;
    const width = el.offsetWidth;
    const height = el.offsetHeight;
    setMeasuredBubble((prev) =>
      prev?.width === width && prev?.height === height ? prev : { width, height }
    );
  }, [open, tourRects, step.id, stepBody, nextHint, canNext]);

  if (!open) return null;

  const highlightRect = tourRects?.highlight ?? null;
  const bubbleStyles = bubblePlacement
    ? { top: bubblePlacement.top, left: bubblePlacement.left }
    : undefined;

  // SVG Mask Cutout values
  const maskX = highlightRect ? highlightRect.left : 0;
  const maskY = highlightRect ? highlightRect.top : 0;
  const maskW = highlightRect ? highlightRect.width : 0;
  const maskH = highlightRect ? highlightRect.height : 0;

  return (
    <div className="fixed inset-0 z-[9990] pointer-events-none flex items-center justify-center">
      {/* 
        True Spotlight using SVG Mask. 
        It draws a full-screen semi-transparent black rect, 
        and then 'cuts out' the targetRect using an XOR or Destination-Out composite.
      */}
      <svg
        className="fixed inset-0 pointer-events-none w-full h-full"
        style={{ zIndex: 9991 }}
        width="100%"
        height="100%"
      >
        <defs>
          <mask id="spotlight-mask">
            {/* White covers the entire screen (opaque in mask) */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {/* Black cuts a hole (transparent in mask) */}
            {highlightRect && (
              <rect
                x={maskX}
                y={maskY}
                width={maskW}
                height={maskH}
                rx="12"
                ry="12"
                fill="black"
              />
            )}
          </mask>
        </defs>
        {/* The overlay is drawn with the mask applied */}
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(15, 23, 42, 0.7)"
          mask="url(#spotlight-mask)"
        />
        

      </svg>

      <div className="pointer-events-none absolute inset-0 z-[9992]">
        <div
          ref={bubbleRef}
          className={
            "pointer-events-auto flex flex-row items-stretch gap-2.5 p-2.5 w-[min(480px,calc(100%-16px))] rounded-2xl bg-white shadow-xl border-2 border-sky-100 overflow-hidden " +
            (bubblePlacement ? "absolute" : "relative mx-auto mt-[25vh]") +
            (scrollSyncing ? "" : " transition-[top,left] duration-200 ease-out")
          }
          style={bubbleStyles}
        >
          <div className="shrink-0 w-[88px] md:w-[96px] flex items-center justify-center self-stretch">
            <img
              src="/Waving-Photoroom.png"
              alt="Polar bear tour guide"
              className="w-[84px] h-[84px] md:w-[92px] md:h-[92px] object-contain select-none pointer-events-none"
              draggable={false}
            />
          </div>

          <div className="flex-1 min-w-0 flex flex-col justify-between gap-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-[11px] md:text-xs font-bold text-slate-900 leading-tight">
                  {step.title}
                </h2>
                <p className="text-[9px] font-semibold uppercase tracking-wide text-sky-500 mt-0.5">
                  Step {stepIndex + 1} of {steps.length}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex shrink-0 h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
                aria-label="Close tour"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <p className="text-[10px] md:text-[11px] text-slate-700 leading-snug font-medium">
              {stepBody}
            </p>

            {!canNext && nextHint ? (
              <p className="text-[9px] leading-tight font-semibold text-sky-600">
                {nextHint}
              </p>
            ) : null}

            <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
              <button
                type="button"
                onClick={onClose}
                className="text-[10px] md:text-[11px] font-semibold text-slate-400 hover:text-slate-600 underline-offset-2 hover:underline transition-colors shrink-0"
              >
                Skip
              </button>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={onPrev}
                  disabled={isFirst}
                  className={
                    "inline-flex items-center justify-center rounded-lg border px-2 py-1 text-[10px] md:text-[11px] font-semibold transition-colors " +
                    (isFirst
                      ? "border-slate-100 bg-slate-50 text-slate-300 cursor-default"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300")
                  }
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={onNext}
                  disabled={!canNext}
                  className="inline-flex items-center justify-center rounded-lg bg-sky-500 px-2.5 py-1 text-[10px] md:text-[11px] font-bold text-white shadow-sm hover:bg-sky-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLast ? "Done" : "Next"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- helpers -------------------------------- */

const ENV = typeof import.meta !== "undefined" ? import.meta.env : process.env;
const PV_MIN = 75000;
const PV_MAX =
  Number(ENV?.VITE_MAX_PROPERTY_VALUE || ENV?.REACT_APP_MAX_PROPERTY_VALUE) ||
  10_000_000;

const safeNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/** Parse money-like values from quick-bar / API (handles £, commas, blanks). */
const parseMoney = (v, fallback = 0) => {
  if (v === null || v === undefined || v === "") return fallback;
  if (typeof v === "number") return Number.isFinite(v) ? v : fallback;
  const n = Number(String(v).replace(/[£,\s]/g, "").trim());
  return Number.isFinite(n) ? n : fallback;
};

const firstPositiveMoney = (...vals) => {
  for (const v of vals) {
    const n = parseMoney(v, 0);
    if (n > 0) return n;
  }
  return 0;
};

const LS_SOURCING_QS = "ds_sourcing_qs";
const LS_SOURCING_LAST_SEARCH = "ds_sourcing_last_search";

function readJsonStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/** Deposit / rent / property / mortgage from every source available at shortlist time. */
function resolveSourcingMoneyInputs(qs, lastSearch, product) {
  const storedQs = readJsonStorage(LS_SOURCING_QS) || {};
  const storedSearch = readJsonStorage(LS_SOURCING_LAST_SEARCH) || {};
  const property_value = firstPositiveMoney(
    qs?.propertyValue,
    storedQs.propertyValue,
    lastSearch?.property_value,
    storedSearch.property_value,
    product?.property_value,
    product?.propertyValue
  );
  const mortgage_amount = firstPositiveMoney(
    qs?.mortgageAmount,
    storedQs.mortgageAmount,
    lastSearch?.mortgage_amount,
    storedSearch.mortgage_amount,
    product?.loan_amount,
    product?.mortgage_amount,
    product?.mortgageAmount
  );
  const deposit_amount = firstPositiveMoney(
    qs?.depositAmount,
    storedQs.depositAmount,
    lastSearch?.deposit_amount,
    storedSearch.deposit_amount,
    property_value > 0 && mortgage_amount > 0 ? property_value - mortgage_amount : 0
  );
  const est_rent = firstPositiveMoney(
    qs?.estRent,
    storedQs.estRent,
    lastSearch?.est_rent,
    storedSearch.est_rent,
    product?.est_rent,
    product?.estimated_rent,
    product?.estimatedRent,
    product?.estRent
  );
  return { property_value, mortgage_amount, deposit_amount, est_rent };
}

const Num = (n) =>
  safeNum(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const firstFinite = (...vals) => {
  for (const v of vals) {
    if (v == null) continue;
    if (typeof v === "string") {
      const s = v.trim();
      const m = s.match(/-?\d+(?:\.\d+)?/);
      if (m) {
        const n = Number(m[0]);
        if (Number.isFinite(n)) return n;
      }
      continue;
    }
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

// eslint-disable-next-line no-unused-vars
const formatPercent = (n, dp = 2) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return `${x.toFixed(dp)}%`;
};

/** Format ICR as exact % from backend (e.g. 5.5 → "5.5%", 6 → "6%") — no rounding to integer. */
function formatIcrPercent(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  const s = x.toFixed(2).replace(/\.?0+$/, "");
  return `${s}%`;
}

function formatErcPercent(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0%";
  const s = x.toFixed(8).replace(/\.?0+$/, "");
  return `${s}%`;
}

const formatMoney = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return `£${x.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

function formatAprcValue(aprc) {
  const x = Number(aprc);
  if (!Number.isFinite(x)) return "—";
  return `${x.toFixed(3)}%`;
}

/** Resolve APRC from product (compare/shortlist may have aprc, aprc_rate, aprc_percent, or _raw). */
function getAprcValue(p) {
  return firstFinite(
    p?.aprc_percent,
    p?.aprc,
    p?.aprc_rate,
    p?._raw?.aprc_percent,
    p?._raw?.aprc,
    p?._raw?.aprc_rate
  );
}

function formatProductFeeDisplay(p) {
  const typeRaw =
    p?.product_fee_type ||
    p?.arrangement_fee_type ||
    p?.fee_type ||
    p?.product_fee_type_selector ||
    "";
  const type = String(typeRaw || "").toLowerCase();

  const amount = firstFinite(
    p?.product_fee_amount,
    p?.product_fee,
    p?.arrangement_fee_amount,
    p?.arrangement_fee
  );

  const percent = firstFinite(
    p?.product_fee_percent,
    p?.fee_percent,
    p?.arrangement_fee_percent
  );

  const minFee = firstFinite(
    p?.min_fee,
    p?.min_arrangement_fee,
    p?.product_min_fee,
    p?.arrangement_min_fee
  );

  if (type.includes("percent") || type === "%" || type === "percentage") {
    if (percent != null) return `${Number(percent).toFixed(2)}%`;
    if (amount != null) return `${Number(amount).toFixed(2)}%`;
    return "—";
  }

  if (type.includes("min")) {
    if (minFee != null) return `Min ${formatMoney(minFee)}`;
    if (amount != null) return `Min ${formatMoney(amount)}`;
    return "—";
  }

  if (amount != null) return formatMoney(amount);
  return "—";
}

/**
 * Normalize backend percent values:
 * - 64   => 64
 * - 0.64 => 64
 */
function normalizePercentValue(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n > 0 && n <= 1) return n * 100;
  return n;
}

/**
 * Display metrics:
 * - LTV should prefer backend product LTV (e.g. 64%) if present,
 *   else fallback to scenario LTV.
 */
function getBackendDisplayMetrics(p, calc, fallbackParams) {
  const pv = safeNum(fallbackParams?.propertyValue);
  const ma = safeNum(fallbackParams?.mortgageAmount);
  const rent = safeNum(fallbackParams?.estimatedRent);

  const productLtvRaw = firstFinite(
    p?.ity_percent,
    p?.max_ltv,
    p?.max_ltv_percent,
    p?.ltv_cap,
    p?.loan_to_value,
    p?.ltv,
    p?.ltv_percent
  );
  const productLtv = normalizePercentValue(productLtvRaw);

  const scenarioLtv =
    Number.isFinite(calc?.ltv) && calc?.ltv > 0
      ? normalizePercentValue(calc?.ltv)
      : pv > 0
        ? (ma / pv) * 100
        : null;

  const mortgageTermDisplay =
    firstFinite(calc?.mortgage_term_years, p?.mortgage_term_years, p?.mortgage_term) ??
    fallbackParams?.mortgageTermYears;

  // ICR from backend - check multiple possible field names
  // Backend may provide icr_rate (currency value like £750) or icr_percent (percentage like 125)
  const icrDisplay =
    firstFinite(
      p?.icr_rate,           // Currency value from backend (e.g., £750)
      p?.icr,                 // Direct ICR value
      calc?.icr,              // ICR from calculation
      p?.icr_percent          // Percentage value from backend
    ) ??
    (rent > 0 && safeNum(calc?.monthly_payment) > 0
      ? (rent / safeNum(calc?.monthly_payment)) * 100
      : null);

  return {
    ltvDisplay: productLtv ?? scenarioLtv,
    mortgageTermDisplay,
    icrDisplay,
  };
}

/** Build display snapshot from backend shortlist item (for use when we have no local snapshot). */
function buildDisplaySnapshotFromBackendItem(item) {
  if (!item || typeof item !== "object") return null;
  const ltvRaw = item.ltv ?? item.ltv_percent;
  const ltv = ltvRaw != null && Number.isFinite(Number(ltvRaw)) ? Number(ltvRaw) : null;
  const mortgageTermYears = firstFinite(
    item.mortgage_term_years,
    typeof item.mortgage_term === "string" ? parseInt(String(item.mortgage_term).replace(/[^\d]/g, ""), 10) : null
  );
  return {
    ltv: ltv ?? undefined,
    rate: safeNum(item.interest_rate ?? item.rate),
    monthlyPayment: safeNum(item.monthly_payment ?? item.monthly_cost),
    totalCost: safeNum(item.total_cost ?? item.cost_over_product_term),
    icr: firstFinite(item.icr_rate, item.icr, item.icr_percent) ?? undefined,
    mortgageTermYears: Number.isFinite(mortgageTermYears) ? mortgageTermYears : undefined,
    repaymentType: item.repayment_type ?? item.repaymentType ?? "Repayment",
  };
}

/** Build ERC array with all years 1-5 (include 0% so shortlisted page shows them). */
function ensureErcYears1To5(ercArray) {
  const byYear = {};
  (ercArray || []).forEach((erc) => {
    const y = erc.year ?? (typeof erc.year_no === "string" ? parseInt(erc.year_no.replace("Year ", "").trim(), 10) : null);
    if (y >= 1 && y <= 5) {
      const pct = erc.charge_percent ?? erc.charge;
      byYear[y] = pct != null && Number.isFinite(Number(pct)) ? Number(pct) : 0;
    }
  });
  return [1, 2, 3, 4, 5].map((y) => ({
    year_no: `Year ${y}`,
    charge_percent: byYear[y] ?? 0,
    year: y,
    charge: byYear[y] ?? 0,
  }));
}

function buildErcFromYearFields(product) {
  return [1, 2, 3, 4, 5].map((y) => {
    const yearKey = `year_${y}`;
    const charge = product[yearKey] ?? product[`erc${y}`] ?? product[`erc_year_${y}`] ?? product[`early_repayment_year_${y}`];
    const pct = charge != null && Number.isFinite(Number(charge)) ? Number(charge) : 0;
    return { year_no: `Year ${y}`, charge_percent: pct, year: y, charge: pct };
  });
}

const InputShell = ({ label, children, labelClassName }) => (
  <label className="block">
    <div className={`mb-1 ${labelClassName ?? "text-slate-700 text-[13px]"}`}>
      {label}
    </div>
    {children}
  </label>
);

const Select = ({ children, className = "", ...rest }) => (
  <div className="relative group transform-gpu">
    <select
      className={
        "w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 pr-10 py-3 " +
        "text-[16px] md:text-[17px] ring-1 ring-slate-200 transition-all duration-150 " +
        "focus:outline-none focus:ring-1 focus:ring-sky-300 focus:border-sky-300 " +
        className
      }
      {...rest}
    >
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-2.5 top-3.5 h-5 w-5 text-sky-500 transition-transform duration-150 group-focus-within:rotate-180" />
  </div>
);

const SOURCING_FILTER_DEFAULTS = {
  repaymentType: "Interest only",
  ltv: "75%",
  epcGreen: "A-C",
  mortgageTerm: "25 yrs",
  productTerm: "5",
  productType: "Fixed",
  borrowerType: "Ltd Co.",
  loanPurpose: "Purchase",
  taxpayerType: "Basic Rate (125%)",
};

const ACTIVE_FILTER_KEYS = [
  "ltv",
  "epcGreen",
  "mortgageTerm",
  "productTerm",
  "productType",
  "loanPurpose",
  "borrowerType",
  "homeowner",
  "residency",
  "btlExp",
  "credit",
  "tenancyType",
  "taxpayerType",
  "tenure",
  "leaseTerm",
  "exAuthority",
  "depositOrigination",
  "day1Remortgage",
  "repaymentType",
];

const SOURCING_SELECT_FIELDS = [
  ["LTV", "ltv", ["", "60%", "65%", "70%", "75%", "80%", "85%"]],
  ["EPC rating", "epcGreen", ["", "A-C", "D-F"]],
  ["Mortgage term", "mortgageTerm", ["", "5 yrs", "10 yrs", "15 yrs", "20 yrs", "25 yrs", "30 yrs", "40 yrs"]],
  ["Product term", "productTerm", ["", "0.5", "1", "2", "3", "5", "10", "Lifetime"]],
  ["Product type", "productType", ["", "Fixed", "Variable", "Tracker", "Discounted"]],
  ["Loan purpose", "loanPurpose", ["", "Purchase", "Remortgage"]],
  ["Borrower type", "borrowerType", ["", "Individual/partnership", "Ltd Co.", "Pension/Trust"]],
  ["Home owner", "homeowner", ["", "No", "Yes"]],
  ["Residency", "residency", ["", "UK", "International", "Expat"]],
  ["BTL experience", "btlExp", ["", "0", "1-5", "5+"]],
  ["Credit worthiness", "credit", ["", "Excellent", "Fair", "Poor"]],
  ["Tenancy Type", "tenancyType", ["", "AST", "Lease", "Licence"]],
  ["Taxpayer type (DSC)", "taxpayerType", ["", "Basic Rate (125%)", "Higher Rate (140%)"]],
];

const PROPERTY_EXTRA_FIELDS = [
  ["Ex Local Authority", "exAuthority", ["", "No", "Yes"]],
  ["Deposit Origination", "depositOrigination", ["", "Savings", "Gifted", "Equity"]],
  ["Day 1 Remortgage", "day1Remortgage", ["", "Pound for Pound", "Market Value", "Refurb"]],
];

const PROPERTY_TYPE_OPTIONS = [
  { key: "BTL", label: "BTL" },
  { key: "HMO", label: "HMO" },
  { key: "MUFB", label: "MUFB" },
  { key: "Short term let", label: "Short term let" },
  { key: "Holiday let", label: "Holiday let" },
  { key: "MPOT", label: "MPOT (multi-property on one title)" },
  { key: "Portfolio", label: "Portfolio" },
];

const productCategory = (p) =>
  String(p?.product_category || p?.category || "").trim().toUpperCase();

const fieldIsSet = (v) =>
  safeNum(v) > 0 || v === true || /^(yes|y|true)$/i.test(String(v ?? "").trim());

const anyPositive = (p, keys) => keys.some((k) => fieldIsSet(p?.[k]));

const anyWithPrefix = (p, prefix) =>
  Object.keys(p || {}).some((k) => k.startsWith(prefix) && fieldIsSet(p[k]));

function productMatchesPropertyType(p, type) {
  switch (type) {
    case "BTL":
      return productCategory(p) === "BTL";
    case "HMO":
      return anyPositive(p, ["hmo_max_rooms"]);
    case "MUFB":
      return anyPositive(p, ["mufb_max_units", "mufb_houses", "mufb_flats", "mufb_units_retained_percent"]);
    case "Short term let":
      return anyPositive(p, ["short_term_lets_avg_weeks", "short_term_lets_standard_rental_income"]);
    case "Holiday let":
      return anyPositive(p, ["holiday_lets_avg_weeks"]);
    case "MPOT":
      return anyWithPrefix(p, "mpot_");
    case "Portfolio":
      return anyWithPrefix(p, "portfolio_");
    default:
      return false;
  }
}

const MUFB_UNIT_TYPE_FIELDS = {
  Flats: ["mufb_flats"],
  Houses: ["mufb_houses"],
};

const MPOT_MIX_FIELDS = {
  Flat: ["mpot_flat"],
  House: ["mpot_house"],
  HMO: ["mpot_hmo"],
  MUFB: ["mpot_mufb"],
  "Holiday let": ["mpot_holiday_let"],
  "Short term let": ["mpot_short_term_let"],
  "Semi-commercial": ["mpot_semi_comm", "mpot_semi_commercial"],
};

const PORTFOLIO_MIX_FIELDS = {
  BTL: ["portfolio_btl"],
  HMO: ["portfolio_hmo"],
  MUFB: ["portfolio_mufb"],
  "Holiday let": ["portfolio_holiday_let"],
  "Short term let": ["portfolio_short_term_let"],
  "Semi-commercial": ["portfolio_semi_comm", "portfolio_semi_commercial"],
};

const mixMatches = (p, fieldMap, selected) => {
  const fields = fieldMap[selected];
  return !fields || anyPositive(p, fields);
};

/** Ticked unit types; tolerates the old single-select string saved in filters storage. */
const tickedUnitTypes = (v) => (Array.isArray(v) ? v : v ? [v] : []);

/** The product must accept every ticked unit type. */
const mixMatchesAll = (p, fieldMap, ticked) =>
  tickedUnitTypes(ticked).every((t) => mixMatches(p, fieldMap, t));

const enteredNumber = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[£,\s]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const productLimit = (p, keys) => {
  const vals = keys.map((k) => safeNum(p?.[k])).filter((n) => n > 0);
  return vals.length ? Math.max(...vals) : null;
};

function productMeetsPropertyCriteria(p, filters) {
  const type = filters?.complexPropertyType;
  if (!productMatchesPropertyType(p, type)) return false;

  if (type === "HMO") {
    const rooms = enteredNumber(filters.hmoRooms);
    const maxRooms = productLimit(p, ["hmo_max_rooms"]);
    if (rooms && (!maxRooms || rooms > maxRooms)) return false;
  }

  if (type === "MUFB") {
    const units = enteredNumber(filters.mufbUnits);
    const maxUnits = productLimit(p, ["mufb_max_units"]);
    if (units && (!maxUnits || units > maxUnits)) return false;
    if (!mixMatches(p, MUFB_UNIT_TYPE_FIELDS, filters.mufbUnitType)) return false;
    const retained = enteredNumber(filters.mufbUnitsRetained);
    const minRetained = productLimit(p, ["mufb_units_retained_percent"]);
    if (retained && minRetained && retained < minRetained) return false;
  }

  if (type === "MPOT") {
    const units = enteredNumber(filters.mpotUnits);
    const maxUnits = productLimit(p, ["mpot_max_units"]);
    if (units && maxUnits && units > maxUnits) return false;
    if (!mixMatchesAll(p, MPOT_MIX_FIELDS, filters.mpotUnitTypes)) return false;
  }

  if (type === "Portfolio") {
    const units = enteredNumber(filters.portfolioUnits);
    const maxUnits = productLimit(p, ["portfolio_max_units"]);
    if (units && maxUnits && units > maxUnits) return false;
    if (!mixMatchesAll(p, PORTFOLIO_MIX_FIELDS, filters.portfolioUnitTypes)) return false;
  }

  return true;
}

const selectedLtvPercent = (ltvValue) => {
  const match = String(ltvValue ?? "").match(/(\d+(\.\d+)?)/);
  const n = match ? parseFloat(match[1]) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Case details the calculator needs for assessed rent and required DSC. */
const calculatorCaseInputs = (filters) => ({
  propertyType: filters?.complexPropertyType || "",
  borrowerType: filters?.borrowerType || "",
  taxpayerType: filters?.taxpayerType || "",
});

const PROPERTY_UNITS_FILTER_KEYS = {
  MUFB: "mufbUnits",
  MPOT: "mpotUnits",
  Portfolio: "portfolioUnits",
};

const TICKED_UNIT_TYPE_FILTERS = {
  MPOT: ["mpotUnitTypes", MPOT_MIX_FIELDS],
  Portfolio: ["portfolioUnitTypes", PORTFOLIO_MIX_FIELDS],
};

/** Rooms / units limits plus ticked MPOT and Portfolio unit types. */
function productPassesSizeCheck(p, filters) {
  const propertyType = filters?.complexPropertyType;
  const matchesType = productMatchesPropertyType(p, propertyType);
  const passesSize = passesPropertySizeCheck(p, {
    propertyType,
    rooms: filters?.hmoRooms,
    units: filters?.[PROPERTY_UNITS_FILTER_KEYS[propertyType]],
    matchesType,
  });
  if (!passesSize) return false;

  const ticked = TICKED_UNIT_TYPE_FILTERS[propertyType];
  return !ticked || !matchesType || mixMatchesAll(p, ticked[1], filters?.[ticked[0]]);
}

const PROPERTY_TYPE_CHIPS = [
  { key: "BTL", label: "BTL", tone: "border-sky-200 bg-sky-50 text-sky-700" },
  { key: "HMO", label: "HMO", tone: "border-violet-200 bg-violet-50 text-violet-700" },
  { key: "MUFB", label: "MUFB", tone: "border-amber-200 bg-amber-50 text-amber-700" },
  { key: "Short term let", label: "STL", tone: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  { key: "Holiday let", label: "Holiday let", tone: "border-teal-200 bg-teal-50 text-teal-700" },
  { key: "MPOT", label: "MPOT", tone: "border-orange-200 bg-orange-50 text-orange-700" },
  { key: "Portfolio", label: "Portfolio", tone: "border-rose-200 bg-rose-50 text-rose-700" },
];

const productPropertyTypeChips = (p) =>
  PROPERTY_TYPE_CHIPS.filter((c) => productMatchesPropertyType(p, c.key));

const PropertyTypeChips = ({ product, compact = false }) => {
  const chips = productPropertyTypeChips(product);
  if (!chips.length) return null;
  return chips.map((c) => (
    <span
      key={c.key}
      className={
        `inline-flex shrink-0 items-center rounded-full border font-semibold whitespace-nowrap ${c.tone} ` +
        (compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs")
      }
    >
      {c.label}
    </span>
  ));
};

const propertyFilterDefaults = (isComplexBtl) => ({
  ...COMPLEX_PROPERTY_FILTER_DEFAULTS,
  complexPropertyType: isComplexBtl ? "HMO" : "BTL",
  ...(isComplexBtl ? { hmoRooms: "5" } : {}),
});

const COMPLEX_PROPERTY_FILTER_DEFAULTS = {
  complexPropertyType: "HMO",
  hmoRooms: "",
  mufbUnits: "",
  mufbUnitType: "",
  mufbUnitsRetained: "",
  mpotUnits: "",
  mpotUnitTypes: [],
  portfolioUnits: "",
  portfolioUnitTypes: [],
};

const MUFB_UNIT_TYPE_OPTIONS = ["", ...Object.keys(MUFB_UNIT_TYPE_FIELDS)];
const MPOT_UNIT_TYPE_OPTIONS = Object.keys(MPOT_MIX_FIELDS);
const PORTFOLIO_UNIT_TYPE_OPTIONS = Object.keys(PORTFOLIO_MIX_FIELDS);
function ComplexPropertyTypePanel({ filtersDraft, setFilterDraft, defaultType = "HMO" }) {
  const tab = filtersDraft.complexPropertyType || defaultType;
  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[15px] ring-1 ring-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-300";
  const labelClass = "text-slate-900 font-semibold text-[15px] md:text-[16px]";

  const numberField = (label, key, placeholder, max) => (
    <InputShell label={label} labelClassName={labelClass}>
      <input
        type="number"
        min={0}
        max={max}
        className={inputClass}
        placeholder={placeholder}
        value={filtersDraft[key] ?? ""}
        onChange={(e) => setFilterDraft(key, e.target.value)}
      />
    </InputShell>
  );

  const selectField = (label, key, options) => (
    <InputShell label={label} labelClassName={labelClass}>
      <Select value={filtersDraft[key] ?? ""} onChange={(e) => setFilterDraft(key, e.target.value)}>
        {options.map((o) => (
          <option key={o || "all"} value={o}>
            {o || "All"}
          </option>
        ))}
      </Select>
    </InputShell>
  );

  const checkboxField = (label, key, options) => {
    const ticked = tickedUnitTypes(filtersDraft[key]);
    const toggle = (option) =>
      setFilterDraft(
        key,
        ticked.includes(option) ? ticked.filter((t) => t !== option) : [...ticked, option]
      );
    return (
      <div>
        <div className={`mb-1 ${labelClass}`}>{label}</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 ring-1 ring-slate-200">
          {options.map((option) => (
            <label key={option} className="inline-flex items-center gap-2 text-[15px] text-slate-800 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-300"
                checked={ticked.includes(option)}
                onChange={() => toggle(option)}
              />
              {option}
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-4 space-y-3">
      <InputShell label="Property type" labelClassName={labelClass}>
        <Select
          value={tab}
          onChange={(e) => setFilterDraft("complexPropertyType", e.target.value)}
        >
          {PROPERTY_TYPE_OPTIONS.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </Select>
      </InputShell>

      <div className="space-y-3">
        {tab === "HMO" ? numberField("No of rooms", "hmoRooms", "e.g. 5") : null}

        {tab === "MUFB" ? (
          <>
            {numberField("No of units", "mufbUnits", "e.g. 4")}
            {selectField("Unit type", "mufbUnitType", MUFB_UNIT_TYPE_OPTIONS)}
            {numberField("Units retained (%)", "mufbUnitsRetained", "e.g. 50", 100)}
          </>
        ) : null}

        {tab === "MPOT" ? (
          <>
            {numberField("No of units on title", "mpotUnits", "e.g. 3")}
            {checkboxField("Unit types", "mpotUnitTypes", MPOT_UNIT_TYPE_OPTIONS)}
          </>
        ) : null}

        {tab === "Portfolio" ? (
          <>
            {numberField("No of properties", "portfolioUnits", "e.g. 6")}
            {checkboxField("Unit types", "portfolioUnitTypes", PORTFOLIO_UNIT_TYPE_OPTIONS)}
          </>
        ) : null}
      </div>
    </div>
  );
}

const SORT_ITEMS = [
  { key: "cost", label: "Total cost (low → high)" },
  { key: "repayment", label: "Monthly repayments (low → high)" },
  { key: "fee", label: "Arrangement fee (low → high)" },
  { key: "rate", label: "Interest rate (low → high)" },
];

function getSourcingFilterOptionLabel(fieldKey, value) {
  if (!value) return "Select";
  if (fieldKey === "residency") {
    const token = String(value).trim().toLowerCase();
    if (token === "international") return "International";
    if (token === "xpat" || token === "expat") return "Expat";
  }
  return value;
}

function countActiveSourcingFilters(filters) {
  let count = 0;
  for (const key of ACTIVE_FILTER_KEYS) {
    const v = String(filters?.[key] ?? "").trim();
    if (!v || v === "Select") continue;
    const def = SOURCING_FILTER_DEFAULTS[key];
    if (def !== undefined && v === String(def)) continue;
    count += 1;
  }
  return count;
}

function SourcingFilterSelect({
  label,
  fieldKey,
  options,
  filtersDraft,
  setFilterDraft,
  onLTVFilterChange,
  labelClassName = "text-slate-900 font-semibold text-[15px]",
}) {
  return (
    <InputShell label={label} labelClassName={labelClassName}>
      <Select
        value={filtersDraft[fieldKey]}
        onChange={(e) => {
          if (fieldKey === "ltv") onLTVFilterChange(e.target.value);
          else setFilterDraft(fieldKey, e.target.value);
        }}
      >
        {options.map((o, i) => (
          <option key={i} value={o}>
            {getSourcingFilterOptionLabel(fieldKey, o)}
          </option>
        ))}
      </Select>
    </InputShell>
  );
}

function MobileFiltersDrawer({
  open,
  onClose,
  filtersDraft,
  setFilterDraft,
  onLTVFilterChange,
  onClearAll,
  onShow,
  productCount,
  isComplexBtl = false,
}) {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const sectionTitle = "text-xs font-bold uppercase tracking-wide text-slate-500 mb-2";
  const fieldKeys = (keys) =>
    SOURCING_SELECT_FIELDS.filter(([, key]) => keys.includes(key));

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/50 md:hidden"
        onClick={onClose}
        aria-hidden
      />
      <div
        id="mobile-filters-drawer"
        className="fixed inset-x-0 bottom-0 z-[61] flex max-h-[90vh] flex-col rounded-t-2xl bg-white shadow-2xl md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Filters"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">Filters</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            aria-label="Close filters"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5 min-h-0">
          <section>
            <div className={sectionTitle}>Repayment</div>
            <div className="inline-flex w-full rounded-xl border border-slate-200 bg-slate-50 p-1">
              {["Interest only", "Repayment"].map((opt) => {
                const active = filtersDraft.repaymentType === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setFilterDraft("repaymentType", opt)}
                    className={
                      "flex-1 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors " +
                      (active
                        ? "bg-white text-sky-700 shadow-sm ring-1 ring-slate-200"
                        : "text-slate-600 hover:text-slate-900")
                    }
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            <ComplexPropertyTypePanel
              filtersDraft={filtersDraft}
              setFilterDraft={setFilterDraft}
              defaultType={isComplexBtl ? "HMO" : "BTL"}
            />
          </section>

          <section>
            <div className={sectionTitle}>Property</div>
            <div className="space-y-3">
              {fieldKeys(["ltv", "epcGreen", "homeowner"]).map(([label, key, options]) => (
                <SourcingFilterSelect
                  key={key}
                  label={label}
                  fieldKey={key}
                  options={options}
                  filtersDraft={filtersDraft}
                  setFilterDraft={setFilterDraft}
                  onLTVFilterChange={onLTVFilterChange}
                />
              ))}
              <SourcingFilterSelect
                label="F/H or L/H"
                fieldKey="tenure"
                options={["", "FREEHOLD", "LEASEHOLD"]}
                filtersDraft={filtersDraft}
                setFilterDraft={setFilterDraft}
                onLTVFilterChange={onLTVFilterChange}
              />
              {filtersDraft.tenure === "LEASEHOLD" && (
                <InputShell
                  label="Leasehold remaining term"
                  labelClassName="text-slate-900 font-semibold text-[15px]"
                >
                  <input
                    type="number"
                    min={0}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[15px] ring-1 ring-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-300"
                    placeholder="Years"
                    value={filtersDraft.leaseTerm}
                    onChange={(e) => setFilterDraft("leaseTerm", e.target.value)}
                  />
                </InputShell>
              )}
            </div>
          </section>

          <section>
            <div className={sectionTitle}>Product</div>
            <div className="space-y-3">
              {fieldKeys(["productType", "productTerm", "mortgageTerm", "loanPurpose"]).map(
                ([label, key, options]) => (
                  <SourcingFilterSelect
                    key={key}
                    label={label}
                    fieldKey={key}
                    options={options}
                    filtersDraft={filtersDraft}
                    setFilterDraft={setFilterDraft}
                    onLTVFilterChange={onLTVFilterChange}
                  />
                )
              )}
            </div>
          </section>

          <section>
            <div className={sectionTitle}>Borrower &amp; criteria</div>
            <div className="space-y-3">
              {fieldKeys([
                "borrowerType",
                "residency",
                "btlExp",
                "credit",
                "tenancyType",
                "taxpayerType",
              ]).map(([label, key, options]) => (
                <SourcingFilterSelect
                  key={key}
                  label={label}
                  fieldKey={key}
                  options={options}
                  filtersDraft={filtersDraft}
                  setFilterDraft={setFilterDraft}
                  onLTVFilterChange={onLTVFilterChange}
                />
              ))}
              {PROPERTY_EXTRA_FIELDS.map(([label, key, options]) => (
                <SourcingFilterSelect
                  key={key}
                  label={label}
                  fieldKey={key}
                  options={options}
                  filtersDraft={filtersDraft}
                  setFilterDraft={setFilterDraft}
                  onLTVFilterChange={onLTVFilterChange}
                />
              ))}
            </div>
          </section>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-white p-4 flex gap-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={onClearAll}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={onShow}
            className="flex-[1.4] rounded-xl bg-sky-500 px-4 py-3 text-sm font-semibold text-white shadow hover:bg-sky-600"
          >
            Show {productCount} product{productCount === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

function MobileSortDrawer({ open, onClose, sortBy, setSortBy }) {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/50 md:hidden"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="fixed inset-x-0 bottom-0 z-[61] flex max-h-[70vh] flex-col rounded-t-2xl bg-white shadow-2xl md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Sort products"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-lg font-semibold text-slate-900">Sort by</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            aria-label="Close sort"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto py-2">
          {SORT_ITEMS.map((it) => {
            const active = it.key === sortBy;
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  setSortBy(it.key);
                  onClose();
                }}
                className={
                  "w-full flex items-center justify-between px-4 py-3.5 text-sm text-left " +
                  (active ? "bg-sky-50 text-sky-800 font-semibold" : "text-slate-800 hover:bg-slate-50")
                }
              >
                <span>{it.label}</span>
                {active ? <Check className="h-4 w-4 shrink-0" /> : null}
              </button>
            );
          })}
        </div>
      </div>
    </>,
    document.body
  );
}

const ComingSoonStyles = () => (
  <style>{`
    @keyframes gradientMove {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes spinBorder { to { transform: rotate(1turn); } }

    @keyframes shinyFade {
      0% {
        background-position: -200% center;
        opacity: 0.8;
      }
      50% {
        background-position: 200% center;
        opacity: 1;
      }
      100% {
        background-position: -200% center;
        opacity: 0.8;
      }
    }

    @keyframes slicedWhiteFade {
      0% {
        background-position: -300% 0;
      }
      50% {
        background-position: 300% 0;
      }
      100% {
        background-position: -300% 0;
      }
    }

    @keyframes shineSweep {
      0% {
        left: -100%;
      }
      50% {
        left: 100%;
      }
      100% {
        left: 100%;
      }
    }

    .comingSoon-pill {
      background: linear-gradient(180deg, rgba(56,189,248,1) 0%, rgba(14,165,233,1) 100%);
      box-shadow: 0 14px 30px rgba(56,189,248,.35);
      border: 1px solid rgba(255,255,255,.4);
      position: relative;
      overflow: hidden;
    }

    .comingSoon-pill::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.7),
        transparent
      );
      animation: shineSweep 2.5s ease-in-out infinite;
    }

    .comingSoon-dot {
      width: 6px; 
      height: 6px; 
      border-radius: 999px;
      background: rgba(255,255,255,1);
      box-shadow: 0 2px 4px rgba(0,0,0,.15);
      position: relative;
      z-index: 1;
      flex-shrink: 0;
    }
    .text-gradient-clip {
      background: linear-gradient(90deg, #0ea5e9, #22d3ee, #0ea5e9);
      background-size: 200% 200%;
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      animation: gradientMove 3s linear infinite;
    }
    .no-spinner::-webkit-outer-spin-button,
    .no-spinner::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    .no-spinner {
      -moz-appearance: textfield;
      appearance: textfield;
    }

    @keyframes borderShine {
      0% {
        background-position: -200% 0;
      }
      100% {
        background-position: 200% 0;
      }
    }

    @keyframes snore {
      0% {
        transform: translateY(0) scale(0.5);
        opacity: 0;
      }
      30% {
        opacity: 1;
      }
      100% {
        transform: translateY(-40px) translateX(20px) scale(1.5);
        opacity: 0;
      }
    }

    .snore-z {
      position: absolute;
      color: black;
      font-weight: bold;
      pointer-events: none;
      animation: snore 3s ease-in-out infinite;
      opacity: 0;
    }

    .cart-shiny-border {
      position: relative;
    }

    .cart-shiny-border::before {
      content: '';
      position: absolute;
      inset: -3px;
      border-radius: 1.25rem;
      background: linear-gradient(
        90deg,
        transparent 0%,
        transparent 25%,
        rgba(14, 165, 233, 0.5) 30%,
        rgba(56, 189, 248, 0.6) 35%,
        rgba(14, 165, 233, 0.7) 40%,
        rgba(56, 189, 248, 0.8) 45%,
        rgba(14, 165, 233, 0.9) 47%,
        rgba(56, 189, 248, 1) 50%,
        rgba(251, 191, 36, 1) 50.5%,
        rgba(245, 158, 11, 0.9) 51%,
        rgba(251, 191, 36, 0.8) 52%,
        rgba(245, 158, 11, 0.7) 55%,
        rgba(251, 191, 36, 0.6) 60%,
        rgba(245, 158, 11, 0.5) 65%,
        transparent 70%,
        transparent 100%
      );
      background-size: 200% 100%;
      animation: borderShine 3s linear infinite;
      z-index: -1;
      padding: 3px;
      -webkit-mask: 
        linear-gradient(#fff 0 0) content-box, 
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      -webkit-mask-composite: exclude;
      mask: 
        linear-gradient(#fff 0 0) content-box, 
        linear-gradient(#fff 0 0);
      mask-composite: exclude;
    }

    .compare-highlight-ring {
      position: relative;
      border-radius: 0.375rem;
    }
    .compare-highlight-ring::before {
      content: '';
      position: absolute;
      inset: -3px;
      border-radius: 0.5rem;
      background: linear-gradient(
        90deg,
        transparent 0%,
        transparent 25%,
        rgba(14, 165, 233, 0.5) 30%,
        rgba(56, 189, 248, 0.6) 35%,
        rgba(14, 165, 233, 0.7) 40%,
        rgba(56, 189, 248, 0.8) 45%,
        rgba(14, 165, 233, 0.9) 47%,
        rgba(56, 189, 248, 1) 50%,
        rgba(251, 191, 36, 1) 50.5%,
        rgba(245, 158, 11, 0.9) 51%,
        rgba(251, 191, 36, 0.8) 52%,
        rgba(245, 158, 11, 0.7) 55%,
        rgba(251, 191, 36, 0.6) 60%,
        rgba(245, 158, 11, 0.5) 65%,
        transparent 70%,
        transparent 100%
      );
      background-size: 200% 100%;
      animation: borderShine 5s linear 0s 2, compareRingFade 2s ease-in-out 10s forwards;
      z-index: 0;
      padding: 3px;
      -webkit-mask: 
        linear-gradient(#fff 0 0) content-box, 
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      -webkit-mask-composite: exclude;
      mask: 
        linear-gradient(#fff 0 0) content-box, 
        linear-gradient(#fff 0 0);
      mask-composite: exclude;
      pointer-events: none;
    }
    @keyframes compareRingFade {
      to { opacity: 0; }
    }

    .cart-text-fade {
      position: relative;
      display: inline-block;
    }

    .cart-text-fade::before {
      content: attr(data-text);
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        transparent 30%,
        rgba(255, 255, 255, 0.3) 35%,
        rgba(255, 255, 255, 0.5) 40%,
        rgba(255, 255, 255, 0.7) 45%,
        rgba(255, 255, 255, 0.9) 50%,
        rgba(255, 255, 255, 0.7) 55%,
        rgba(255, 255, 255, 0.5) 60%,
        rgba(255, 255, 255, 0.3) 65%,
        transparent 70%,
        transparent 100%
      );
      background-size: 300% 100%;
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: slicedWhiteFade 30s linear infinite;
      pointer-events: none;
    }

      filter: blur(10px);
      opacity: 0.5;
    }

    .hide-scrollbar{-ms-overflow-style:none;scrollbar-width:none}
    .hide-scrollbar::-webkit-scrollbar{display:none;width:0;height:0}
  `}</style>
);

const GradientChip = ({ children, compact = false }) => (
  <span className="inline-flex shrink-0 rounded-full bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-500 p-[1px]">
    <span
      className={
        "rounded-full bg-white font-semibold text-slate-900 whitespace-nowrap " +
        (compact ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-[12px]")
      }
    >
      {children}
    </span>
  </span>
);

const GreenTick = ({ compact }) => (
  <span
    className={
      "inline-flex shrink-0 items-center justify-center rounded-full bg-green-500 text-white " +
      (compact ? "h-2.5 w-2.5" : "h-4 w-4")
    }
    aria-hidden
  >
    <Check className={compact ? "h-1.5 w-1.5" : "h-2.5 w-2.5"} strokeWidth={3} />
  </span>
);

const FreeValChip = ({ compact = false }) => (
  <span
    className={
      "inline-flex shrink-0 items-center gap-0.5 rounded-full border border-green-200 bg-green-50 text-green-700 font-semibold whitespace-nowrap " +
      (compact ? "px-1.5 py-0.5 text-[9px] leading-tight" : "px-3 py-1 text-[12px]")
    }
  >
    Free Val
    <GreenTick compact={compact} />
  </span>
);

/** True when product offers free valuation (API / features flags). */
function productHasFreeValuation(product) {
  const raw =
    product?.free_valuation ??
    product?.freeValuation ??
    product?.features?.["Free Valuation"] ??
    product?._raw?.free_valuation;
  if (raw === true || raw === 1 || raw === "1") return true;
  const s = String(raw ?? "").trim().toLowerCase();
  return s === "yes" || s === "true" || s === "y";
}

const normalizeFilterToken = (value) => String(value ?? "").trim().toLowerCase();
const firstNonEmpty = (...values) => values.find((value) => String(value ?? "").trim() !== "");

const splitCsvValues = (value) =>
  String(value ?? "")
    .split(/[,;|]|\s+and\s+/i)
    .map((x) => x.trim())
    .filter(Boolean);

function getProductResidencyValues(product) {
  const listVal = product?.residency_list;
  if (Array.isArray(listVal)) {
    return listVal.flatMap((entry) => {
      if (entry && typeof entry === "object") {
        return splitCsvValues(
          entry.residency ??
            entry.residency_type ??
            entry.residencyType ??
            entry.name ??
            entry.value ??
            ""
        );
      }
      return splitCsvValues(entry);
    });
  }

  for (const key of ["residency", "residencyType", "residency_type"]) {
    const raw = product?.[key];
    if (Array.isArray(raw)) {
      return raw.flatMap((entry) =>
        entry && typeof entry === "object"
          ? splitCsvValues(entry.residency ?? entry.name ?? entry.value ?? "")
          : splitCsvValues(entry)
      );
    }
    if (typeof raw === "string" && raw.trim()) return splitCsvValues(raw);
  }

  return [];
}

function matchesResidencyFilter(selectedResidency, product) {
  const selected = normalizeFilterToken(selectedResidency);
  if (!selected || selected === "select" || selected === "uk") return true;

  const values = getProductResidencyValues(product).map(normalizeFilterToken);
  if (!values.length) return false;

  const hasInternational = values.includes("international");
  const hasXpat = values.some((v) => v === "xpat" || v === "expat");

  // Products tagged with both overseas types appear for Xpat and International filters
  if (hasInternational && hasXpat) {
    return selected === "international" || selected === "xpat" || selected === "expat";
  }

  if (selected === "xpat" || selected === "expat") return hasXpat;
  if (selected === "international") return hasInternational;
  return values.includes(selected);
}

const ChipRow = ({ term, type, ltv, productTermRaw, freeValuation = false, nowrap = false, compact = false }) => {
  const isLifetimeProduct = normalizeFilterToken(productTermRaw) === "lifetime";
  const termLabel = isLifetimeProduct ? "Lifetime" : term ? `${term} yr` : null;
  return (
    <div className={`flex items-center gap-1.5 ${nowrap ? "flex-nowrap shrink-0" : "flex-wrap gap-2"}`}>
      {termLabel ? <GradientChip compact={compact}>{termLabel}</GradientChip> : null}
      {type ? <GradientChip compact={compact}>{type}</GradientChip> : null}
      {typeof ltv === "number" ? <GradientChip compact={compact}>{`LTV ${ltv}%`}</GradientChip> : null}
      {freeValuation ? <FreeValChip compact={compact || nowrap} /> : null}
    </div>
  );
};

/* =============================================================================
   DSC Badge Component - Shows if product meets debt service coverage
============================================================================= */
const formatMinimumRent = (minimumRent) =>
  `min rent ${formatMoney(minimumRent.minimumMonthlyRent)}/mo (${formatMoney(minimumRent.weeklyEquivalent)}/wk)`;

const DSCBadge = ({ dsc, requiredDSC, minimumRent = null, compact = false }) => {
  if (!requiredDSC || requiredDSC === 0) return null;

  const meetsDSC = dsc >= requiredDSC;
  const tone = meetsDSC
    ? "bg-green-50 text-green-700 border-green-200"
    : "bg-red-50 text-red-700 border-red-200";

  if (compact) {
    return (
      <div
        className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ${tone}`}
      >
        {meetsDSC ? (
          <>
            <CheckCircle2 className="h-3 w-3" />
            DSC {formatIcrPercent(dsc)} ✓
          </>
        ) : (
          <>
            <XCircle className="h-3 w-3" />
            DSC {formatIcrPercent(dsc)}
            {minimumRent ? ` · ${formatMinimumRent(minimumRent)}` : null}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meetsDSC
      ? 'bg-green-50 text-green-700 border border-green-200'
      : 'bg-red-50 text-red-700 border border-red-200'
      }`}>
      {meetsDSC ? (
        <>
          <CheckCircle2 className="h-3.5 w-3.5" />
          DSC: {formatIcrPercent(dsc)} ✓
        </>
      ) : (
        <>
          <XCircle className="h-3.5 w-3.5" />
          DSC: {formatIcrPercent(dsc)} (needs {formatIcrPercent(requiredDSC)})
          {minimumRent ? ` · ${formatMinimumRent(minimumRent)}` : null}
        </>
      )}
    </div>
  );
};

/* =============================================================================
   Residency Badge — shown when International / Expat filter is active
============================================================================= */
function getResidencyChipLabel(residencyFilter) {
  const token = normalizeFilterToken(residencyFilter);
  if (token === "international") return "International";
  if (token === "xpat" || token === "expat") return "Expat";
  return null;
}

const ResidencyBadge = ({ label, compact = false }) => {
  if (!label) return null;

  return (
    <div
      className={
        "inline-flex shrink-0 items-center gap-1 rounded-full border border-sky-200 bg-sky-50 font-semibold text-sky-700 whitespace-nowrap " +
        (compact ? "px-2 py-0.5 text-[10px]" : "gap-1.5 px-2.5 py-1 text-xs")
      }
    >
      <CheckCircle2 className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
      {label}
    </div>
  );
};

/* =============================================================================
   Frontend calculator (IGNORE backend calc_* fields)
   - User inputs: property value, deposit amount, mortgage amount, rent
   - Product provides: rate, product term, mortgage term, fees, etc.
   - Repayment monthly payment matches your screenshot by:
       1) adding arrangement fee to loan amount
       2) using product mortgage term (if provided) for PMT
       3) computing interest paid + remaining balance across product term months
============================================================================= */

function getProductRatePercent(p) {
  return (
    firstFinite(
      p?.product_rate_percent,
      p?.product_rate,
      p?.rate_percent,
      p?.rate,
      p?.interest_rate,
      p?.interest_rate_percent
    ) ?? 0
  );
}

function getProductTermYears(p, mortgageTermYears) {
  const raw =
    p?.product_termsyears ??
    p?.product_term_years ??
    p?.product_term;

  const s = String(raw ?? "").trim();
  if (!s) return mortgageTermYears;

  if (s.toLowerCase() === "lifetime") return mortgageTermYears;

  const n = firstFinite(raw);
  if (Number.isFinite(n) && n > 0) return n;

  return mortgageTermYears;
}

function resolveArrangementFeeAmount(p, mortgageAmount) {
  return calculateArrangementFee(p, mortgageAmount).arrangementFee;
}

function getMortgageTermYears(p, ctx) {
  // Prefer product mortgage term if present (matches your screenshot)
  const fromProduct = firstFinite(
    p?.mortgage_term_years,
    p?.mortgage_term,
    p?.mortgage_term_yrs
  );

  const fromCtx = firstFinite(ctx?.mortgageTermYears);

  return Number.isFinite(fromProduct) && fromProduct > 0
    ? fromProduct
    : Number.isFinite(fromCtx) && fromCtx > 0
      ? fromCtx
      : 25;
}

function buildFrontendCalc(p, repaymentType, ctx) {
  const propertyValue = safeNum(ctx?.propertyValue);
  const mortgageAmount = safeNum(ctx?.mortgageAmount);
  const rent = safeNum(ctx?.estimatedRent);

  const mortgageTermYears = getMortgageTermYears(p, ctx);
  const productTermYears = getProductTermYears(p, mortgageTermYears);

  const annualRate = safeNum(getProductRatePercent(p));

  // Fees
  const arrangementFee = resolveArrangementFeeAmount(p, mortgageAmount);
  const applicationFee = Math.max(0, safeNum(p?.application_fee));
  const valuationFee = Math.max(0, safeNum(p?.valuation_fee));
  const legalFee = Math.max(0, safeNum(p?.legal_fee)); // optional
  const totalFees = arrangementFee + applicationFee + valuationFee + legalFee;

  const totalLoanAmount = mortgageAmount + arrangementFee;

  // LTV scenario (we still compute it, but UI prefers product LTV if present)
  const ltv = propertyValue > 0 ? (mortgageAmount / propertyValue) * 100 : 0;

  const totalTermMonths = termMonths(mortgageTermYears);
  const productTermMonths = termMonths(productTermYears);

  // Interest only
  const monthlyPaymentIO = interestOnlyPayment(totalLoanAmount, annualRate);
  const costOverProductTermIO = monthlyPaymentIO * productTermMonths;
  const totalCostIO = costOverProductTermIO + totalFees;
  const icrIO =
    rent > 0 && monthlyPaymentIO > 0 ? (rent / monthlyPaymentIO) * 100 : 0;

  // Repayment (amortising)
  const monthlyPaymentRep = amortisedPayment(totalLoanAmount, annualRate, totalTermMonths);
  const { interestPaid: costOverProductTermRep, remainingBalance } = repaymentOverProductTerm(
    totalLoanAmount,
    annualRate,
    monthlyPaymentRep,
    productTermMonths
  );
  const totalCostRep = costOverProductTermRep + totalFees;
  const icrRep =
    rent > 0 && monthlyPaymentRep > 0 ? (rent / monthlyPaymentRep) * 100 : 0;

  const dsc = calculateDsc(p, {
    loanIncludingFee: totalLoanAmount,
    termMonthsTotal: totalTermMonths,
    estimatedRent: rent,
    propertyType: ctx?.propertyType,
    borrowerType: ctx?.borrowerType,
    taxpayerType: ctx?.taxpayerType,
  });

  const isRepayment = repaymentType === "Repayment";
  const chosenDsc = isRepayment ? dsc.repayment : dsc.interestOnly;
  const chosen = isRepayment
    ? {
      monthly_payment: monthlyPaymentRep,
      total_cost: totalCostRep,
      icr: icrRep,
      remaining_balance: remainingBalance,
      cost_over_product_term: costOverProductTermRep,
    }
    : {
      monthly_payment: monthlyPaymentIO,
      total_cost: totalCostIO,
      icr: icrIO,
      remaining_balance: totalLoanAmount,
      cost_over_product_term: costOverProductTermIO,
    };

  return {
    _isFrontendCalc: true,
    interest_rate: annualRate,

    // helpful for UI/debug
    loan_amount: mortgageAmount,
    total_loan_amount: totalLoanAmount,

    arrangement_fee: arrangementFee,
    application_fee: applicationFee,
    valuation_fee: valuationFee,
    legal_fee: legalFee,
    total_fees: totalFees,

    ltv,
    mortgage_term_years: mortgageTermYears,
    product_term_years: productTermYears,

    assessed_rent: dsc.assessedRent,
    required_dsc: dsc.required,
    dsc_interest_only: dsc.interestOnly,
    dsc_repayment: dsc.repayment,
    dsc_passes: dsc.passesAny,
    dsc_missing_fields: dsc.missing,
    dsc_percent: chosenDsc.dscPercent,
    dsc_minimum_rent: chosenDsc.minimumRent,

    ...chosen,
  };
}

/** Build calc object from backend-supplied fields (from get_mortgage_products_details). */
function buildBackendCalc(p, repaymentType, ctx) {
  const mortgageAmount = safeNum(ctx?.mortgageAmount ?? p?.mortgage_amount ?? p?.loan_amount);
  const propertyValue = safeNum(ctx?.propertyValue);
  const rent = safeNum(ctx?.estimatedRent);

  const calcData = repaymentType === "Repayment"
    ? p?.calc_repayment
    : p?.calc_interest_only;

  const monthlyPayment = firstFinite(
    calcData?.monthly_payment,
    p?.calc_monthly_payment,
    p?.monthly_payment
  );
  const totalCost = firstFinite(
    calcData?.total_cost,
    p?.calc_total_cost,
    p?.total_cost
  );
  const interestRate = firstFinite(
    calcData?.interest_rate,
    p?.calc_interest_rate,
    p?.interest_rate,
    p?.product_rate_percent,
    p?.product_rate,
    p?.rate
  );
  const ltv = firstFinite(
    calcData?.ltv,
    p?.calc_ltv,
    p?.ltv,
    p?.ltv_percent,
    p?.ity_percent
  );
  const arrangementFee = firstFinite(
    calcData?.arrangement_fee,
    p?.calc_arrangement_fee,
    p?.arrangement_fee,
    p?.product_fee_amount
  );
  const totalFees = firstFinite(
    calcData?.total_fees,
    p?.calc_total_fees,
    p?.total_fees
  );
  const remainingBalance = firstFinite(
    calcData?.remaining_balance,
    p?.calc_remaining_balance,
    p?.remaining_balance
  );
  const mortgageTermYears =
    firstFinite(
      calcData?.mortgage_term_years,
      p?.mortgage_term_years,
      p?.mortgage_term,
      ctx?.mortgageTermYears
    ) ?? 25;
  const productTermYears = firstFinite(
    calcData?.product_term_years,
    p?.product_termsyears,
    p?.product_term_years,
    p?.product_term
  );

  // Backend always returns icr: 0.0 inside calc objects — always compute from rent/payment.
  const computedICR =
    rent > 0 && monthlyPayment != null && monthlyPayment > 0
      ? (rent / monthlyPayment) * 100
      : null;

  return {
    _isBackendCalc: true,
    interest_rate: interestRate ?? 0,
    loan_amount: mortgageAmount,
    total_loan_amount: mortgageAmount + (arrangementFee ?? 0),
    arrangement_fee: arrangementFee ?? 0,
    application_fee: safeNum(calcData?.application_fee ?? p?.application_fee) ?? 0,
    valuation_fee: safeNum(calcData?.valuation_fee ?? p?.valuation_fee) ?? 0,
    legal_fee: safeNum(calcData?.legal_fee ?? p?.legal_fee) ?? 0,
    total_fees: totalFees ?? arrangementFee ?? 0,
    ltv: ltv ?? (propertyValue > 0 ? (mortgageAmount / propertyValue) * 100 : 0),
    mortgage_term_years: mortgageTermYears,
    product_term_years: productTermYears,
    monthly_payment: monthlyPayment ?? 0,
    total_cost: totalCost ?? 0,
    remaining_balance: remainingBalance ?? 0,
    cost_over_product_term: totalCost ?? 0,
    icr: computedICR,
  };
}

function getEffectiveCalc(p, repaymentType, ctx) {
  // Frontend calculator is the source of truth for every figure it produces
  // (fees, loan incl. fee, payments, costs, DSC) so payment and DSC are always
  // based on the same loan. Backend values only fill fields it doesn't produce.
  const backend = buildBackendCalc(p, repaymentType, ctx);
  const frontend = buildFrontendCalc(p, repaymentType, ctx);

  return {
    ...backend,
    ...frontend,
    _isHybridCalc: true,
    ltv: backend?.ltv != null && Number.isFinite(Number(backend.ltv)) ? Number(backend.ltv) : frontend.ltv,
  };
}

/* =============================================================================
   DSC (Debt Service Coverage) Helper Functions
============================================================================= */

/**
 * Get DSC requirement percentage based on taxpayer type filter
 */
function getDSCRequirement(filters) {
  if (!filters.taxpayerType || filters.taxpayerType === "") {
    return 0; // No DSC filter applied
  }

  const type = String(filters.taxpayerType).toLowerCase();

  if (type.includes("higher") || type.includes("140")) {
    return 140; // Higher rate taxpayer
  }

  if (type.includes("basic") || type.includes("125")) {
    return 125; // Basic rate taxpayer
  }

  return 0;
}

/**
 * Normalize ICR/DSC value to a percentage suitable for threshold comparison.
 * Some feeds return ratio-style values (e.g. 5.5 meaning 550%).
 */
function normalizeDSCPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n <= 10) return n * 100;
  if (n <= 1) return n * 100;
  return n;
}

/**
 * Check if product meets DSC requirement
 * @deprecated Not currently used, kept for future reference
 */
// eslint-disable-next-line no-unused-vars
function meetsDSCRequirement(product, repaymentType, fallbackCtx, requiredDSC) {
  if (!requiredDSC || requiredDSC === 0) return true; // No DSC filter

  const calc = getEffectiveCalc(product, repaymentType, fallbackCtx);
  const dsc = normalizeDSCPercent(calc?.icr); // ICR normalized to percentage

  return dsc >= requiredDSC;
}

/* --------------------------------- UI bits -------------------------------- */

const SleepyPolarBearEmptyState = ({
  title = "No matching products to show.",
  subtitle = "Try adjusting the filters or loan details to see more options.",
}) => (
  <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
    <div className="relative w-56 h-40">
      <img
        src="/sleepy_bear.png"
        alt="Sleepy polar bear resting"
        className="h-full w-full object-contain mx-auto"
      />
      {/* Black Snoring Animation "Zzz" - positioned to left for polar bear nose */}
      <span className="snore-z text-lg left-[28%] top-[45%]" style={{ animationDelay: '0s' }}>Z</span>
      <span className="snore-z text-xl left-[22%] top-[38%]" style={{ animationDelay: '1s' }}>Z</span>
      <span className="snore-z text-2xl left-[16%] top-[31%]" style={{ animationDelay: '2s' }}>Z</span>
    </div>
    <p className="mt-4 text-base font-semibold text-slate-800">
      {title}
    </p>
    <p className="mt-1 text-sm text-slate-500 max-w-sm">
      {subtitle}
    </p>
  </div>
);

const DetailRow = ({ label, value, strong }) => (
  <div className="flex items-center justify-between gap-3 min-w-0">
    <dt className="text-xs md:text-sm text-slate-600 shrink-0 truncate">{label}</dt>
    <dd
      className={
        "text-xs md:text-sm text-right shrink-0 break-words " +
        (strong ? "font-semibold text-slate-900" : "text-slate-900")
      }
    >
      {value}
    </dd>
  </div>
);

const ProductCard = ({
  p,
  isOpen,
  onToggle,
  shortlisted,
  shortlistIsFull,
  onToggleShortlist,
  repaymentType,
  fallbackParams,
  onTrackClick,
  requiredDSC,
  residencyLabel,
  displaySnapshot,
}) => {
  const { reportError } = useError();

  const calc = getEffectiveCalc(p, repaymentType, fallbackParams) || {};
  const rate = safeNum(calc.interest_rate);
  const repayment = safeNum(calc.monthly_payment);
  const totalCost = safeNum(calc.total_cost);
  const dsc = safeNum(calc.dsc_percent);
  const productRequiredDSC = safeNum(calc.required_dsc);
  const dscMinimumRent = calc.dsc_minimum_rent || null;
  const showDscBadge = productRequiredDSC > 0 && (requiredDSC > 0 || Boolean(dscMinimumRent));

  const { ltvDisplay, mortgageTermDisplay, icrDisplay } = getBackendDisplayMetrics(
    p,
    calc,
    fallbackParams
  );

  // When shortlisted, use frozen snapshot so display does not change with filters
  const useSnapshot = shortlisted && displaySnapshot && typeof displaySnapshot === "object";
  const ltv = useSnapshot && displaySnapshot.ltv != null
    ? Number(displaySnapshot.ltv)
    : (Number.isFinite(ltvDisplay) ? ltvDisplay : NaN);
  const mortgageTermYears = useSnapshot && displaySnapshot.mortgageTermYears != null
    ? Number(displaySnapshot.mortgageTermYears)
    : (Number.isFinite(mortgageTermDisplay) ? mortgageTermDisplay : NaN);
  const icr = useSnapshot && displaySnapshot.icr != null
    ? Number(displaySnapshot.icr)
    : (Number.isFinite(icrDisplay) ? icrDisplay : NaN);
  const rateShown = useSnapshot && displaySnapshot.rate != null ? safeNum(displaySnapshot.rate) : rate;
  const repaymentShown = useSnapshot && displaySnapshot.monthlyPayment != null ? safeNum(displaySnapshot.monthlyPayment) : repayment;
  const totalCostShown = useSnapshot && displaySnapshot.totalCost != null ? safeNum(displaySnapshot.totalCost) : totalCost;

  const productTermYears = parseFloat(p?.product_termsyears || p?.product_term_years || "0");
  const loanAmount = safeNum(
    calc.loan_amount ?? fallbackParams?.mortgageAmount ?? p?.mortgage_amount ?? 0
  );

  const productId = p?.name || p?.product_id || "—";

  // Helper to check if scheduled date has passed
  const hasScheduleDatePassed = (product) => {
    const scheduleDate = product?.date_time ||
      product?.product_schedule_at ||
      product?.schedule_at ||
      product?.scheduled_at ||
      product?.product_schedule_date ||
      product?.schedule_date ||
      product?.scheduled_date;

    if (!scheduleDate) return false;

    try {
      let dateStr = String(scheduleDate).trim();

      // Convert DD-MM-YYYY HH:MM:SS format to ISO if needed
      if (dateStr.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}:\d{2}$/)) {
        const parts = dateStr.split(' ');
        const datePart = parts[0].split('-');
        const timePart = parts[1] || '00:00:00';
        dateStr = `${datePart[2]}-${datePart[1]}-${datePart[0]}T${timePart}`;
      } else if (dateStr.includes(' ') && !dateStr.includes('T')) {
        dateStr = dateStr.replace(' ', 'T');
      }

      const scheduleDateTime = new Date(dateStr);
      const now = new Date();

      if (isNaN(scheduleDateTime.getTime())) return false;

      return scheduleDateTime <= now;
    } catch (e) {
      return false;
    }
  };

  // Check for scheduled products: must have schedule flag AND date must not have passed
  const propertyChipLabels = productPropertyTypeChips(p).map((c) => c.label.toUpperCase());
  const productTypeChip = propertyChipLabels.includes(String(p?.product_type || "").trim().toUpperCase())
    ? undefined
    : p?.product_type;
  const hasScheduleFlag = p?.product_schedule === 1 || p?.product_schedule === "1" || p?._isScheduled === true;
  const scheduleDatePassed = hasScheduleDatePassed(p);
  const isScheduled = hasScheduleFlag && !scheduleDatePassed;

  const rawStatus = isScheduled
    ? "Scheduled"
    : p?.product_status || p?.status || "";
  const status = String(rawStatus || "").toLowerCase();

  // Only show badge for scheduled products, remove "Live" badge
  // eslint-disable-next-line no-unused-vars
  let statusLabel = null;
  // eslint-disable-next-line no-unused-vars
  let statusClasses = "";

  if (status === "scheduled" || isScheduled) {
    // eslint-disable-next-line no-unused-vars
    statusLabel = "Coming Soon";
    // eslint-disable-next-line no-unused-vars
    statusClasses = "coming-soon-badge text-white shadow-sm ring-1 ring-white/20";
  }

  const detailsAnchorRef = React.useRef(null);
  const [detailsPosition, setDetailsPosition] = React.useState(null);

  React.useLayoutEffect(() => {
    if (!isOpen) {
      setDetailsPosition(null);
      return;
    }
    const update = () => {
      const win = typeof window !== "undefined" ? window : { innerWidth: 800, innerHeight: 600 };
      // Mobile: compact bottom sheet — skip floating anchor positioning
      if (win.innerWidth < 768) {
        setDetailsPosition({ mobile: true });
        return;
      }
      const el = detailsAnchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const panelW = Math.min(760, win.innerWidth * 0.95);
      const marginRight = 24;
      const marginLeft = 16;
      let left = r.left;
      if (left + panelW > win.innerWidth - marginRight) left = win.innerWidth - panelW - marginRight;
      if (left < marginLeft) left = marginLeft;
      const spaceBelow = win.innerHeight - (r.bottom + 8);
      const minSpaceNeeded = 320;
      const openBelow = spaceBelow >= minSpaceNeeded;
      if (openBelow) {
        setDetailsPosition({ top: r.bottom + 8, left, bottom: undefined, mobile: false });
      } else {
        setDetailsPosition({ top: undefined, left, bottom: win.innerHeight - r.top + 8, mobile: false });
      }
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    if (typeof window === "undefined" || window.innerWidth >= 768) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const isMobileDetails =
    detailsPosition?.mobile === true ||
    (isOpen &&
      detailsPosition == null &&
      typeof window !== "undefined" &&
      window.innerWidth < 768);

  const handleTrackInteraction = React.useCallback(() => {
    if (isScheduled) return;
    Promise.resolve(onTrackClick?.(p)).catch(() => {});
  }, [isScheduled, onTrackClick, p]);

  const handleToggleDetails = async () => {
    try {
      onToggle?.();
    } catch (err) {
      reportError(err, { where: "ProductCard.handleToggleDetails", productId });
    }
  };

  const handleToggleShortlist = () => {
    try {
      onToggleShortlist?.();
    } catch (err) {
      reportError(err, { where: "ProductCard.handleToggleShortlist", productId });
    }
  };

  // Show all years (1-5) even if ERC is 0
  const ercYears = [1, 2, 3, 4, 5]
    .map((y) => {
      const v = safeNum(p?.[`year_${y}`]);
      // Include year if value is a finite number (including 0)
      return Number.isFinite(v) ? { year: y, value: v } : { year: y, value: 0 };
    });

  return (
    <div className="mb-2 md:mb-2.5">
      <div
        className="relative min-w-0 rounded-2xl border border-slate-300 bg-white"
        onClickCapture={handleTrackInteraction}
      >
        {/* "Launching shortly" overlay for scheduled products - blur layer */}
        {isScheduled && (
          <div className="absolute inset-0 z-10 pointer-events-none rounded-2xl bg-white/60 backdrop-blur-[1px]" />
        )}

        {/* "Coming soon" badge and "Launching shortly" text - centered */}
        {isScheduled && !isOpen && (
          <div className="absolute inset-0 z-[50] pointer-events-none flex flex-col items-center justify-center gap-2">
            <div className="z-[60]">
              <span className="comingSoon-pill inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white relative">
                <span className="comingSoon-dot" />
                <span className="relative z-10">Coming soon</span>
              </span>
            </div>
            <div className="text-center px-6 z-[60]">
              <div className="text-[36px] md:text-[48px] font-extrabold tracking-tight text-sky-400 drop-shadow-sm">
                Launching shortly
              </div>
            </div>
          </div>
        )}

        {/* Mobile: chips scroll full width; shortlist sits clear on the right */}
        <div
          className={`relative flex md:hidden items-center rounded-t-2xl border-b border-slate-200 px-3 py-2 min-w-0 ${isScheduled ? "opacity-70" : ""}`}
        >
          <div
            className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden hide-scrollbar touch-pan-x overscroll-x-contain pr-[9rem]"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="flex w-max max-w-none flex-nowrap items-center gap-1.5 py-0.5">
              <PropertyTypeChips product={p} compact />
              <ChipRow
                term={productTermYears}
                type={productTypeChip}
                ltv={Number.isFinite(ltv) ? Math.round(ltv) : undefined}
                productTermRaw={firstNonEmpty(p?.product_termsyears, p?.product_term_years, p?.product_term)}
                freeValuation={productHasFreeValuation(p)}
                nowrap
                compact
              />
              {showDscBadge && !(requiredDSC > 0) && (
                <DSCBadge dsc={dsc} requiredDSC={productRequiredDSC} minimumRent={dscMinimumRent} compact />
              )}
              {residencyLabel && <ResidencyBadge label={residencyLabel} compact />}
            </div>
          </div>
          <button
            id="add-to-shortlist"
            type="button"
            onClick={handleToggleShortlist}
            disabled={isScheduled || (!shortlisted && shortlistIsFull)}
            className={
              "absolute right-3 top-1/2 z-[1] shrink-0 -translate-y-1/2 inline-flex items-center gap-1 rounded-full border bg-white px-2.5 py-1.5 text-[10px] font-semibold transition " +
              (isScheduled
                ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed opacity-50"
                : shortlisted
                  ? "border-sky-300 bg-sky-100 text-sky-700"
                  : !shortlisted && shortlistIsFull
                    ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "border-slate-200 text-slate-700 hover:bg-slate-50")
            }
            title={
              isScheduled
                ? "Product not yet available"
                : shortlisted
                  ? "Added to shortlist"
                  : shortlistIsFull
                    ? "You can shortlist up to 5 products"
                    : "Shortlist and apply"
            }
            aria-label={shortlisted ? "Remove from shortlist" : "Shortlist and apply"}
          >
            {shortlisted ? (
              <>
                <BookmarkCheck className="h-3.5 w-3.5 shrink-0" />
                <span className="whitespace-nowrap leading-none">Added</span>
              </>
            ) : shortlistIsFull ? (
              <>
                <Bookmark className="h-3.5 w-3.5 shrink-0" />
                <span className="whitespace-nowrap leading-none">Full</span>
              </>
            ) : (
              <>
                <Bookmark className="h-3.5 w-3.5 shrink-0" />
                <span className="whitespace-nowrap text-[10px] leading-none">{SHORTLIST_APPLY_LABEL}</span>
              </>
            )}
          </button>
        </div>

        {/* Desktop: original header */}
        <div className={`hidden md:flex items-center justify-between gap-3 rounded-t-2xl border-b border-slate-200 px-3 py-2.5 ${isScheduled ? "opacity-70" : ""}`}>
          <div className="min-w-0 flex items-center gap-2 flex-wrap">
            <PropertyTypeChips product={p} />
            <ChipRow
              term={productTermYears}
              type={productTypeChip}
              ltv={Number.isFinite(ltv) ? Math.round(ltv) : undefined}
              productTermRaw={firstNonEmpty(p?.product_termsyears, p?.product_term_years, p?.product_term)}
              freeValuation={productHasFreeValuation(p)}
            />
            {showDscBadge && <DSCBadge dsc={dsc} requiredDSC={productRequiredDSC} minimumRent={dscMinimumRent} />}
            {residencyLabel && <ResidencyBadge label={residencyLabel} />}
          </div>

          <div ref={detailsAnchorRef} className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={handleToggleShortlist}
              disabled={isScheduled || (!shortlisted && shortlistIsFull)}
              className={
                "hidden md:inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition " +
                (isScheduled
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed opacity-50"
                  : shortlisted
                    ? "border-sky-300 bg-sky-100 text-sky-700"
                    : !shortlisted && shortlistIsFull
                      ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
              }
              title={
                isScheduled
                  ? "Product not yet available"
                  : shortlisted
                    ? "Added to shortlist"
                    : shortlistIsFull
                      ? "You can shortlist up to 5 products"
                      : "Shortlist and apply"
              }
            >
              {shortlisted ? (
                <>
                  <BookmarkCheck className="h-5 w-5" />
                  Added to shortlist
                </>
              ) : shortlistIsFull ? (
                <>
                  <Bookmark className="h-5 w-5" />
                  Shortlist full
                </>
              ) : (
                <>
                  <Bookmark className="h-5 w-5" />
                  {SHORTLIST_APPLY_LABEL}
                </>
              )}
            </button>

            <button
              data-role="details-toggle"
              onClick={handleToggleDetails}
              disabled={isScheduled}
              className={`inline-flex items-center gap-1.5 text-[16px] md:text-[17px] font-semibold ${isScheduled
                ? "text-slate-400 cursor-not-allowed opacity-50"
                : "text-sky-700 hover:text-sky-800"
                } transition-colors`}
            >
              Full details
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                  }`}
              />
            </button>
          </div>
        </div>

        {/* Mobile: compact body — rate left, metrics right */}
        <div className={`flex md:hidden items-stretch gap-2 px-3 py-2 ${isScheduled ? "opacity-50" : ""}`}>
          <div
            className={`w-[118px] shrink-0 flex flex-col items-center justify-center rounded-xl bg-sky-500 px-2 py-2.5 text-white shadow-sm ${isScheduled ? "opacity-60" : ""}`}
          >
            <div className="text-[10px] font-semibold leading-tight text-center">Interest rate</div>
            <div className="mt-0.5 text-xl font-extrabold leading-none">
              {Number.isFinite(rateShown) ? rateShown.toFixed(2) : "—"}%
            </div>
          </div>
          <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5 py-0.5">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-slate-500 shrink-0">Monthly</span>
              <span className="font-bold text-slate-900 truncate">£{Num(repaymentShown)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-slate-500 shrink-0">Arrangement fee</span>
              <span className="font-bold text-slate-900 truncate text-right">{formatProductFeeDisplay(p)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-slate-500 shrink-0">Cost over term</span>
              <span className="font-bold text-slate-900 truncate">£{Num(totalCostShown)}</span>
            </div>
          </div>
        </div>

        {/* Mobile: footer — full details */}
        <div
          className={`md:hidden border-t border-slate-200 px-3 py-2 ${isScheduled ? "opacity-70" : ""}`}
        >
          <button
            data-role="details-toggle"
            type="button"
            onClick={handleToggleDetails}
            disabled={isScheduled}
            className={`w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold ${isScheduled
              ? "text-slate-400 cursor-not-allowed opacity-50"
              : "text-sky-700 hover:text-sky-800"
              } transition-colors`}
          >
            Full details
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        {/* Desktop: original body */}
        <div className={`hidden md:block px-3 py-2.5 ${isScheduled ? "opacity-50" : ""}`}>
          <div className="grid grid-cols-[max-content,1fr,1fr,1fr] gap-x-2 items-center">
            <div className={`flex flex-col items-center justify-center rounded-xl bg-sky-500 px-6 py-4 min-h-[84px] text-white shadow-sm ${isScheduled ? "opacity-60" : ""}`}>
              <div className="text-[17px] font-semibold leading-tight">
                Interest rate
              </div>
              <div className="mt-0.5 text-[34px] font-extrabold leading-none">
                {Number.isFinite(rateShown) ? rateShown.toFixed(2) : "—"}%
              </div>
            </div>

            <div className="flex flex-col items-center justify-center text-center">
              <div className="text-[17px] text-slate-600 leading-tight">
                Monthly repayments
              </div>
              <div className="mt-0.5 text-[24px] font-extrabold text-slate-900 leading-none">
                £{Num(repaymentShown)}
              </div>
            </div>

            <div className="flex flex-col items-center justify-center text-center">
              <div className="text-[17px] text-slate-600 leading-tight">
                Arrangement fee
              </div>
              <div className="mt-0.5 text-[24px] font-extrabold text-slate-900 leading-none">
                {formatProductFeeDisplay(p)}
              </div>
            </div>

            <div className="flex flex-col items-center justify-center text-center">
              <div className="text-[17px] text-slate-600 leading-tight">
                Cost over product term
              </div>
              <div className="mt-0.5 text-[24px] font-extrabold text-slate-900 leading-none">
                £{Num(totalCostShown)}
              </div>
            </div>
          </div>
        </div>

        {isOpen && createPortal(
          <>
            {isMobileDetails ? (
              <div
                className="fixed inset-0 z-[99998] bg-black/40 md:hidden"
                onClick={onToggle}
                aria-hidden
              />
            ) : null}
            <div
              data-role="details-popover"
              className={
                isMobileDetails
                  ? "fixed z-[99999] inset-x-3 bottom-[4.75rem] max-h-[min(58dvh,420px)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl flex flex-col"
                  : "fixed z-[99999] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl flex flex-col w-[min(760px,95vw)] max-h-[85vh]"
              }
              style={
                isMobileDetails
                  ? { zIndex: 99999 }
                  : {
                      zIndex: 99999,
                      top: detailsPosition?.top != null ? `${detailsPosition.top}px` : detailsPosition ? "auto" : "1.5rem",
                      left: detailsPosition ? `${detailsPosition.left}px` : "1.5rem",
                      right: detailsPosition ? "auto" : "1.5rem",
                      bottom: detailsPosition?.bottom != null ? `${detailsPosition.bottom}px` : detailsPosition ? "auto" : "1.5rem",
                      maxHeight: detailsPosition ? "85vh" : "calc(100dvh - 3rem)",
                    }
              }
            >
            {/* Sticky header: always visible so Close is easy to find */}
            <div className="flex-shrink-0 sticky top-0 z-10 flex items-center justify-between gap-3 bg-white border-b border-slate-200 px-3 py-2.5 md:px-6 md:py-4 min-h-[52px] md:min-h-[60px] pt-[max(0.5rem,env(safe-area-inset-top))] md:pt-4 rounded-t-2xl">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] md:text-xs font-semibold uppercase tracking-wide text-sky-600">
                  Product details
                </div>
                <div className="mt-0.5 text-sm md:text-lg font-semibold text-slate-900 truncate pr-2">
                  {p?.product_name || "Mortgage product"}
                </div>
                <div className="mt-0.5 text-[11px] md:text-xs text-slate-600">
                  Product ID: <span className="font-semibold text-slate-900">{productId}</span>
                </div>
                {(showDscBadge || residencyLabel) && (
                  <div className="mt-1 md:mt-2 flex flex-wrap items-center gap-1.5">
                    {showDscBadge && <DSCBadge dsc={dsc} requiredDSC={productRequiredDSC} minimumRent={dscMinimumRent} />}
                    {residencyLabel && <ResidencyBadge label={residencyLabel} />}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onToggle}
                aria-label="Close details"
                className="flex-shrink-0 flex items-center justify-center rounded-xl bg-slate-200 text-slate-600 hover:bg-slate-300 active:bg-slate-400 font-semibold min-h-[40px] min-w-[40px] p-0 md:h-9 md:w-9 md:min-h-0 md:min-w-0 md:rounded-lg md:border md:border-slate-200 md:bg-transparent md:text-slate-700 md:hover:bg-slate-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-2.5 md:px-6 md:py-4 min-h-0">
              <div className="grid grid-cols-1 md:grid-cols-[2fr,1.4fr] gap-2.5 md:gap-6">
                <div className="min-w-0">
                  <dl className="grid grid-cols-2 gap-x-2 gap-y-1.5 md:grid-cols-1 md:gap-y-2 [&>*]:flex [&>*]:flex-col [&>*]:items-start [&>*]:gap-0.5 [&>*]:min-w-0 [&_dd]:text-left md:[&>*]:flex-row md:[&>*]:items-center md:[&>*]:justify-between md:[&_dd]:text-right">
                    <DetailRow label="APRC" value={formatAprcValue(getAprcValue(p))} strong />
                    {loanAmount > 0 && (
                      <DetailRow label="Loan amount" value={`£${Num(loanAmount)}`} strong />
                    )}
                    {(() => {
                      const propertyValue = safeNum(fallbackParams?.propertyValue) || safeNum(p?.property_value);
                      return propertyValue > 0 ? (
                        <DetailRow label="Property value" value={`£${Num(propertyValue)}`} strong />
                      ) : null;
                    })()}
                    <DetailRow
                      label="Mortgage term"
                      value={Number.isFinite(mortgageTermYears) ? `${mortgageTermYears} yrs` : "—"}
                    />
                    <DetailRow
                      label="Monthly payment p/m"
                      value={`£${Num(repayment)}`}
                      strong
                    />
                    <DetailRow
                      label="Cost over product term"
                      value={`£${Num(totalCost)}`}
                    />
                    {repaymentType === "Repayment" && Number.isFinite(calc?.remaining_balance) ? (
                      <DetailRow
                        label="Remaining balance"
                        value={`£${Num(calc.remaining_balance)}`}
                      />
                    ) : null}
                    <DetailRow label="Product fee" value={formatProductFeeDisplay(p)} />
                    <DetailRow
                      label="Total fees"
                      value={`£${Num(calc?.total_fees)}`}
                    />
                    <DetailRow
                      label="DSC / ICR"
                      value={formatIcrPercent(icr)}
                      strong
                    />
                  </dl>
                  <p className="mt-2 md:mt-3 hidden md:block text-[11px] md:text-xs text-slate-500">
                    * Some lenders may charge additional fees which may increase the monthly payment.
                  </p>
                </div>

                <div className="min-w-0 md:border-l md:border-slate-200 md:pl-6">
                  <div className="text-[11px] md:text-sm font-semibold text-slate-900 mb-1 md:mb-2">
                    Early repayment charges
                  </div>
                  {ercYears.length === 0 ? (
                    <p className="text-[11px] md:text-sm text-slate-500">
                      No early repayment charges information provided.
                    </p>
                  ) : (
                    <>
                      <div className="grid grid-cols-5 gap-x-1 text-center md:hidden">
                        {ercYears.map((erc) => (
                          <div key={`label-${erc.year}`} className="text-[10px] font-medium text-slate-500 leading-tight">
                            Year {erc.year}
                          </div>
                        ))}
                        {ercYears.map((erc) => (
                          <div key={`value-${erc.year}`} className="mt-0.5 text-[12px] font-semibold text-slate-900 tabular-nums leading-tight">
                            {formatErcPercent(erc.value)}
                          </div>
                        ))}
                      </div>
                      <dl className="hidden md:grid grid-cols-1 gap-y-2 [&>*]:flex [&>*]:flex-col [&>*]:items-start [&>*]:gap-0.5 [&>*]:min-w-0 [&_dd]:text-left">
                        {ercYears.map((erc) => (
                          <DetailRow
                            key={erc.year}
                            label={`Year ${erc.year}`}
                            value={formatErcPercent(erc.value)}
                          />
                        ))}
                      </dl>
                    </>
                  )}
                  <p className="mt-2 md:hidden text-[11px] text-slate-500">
                    * Some lenders may charge additional fees which may increase the monthly payment.
                  </p>
                </div>
              </div>
            </div>
          </div>
          </>,
          document.body
        )}
      </div>
    </div>
  );
};

const LockedResultsGate = ({
  hiddenProducts,
  hiddenCount,
  totalCount,
  onUnlock,
  repaymentType,
  fallbackParams,
  requiredDSC,
  residencyLabel,
}) => {
  // Show a couple of real (blurred) cards behind the CTA so the user can see there's more.
  const teasers = hiddenProducts.filter((p) => !p?._isScheduled).slice(0, 2);
  return (
    <div className="relative mt-2">
      <div className="pointer-events-none select-none blur-[6px] opacity-70" aria-hidden="true">
        {teasers.map((p) => (
          <ProductCard
            key={p?.name ?? Math.random().toString(36).slice(2)}
            p={p}
            isOpen={false}
            onToggle={() => {}}
            shortlisted={false}
            shortlistIsFull={false}
            onToggleShortlist={() => {}}
            repaymentType={repaymentType}
            fallbackParams={fallbackParams}
            onTrackClick={() => {}}
            requiredDSC={requiredDSC}
            residencyLabel={residencyLabel}
          />
        ))}
      </div>

      <div className="absolute inset-0 flex flex-col items-center justify-start rounded-2xl bg-gradient-to-b from-white/40 via-white/80 to-white pt-4 sm:pt-6">
        <div className="w-full flex flex-col items-center text-center px-4 pb-4 pt-2">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-sky-600">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h3 className="text-base font-bold text-slate-900">
            {hiddenCount} more product{hiddenCount === 1 ? "" : "s"} match your search
          </h3>
          <p className="mt-1 max-w-sm text-sm text-slate-600">
            Register for free to unlock the full list and keep sourcing.
          </p>
          <button
            type="button"
            onClick={onUnlock}
            className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-xl bg-sky-500 px-6 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-sky-600"
          >
            See all {totalCount} products — free
          </button>
        </div>
      </div>
    </div>
  );
};

const SortControl = ({ sortBy, setSortBy }) => {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);
  const { reportError } = useError();

  React.useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const items = [
    { key: "cost", label: "Total cost (low → high)" },
    { key: "repayment", label: "Monthly repayments (low → high)" },
    { key: "fee", label: "Arrangement fee (low → high)" },
    { key: "rate", label: "Interest rate (low → high)" },
  ];

  const current = items.find((i) => i.key === sortBy)?.label ?? "Sort by";

  const onPick = (key) => {
    try {
      const valid = items.some((i) => i.key === key);
      if (!valid) throw new Error(`Unknown sort key: ${key}`);
      setSortBy(key);
      setOpen(false);
    } catch (err) {
      reportError(err, { where: "SortControl.onPick" });
    }
  };

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold shadow-sm hover:shadow md:px-4"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <SlidersHorizontal className="h-4 w-4 text-slate-600" />
        <span className="text-slate-800">Sort By:</span>
        <span className="text-slate-500 font-medium">{current}</span>
      </button>

      {open && (
        <div
          ref={popRef}
          className="absolute right-0 z-30 mt-2 w-[280px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
          role="listbox"
        >
          {items.map((it) => {
            const active = it.key === sortBy;
            return (
              <button
                key={it.key}
                role="option"
                aria-selected={active}
                onClick={() => onPick(it.key)}
                className={
                  "w-full flex items-center justify-between px-4 py-3 text-sm " +
                  (active ? "bg-sky-50 text-sky-800" : "hover:bg-slate-50 text-slate-800")
                }
              >
                <span>{it.label}</span>
                {active ? <Check className="h-4 w-4" /> : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function BorrowerSourcing({ variant = "btl" } = {}) {
  const navigate = useNavigate();
  const isComplexBtl = variant === "complex-btl";
  const productLabel = isComplexBtl ? "Complex BTL" : "BTL";
  const filtersStorageKey = isComplexBtl
    ? "ds_complex_btl_sourcing_filters"
    : "ds_sourcing_filters";
  const onboardingSeenKey = isComplexBtl
    ? "complex_btl_polar_onboarding_seen"
    : "btl_polar_onboarding_seen";
  const tourDoneKey = isComplexBtl ? "complex_btl_polar_tour_done" : "btl_polar_tour_done";
  const signupPromptKey = isComplexBtl
    ? "complex_btl_sourcing_signup_prompt_seen"
    : BTL_SIGNUP_PROMPT_KEY;
  const cartMascotSeenKey = isComplexBtl
    ? "complex_btl_cart_mascot_seen"
    : BTL_CART_MASCOT_SEEN_KEY;
  const reviewStorageKey = isComplexBtl ? "complex_btl_sourcing" : "btl_sourcing";

  const MAX_SHORTLIST = 5;
  const { track: trackEngagementEvent } = useSourcingEngagement("BTL");
  const {
    open: reviewPromptOpen,
    dismiss: dismissReviewPrompt,
    openNow: openReviewPrompt,
  } = useReviewPrompt({ storageKey: reviewStorageKey, delayMs: REVIEW_PROMPT_DELAY_MS, requireInteraction: true });

  React.useEffect(() => {
    rememberSourcingCategory(isComplexBtl ? "CBTL" : "BTL");
  }, [isComplexBtl]);

  const [ephemeralError, setEphemeralError] = React.useState("");
  const [showOnboarding, setShowOnboarding] = React.useState(() => {
    if (typeof window === "undefined" || isMobileTourViewport()) return false;
    try {
      return !localStorage.getItem(onboardingSeenKey);
    } catch {
      return true;
    }
  });
  const [showSignupPrompt, setShowSignupPrompt] = React.useState(false);
  const [signupPromptStartOnForm, setSignupPromptStartOnForm] = React.useState(false);
  const [tourOpen, setTourOpen] = React.useState(false);
  const [tourStepIndex, setTourStepIndex] = React.useState(0);
  const TOUR_STEP_IDS = ["quickbar", "results-panel", "filters", "add-to-shortlist", "shortlist-cart-window"];
  const tourStepId = TOUR_STEP_IDS[tourStepIndex] || "quickbar";
  const tourFiltersBaselineRef = React.useRef(null);

  // Soft signup prompt for guests after 40s. Closing (X / Not now) dismisses for this session.
  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isAuthenticated()) return undefined;
    try {
      if (sessionStorage.getItem(signupPromptKey) === "1") return undefined;
    } catch {
      /* ignore */
    }

    const timer = window.setTimeout(() => {
      if (isAuthenticated()) return;
      try {
        if (sessionStorage.getItem(signupPromptKey) === "1") return;
      } catch {
        /* ignore */
      }
      setSignupPromptStartOnForm(false);
      setShowSignupPrompt(true);
    }, 40000);

    return () => window.clearTimeout(timer);
  }, [signupPromptKey]);

  const dismissSignupPrompt = React.useCallback(() => {
    setShowSignupPrompt(false);
    setSignupPromptStartOnForm(false);
    try {
      sessionStorage.setItem(signupPromptKey, "1");
    } catch {
      /* ignore */
    }
  }, [signupPromptKey]);

  const handleSignupPromptSuccess = React.useCallback(({ keepOpen = false } = {}) => {
    if (!keepOpen) {
      setShowSignupPrompt(false);
      setSignupPromptStartOnForm(false);
    }
    try {
      sessionStorage.setItem(signupPromptKey, "1");
    } catch {
      /* ignore */
    }
  }, [signupPromptKey]);

  const TOUR_FILTER_KEYS = [
    "ltv",
    "epcGreen",
    "mortgageTerm",
    "productTerm",
    "productType",
    "loanPurpose",
    "borrowerType",
    "homeowner",
    "residency",
    "btlExp",
    "credit",
    "tenancyType",
    "taxpayerType",
    "tenure",
    "leaseTerm",
    "exAuthority",
    "depositOrigination",
    "day1Remortgage",
    // repaymentType is part of the quick bar, but leaving it here doesn't hurt for step 3.
    "repaymentType",
  ];
  const reportError = React.useCallback((err, meta) => {
    // eslint-disable-next-line no-console
    console.error("Reported error:", err, meta);
    setEphemeralError(
      typeof err?.message === "string"
        ? err.message
        : "Something went wrong. Please try again."
    );
  }, []);

  const [qs, setQs] = React.useState(() => {
    const saved = readJsonStorage(LS_SOURCING_QS);
    if (saved) return { propertyValue: "", depositAmount: "", mortgageAmount: "", estRent: "", ...saved };
    return {
      propertyValue: "",
      depositAmount: "",
      mortgageAmount: "",
      estRent: "",
    };
  });

  const lastSearchInputsRef = React.useRef(
    readJsonStorage(LS_SOURCING_LAST_SEARCH) || {
      property_value: 0,
      mortgage_amount: 0,
      deposit_amount: 0,
      est_rent: 0,
    }
  );

  React.useEffect(() => {
    localStorage.setItem(LS_SOURCING_QS, JSON.stringify(qs));
    const snapshot = {
      property_value: parseMoney(qs.propertyValue),
      mortgage_amount: parseMoney(qs.mortgageAmount),
      deposit_amount:
        parseMoney(qs.depositAmount) > 0
          ? parseMoney(qs.depositAmount)
          : Math.max(0, parseMoney(qs.propertyValue) - parseMoney(qs.mortgageAmount)),
      est_rent: parseMoney(qs.estRent),
    };
    if (
      snapshot.property_value > 0 ||
      snapshot.mortgage_amount > 0 ||
      snapshot.deposit_amount > 0 ||
      snapshot.est_rent > 0
    ) {
      lastSearchInputsRef.current = snapshot;
      try {
        localStorage.setItem(LS_SOURCING_LAST_SEARCH, JSON.stringify(snapshot));
      } catch {
        /* ignore */
      }
    }
  }, [qs]);

  const [errors, setErrors] = React.useState({
    propertyValue: "",
    depositAmount: "",
    mortgageAmount: "",
    estRent: "",
  });
  const [shakingQuickFields, setShakingQuickFields] = React.useState({});
  const shakeTimerRef = React.useRef(null);

  React.useEffect(() => () => window.clearTimeout(shakeTimerRef.current), []);

  /**
   * Mobile only, after the user leaves a field (not while typing): shake the
   * invalid inputs and vibrate (Android; iOS browsers have no Vibration API).
   * Empty fields are skipped — the inline message already prompts for them.
   */
  const shakeInvalidQuickFields = React.useCallback((keys) => {
    if (!isMobileViewport() || keys.length === 0) return;
    window.clearTimeout(shakeTimerRef.current);
    setShakingQuickFields({});
    window.requestAnimationFrame(() => {
      setShakingQuickFields(Object.fromEntries(keys.map((k) => [k, true])));
      shakeTimerRef.current = window.setTimeout(() => setShakingQuickFields({}), 450);
    });
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate([60, 40, 60]);
      }
    } catch {
      /* vibration unsupported */
    }
  }, []);

  const onlyNum = (s) => /^\d*\.?\d*$/.test(s);
  const clampNonNeg = (s) => {
    if (s === "") return "";
    const n = Number(s);
    return Number.isFinite(n) && n >= 0 ? s : "0";
  };

  const limitsFromPV = (pv) => {
    const pvNum = safeNum(pv);
    const depMin = Math.ceil(0.15 * pvNum);
    const depMax = pvNum;
    const mortMin = Math.ceil(0.15 * pvNum);
    const mortMax = Math.floor(0.85 * pvNum);
    return { depMin, depMax, mortMin, mortMax };
  };

  const validateAll = React.useCallback((state) => {
    const out = { propertyValue: "", depositAmount: "", mortgageAmount: "", estRent: "" };
    const pv = safeNum(state.propertyValue);
    const dep = safeNum(state.depositAmount);
    const mort = safeNum(state.mortgageAmount);
    const rent = safeNum(state.estRent);

    if (state.propertyValue === "" || pv <= 0) out.propertyValue = "Enter a property value.";
    else {
      if (pv < PV_MIN) out.propertyValue = `Minimum £${PV_MIN.toLocaleString()}.`;
      else if (pv > PV_MAX) out.propertyValue = `Must be ≤ £${PV_MAX.toLocaleString()}.`;
    }

    if (!out.propertyValue) {
      const { depMin, depMax, mortMin, mortMax } = limitsFromPV(pv);

      if (state.depositAmount === "") out.depositAmount = "Enter a deposit amount.";
      else {
        if (dep < depMin)
          out.depositAmount = `Must be ≥ £${depMin.toLocaleString()} (15% of property value).`;
        else if (dep > depMax)
          out.depositAmount = `Must be ≤ £${depMax.toLocaleString()} (100% of property value).`;
      }

      if (state.mortgageAmount === "") out.mortgageAmount = "Enter a mortgage amount.";
      else {
        if (mort < mortMin)
          out.mortgageAmount = `Must be ≥ £${mortMin.toLocaleString()} (15% of property value).`;
        else if (mort > mortMax)
          out.mortgageAmount = `Must be ≤ £${mortMax.toLocaleString()} (85% of property value).`;
      }

      if (state.depositAmount !== "" && state.mortgageAmount !== "") {
        const diff = dep + mort - pv;
        if (Math.abs(diff) > 0.5) {
          const msg = `Deposit + Mortgage must equal Property Value (off by £${Math.abs(
            Math.round(diff)
          ).toLocaleString()}).`;
          if (!out.depositAmount) out.depositAmount = msg;
          if (!out.mortgageAmount) out.mortgageAmount = msg;
        }
      }
    }

    if (state.estRent === "" || rent <= 0) out.estRent = "Estimated rent must be greater than 0.";
    return out;
  }, []);

  const recalcOtherField = (key, rawValue, prev) => {
    const v = clampNonNeg(rawValue);
    if (key === "estRent" || key === "propertyValue") return { ...prev, [key]: v };

    const pv = safeNum(prev.propertyValue);
    if (!(pv > 0)) return { ...prev, [key]: v };

    if (key === "depositAmount") {
      const dep = safeNum(v);
      const nextMortgage = Math.max(pv - dep, 0);
      return { ...prev, depositAmount: v, mortgageAmount: String(nextMortgage) };
    }
    if (key === "mortgageAmount") {
      const mort = safeNum(v);
      const nextDeposit = Math.max(pv - mort, 0);
      return { ...prev, mortgageAmount: v, depositAmount: String(nextDeposit) };
    }
    return { ...prev, [key]: v };
  };

  // Typing a mortgage or deposit overrides the LTV dropdown, so clear it.
  const clearLtvOnQuickEdit = (key) => {
    if ((key === "mortgageAmount" || key === "depositAmount") && filtersDraft.ltv) {
      setFilterDraft("ltv", "");
    }
  };

  const onQuickChange = (key, e) => {
    const raw = e.target.value;
    if (!onlyNum(raw)) return;
    clearLtvOnQuickEdit(key);
    setQs((prev) => {
      const next = recalcOtherField(key, raw, prev);
      setErrors(validateAll(next));
      return next;
    });
  };

  const onQuickPaste = (key, e) => {
    try {
      const text = (e.clipboardData || window.clipboardData).getData("text") || "";
      let s = text.replace(/[^\d.]/g, "");
      const parts = s.split(".");
      if (parts.length > 2) s = parts[0] + "." + parts.slice(1).join("");
      e.preventDefault();
      clearLtvOnQuickEdit(key);
      setQs((prev) => {
        const next = recalcOtherField(key, s, prev);
        setErrors(validateAll(next));
        return next;
      });
    } catch (err) {
      reportError(err, { where: "onQuickPaste", field: key });
    }
  };

  const onQuickKeyDown = (e) => {
    const allowed = new Set([
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "Tab",
      "Enter",
      ".",
    ]);
    if (e.ctrlKey || e.metaKey) return;
    if (allowed.has(e.key)) return;
    if (e.key < "0" || e.key > "9") e.preventDefault();
  };

  const onLTVFilterChange = (ltvValue) => {
    try {
      setFilterDraft("ltv", ltvValue);

      if (!ltvValue || ltvValue === "" || ltvValue === "Select") {
        return;
      }

      const pv = safeNum(qs.propertyValue);
      if (!(pv > 0)) {
        return;
      }

      const ltvPercent = selectedLtvPercent(ltvValue);
      if (!ltvPercent) return;

      const { mortgageAmount: newMortgage, depositAmount: newDeposit } = mortgageFromLtv(pv, ltvPercent);

      // Prevent clearing products during LTV change
      // setKeepProductsDuringLoad(true); // Removed to show full loader
      setIsLoading(true);

      // eslint-disable-next-line no-console
      console.log("[BorrowerSourcing] LTV filter changed:", {
        ltvValue,
        ltvPercent,
        propertyValue: pv,
        newMortgage,
        newDeposit,
      });

      setQs((prev) => {
        const next = {
          ...prev,
          mortgageAmount: String(newMortgage),
          depositAmount: String(newDeposit),
        };
        setErrors(validateAll(next));
        return next;
      });
    } catch (err) {
      reportError(err, { where: "onLTVFilterChange" });
    }
  };

  const DEFAULT_SOURCING_FILTERS = {
    repaymentType: "Interest only",
    ltv: "75%",
    epcGreen: "A-C",
    mortgageTerm: "25 yrs",
    productTerm: "5",
    productType: "Fixed",
    borrowerType: "Ltd Co.",
    loanPurpose: "Purchase",
    taxpayerType: "Basic Rate (125%)",
  };

  const [filtersDraft, setFiltersDraft] = React.useState(() => {
    const base = {
      repaymentType: "Interest only",
      ltv: "75%",
      epcGreen: "A-C",
      mortgageTerm: "25 yrs",
      productTerm: "5",
      productType: "Fixed",
      borrowerType: "Ltd Co.",
      loanPurpose: "Purchase",
      homeowner: "",
      residency: "",
      btlExp: "",
      credit: "",
      tenancyType: "",
      tenure: "",
      leaseTerm: "",
      exAuthority: "",
      depositOrigination: "",
      day1Remortgage: "",
      purchaseType: "",
      taxpayerType: "Basic Rate (125%)",
      ...propertyFilterDefaults(isComplexBtl),
    };
    try {
      const saved = localStorage.getItem(filtersStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        const merged = { ...base, ...parsed };

        for (const key of Object.keys(DEFAULT_SOURCING_FILTERS)) {
          const v = merged[key];
          if (v === undefined || v === null || v === "" || v === "Select") {
            merged[key] = DEFAULT_SOURCING_FILTERS[key];
          }
        }
        const propertyDefaults = propertyFilterDefaults(isComplexBtl);
        for (const key of Object.keys(propertyDefaults)) {
          if (merged[key] === undefined || merged[key] === null || merged[key] === "") {
            merged[key] = propertyDefaults[key];
          }
        }
        return merged;
      }
    } catch (e) { /* ignore */ }
    return base;
  });

  React.useEffect(() => {
    localStorage.setItem(filtersStorageKey, JSON.stringify(filtersDraft));
  }, [filtersDraft, filtersStorageKey]);

  const setFilterDraft = (k, v) => setFiltersDraft((s) => ({ ...s, [k]: v }));
  const [filtersApplied, setFiltersApplied] = React.useState(filtersDraft);

  const [sortBy, setSortBy] = React.useState("rate");
  const [calculatedProducts, setCalculatedProducts] = React.useState([]);
  // eslint-disable-next-line no-unused-vars
  const [scheduledProducts, setScheduledProducts] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [calcSeconds, setCalcSeconds] = React.useState(0);
  const [minimumRentRequired, setMinimumRentRequired] = React.useState(null);
  const [dscOnlyFailures, setDscOnlyFailures] = React.useState({ count: 0, lowest: null });
  const [propertySizeHiddenCount, setPropertySizeHiddenCount] = React.useState(0);
  const [keepProductsDuringLoad, setKeepProductsDuringLoad] = React.useState(false);

  // Track the latest request ID to avoid race conditions
  const requestRef = React.useRef(0);

  const hasNoErrors = React.useMemo(
    () => !errors.propertyValue && !errors.depositAmount && !errors.mortgageAmount && !errors.estRent,
    [errors]
  );

  const inputsReady = React.useMemo(() => {
    const filled =
      qs.propertyValue !== "" &&
      qs.depositAmount !== "" &&
      qs.mortgageAmount !== "" &&
      qs.estRent !== "";
    return filled && hasNoErrors;
  }, [qs, hasNoErrors]);

  const allInputsEmpty =
    qs.propertyValue === "" && qs.depositAmount === "" && qs.mortgageAmount === "" && qs.estRent === "";

  React.useEffect(() => {
    setErrors(validateAll(qs));
  }, [qs, validateAll]);

  const mapFiltersToAPI = (filters) => {
    const mapped = {};

    if (filters.epcGreen && filters.epcGreen !== "" && filters.epcGreen !== "Select") {
      // EPC rule:
      // - A-C => do not apply any filter (show all products)
      // - D-F => only show EPC rated products where backend has "No"
      if (filters.epcGreen === "D-F") mapped.epc_rating_green = "No";
    }

    if (
      filters.productTerm &&
      filters.productTerm !== "" &&
      filters.productTerm !== "Lifetime" &&
      filters.productTerm !== "Select"
    ) {
      mapped.product_termsyears = filters.productTerm;
    }

    if (filters.productType && filters.productType !== "" && filters.productType !== "Select") {
      mapped.product_type = filters.productType;
    }

    if (filters.borrowerType && filters.borrowerType !== "" && filters.borrowerType !== "Select") {
      mapped.borrower_type = filters.borrowerType;
    }

    if (filters.homeowner && filters.homeowner !== "" && filters.homeowner !== "Select") {
      // Business rule: selecting "Yes" should not restrict products by home_owner.
      // Only apply backend filter when user explicitly selects "No".
      if (filters.homeowner === "No") {
        mapped.home_owner = "No";
      }
    }

    // Residency (UK / International / Xpat) is filtered on the frontend only.

    if (filters.btlExp && filters.btlExp !== "" && filters.btlExp !== "Select") {
      // Business rule:
      // - 0   => only 0
      // - 1-5 => 0 and 1-5 (handled in frontend filter logic)
      // - 5+  => all products (no backend restriction)
      if (filters.btlExp === "No" || filters.btlExp === "0") {
        mapped.btl_experince_year = "0";
      }
    }

    if (filters.credit && filters.credit !== "" && filters.credit !== "Select") {
      const creditMap = {
        Excellent: "Excellent 881+",
        Fair: "Fair 721-880",
        Poor: "Poor <550",
      };
      mapped.credit_worthiness = creditMap[filters.credit] || filters.credit;
    }

    if (filters.tenancyType && filters.tenancyType !== "" && filters.tenancyType !== "Select") {
      mapped.tenancy_type = filters.tenancyType;
    }

    if (filters.tenure && filters.tenure !== "" && filters.tenure !== "Select") {
      mapped.fh_or_lh = filters.tenure;
    }

    if (filters.leaseTerm && filters.leaseTerm !== "") {
      const term = parseInt(filters.leaseTerm, 10);
      if (!Number.isNaN(term) && term > 0) mapped.leasehold_remaining_termyears = term;
    }

    if (filters.exAuthority && filters.exAuthority !== "" && filters.exAuthority !== "Select") {
      mapped.ex_local_authority = filters.exAuthority;
    }

    if (filters.depositOrigination && filters.depositOrigination !== "" && filters.depositOrigination !== "Select") {
      mapped.deposit_originantion = filters.depositOrigination;
    }

    if (filters.day1Remortgage && filters.day1Remortgage !== "" && filters.day1Remortgage !== "Select") {
      mapped.day_1_remortgage = filters.day1Remortgage;
    }

    // Loan purpose filter - Purchase, Remortgage, Product Transfer
    if (filters.loanPurpose && filters.loanPurpose !== "" && filters.loanPurpose !== "Select") {
      mapped.loan_purpose = filters.loanPurpose;
    }

    return mapped;
  };

  const fetchProducts = React.useCallback(
    async (useFilters) => {
      if (!inputsReady) return;

      const requestId = ++requestRef.current;
      setIsLoading(true);
      const start = performance.now();

      try {
        const pv = parseMoney(qs.propertyValue);
        const mort = parseMoney(qs.mortgageAmount);
        const rent = parseMoney(qs.estRent);
        const deposit =
          parseMoney(qs.depositAmount) > 0
            ? parseMoney(qs.depositAmount)
            : Math.max(0, pv - mort);
        lastSearchInputsRef.current = {
          property_value: pv,
          mortgage_amount: mort,
          deposit_amount: deposit,
          est_rent: rent,
        };
        const parsedTermYears = parseInt(
          String(useFilters.mortgageTerm).replace(/[^\d]/g, ""),
          10
        );
        const termYears =
          Number.isFinite(parsedTermYears) && parsedTermYears > 0 ? parsedTermYears : undefined;

        const apiFilters = mapFiltersToAPI(useFilters);

        const fallbackCtx = {
          propertyValue: pv,
          mortgageAmount: mort,
          estimatedRent: rent,
          mortgageTermYears: termYears,
          ...calculatorCaseInputs(useFilters),
        };

        // Dropdown LTV when picked, otherwise mortgage ÷ property value (2 dp). Never recalculated from a picked LTV.
        const calculatedLTV = ltvForChecks({
          selectedLtv: selectedLtvPercent(useFilters.ltv),
          propertyValue: pv,
          mortgageAmount: mort,
        });
        // eslint-disable-next-line no-console
        console.log("[BorrowerSourcing] Calculated LTV:", {
          propertyValue: pv,
          mortgageAmount: mort,
          calculatedLTV: calculatedLTV.toFixed(2),
        });

        // Request body - mortgage_term_years removed to fetch all products
        // Frontend will use product's own mortgage_term_years or context value for calculations
        const requestBody = {
          property_value: pv,
          mortgage_amount: mort,
          estimated_rent: rent,
          // mortgage_term_years removed - fetch all products regardless of term
          repaymentType: useFilters.repaymentType || filtersApplied.repaymentType || "Interest only", // Include for analytics tracking
          mortgage_term_years: termYears, // Include for analytics tracking
          filters: apiFilters,
          limit: SOURCING_PRODUCTS_FETCH_LIMIT,
          limit_page_length: SOURCING_PRODUCTS_FETCH_LIMIT,
        };

        // eslint-disable-next-line no-console
        console.log("🚀 Request Body:", JSON.stringify(requestBody, null, 2));

        const res = await calculateMortgageProducts(requestBody);

        // If a new request has started since this one, ignore the result
        if (requestId !== requestRef.current) return;

        const sanitizeSourcingProduct = (product) => {
          if (!product || typeof product !== "object") return product;
          const {
            _seen,
            applicant,
            lender_product_code,
            modified_by,
            owner,
            ...rest
          } = product;
          return rest;
        };

        const apiProducts = Array.isArray(res?.products)
          ? res.products.map(sanitizeSourcingProduct)
          : [];
        const scheduledFromApi = Array.isArray(res?.scheduled_products)
          ? res.scheduled_products.map(sanitizeSourcingProduct)
          : [];

        // Also check regular products array for products with product_schedule === 1
        const scheduledInRegular = apiProducts.filter(
          (p) => p?.product_schedule === 1 || p?.product_schedule === "1"
        );
        const liveProducts = apiProducts.filter(
          (p) => !(p?.product_schedule === 1 || p?.product_schedule === "1")
        );

        // Debug: Log scheduled products found
        console.log("[BorrowerSourcing] Scheduled products from API:", {
          scheduledFromApi: scheduledFromApi.length,
          scheduledInRegular: scheduledInRegular.length,
          totalScheduled: scheduledFromApi.length + scheduledInRegular.length,
          sampleScheduled: scheduledInRegular.length > 0 ? {
            name: scheduledInRegular[0]?.name,
            product_schedule: scheduledInRegular[0]?.product_schedule,
            date_time: scheduledInRegular[0]?.date_time,
            allFields: Object.keys(scheduledInRegular[0] || {})
          } : null
        });

        // Debug: Log scheduled products found
        console.log("[BorrowerSourcing] Scheduled products detection:", {
          scheduledFromApi: scheduledFromApi.length,
          scheduledInRegular: scheduledInRegular.length,
          totalScheduled: scheduledFromApi.length + scheduledInRegular.length,
          sampleScheduled: scheduledInRegular.length > 0 ? {
            name: scheduledInRegular[0]?.name,
            product_schedule: scheduledInRegular[0]?.product_schedule,
            date_time: scheduledInRegular[0]?.date_time,
            allDateFields: {
              date_time: scheduledInRegular[0]?.date_time,
              product_schedule_at: scheduledInRegular[0]?.product_schedule_at,
              schedule_at: scheduledInRegular[0]?.schedule_at,
            }
          } : null
        });

        // Helper function to check if scheduled date has passed
        const hasScheduleDatePassed = (product) => {
          const scheduleDate = product?.date_time ||
            product?.product_schedule_at ||
            product?.schedule_at ||
            product?.scheduled_at ||
            product?.product_schedule_date ||
            product?.schedule_date ||
            product?.scheduled_date;

          if (!scheduleDate) return false; // No date means not scheduled or can't determine

          try {
            let dateStr = String(scheduleDate).trim();

            // Convert DD-MM-YYYY HH:MM:SS format to ISO if needed
            if (dateStr.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}:\d{2}$/)) {
              const parts = dateStr.split(' ');
              const datePart = parts[0].split('-');
              const timePart = parts[1] || '00:00:00';
              dateStr = `${datePart[2]}-${datePart[1]}-${datePart[0]}T${timePart}`;
            } else if (dateStr.includes(' ') && !dateStr.includes('T')) {
              dateStr = dateStr.replace(' ', 'T');
            }

            const scheduleDateTime = new Date(dateStr);
            const now = new Date();

            if (isNaN(scheduleDateTime.getTime())) return false;

            // If schedule date has passed, product should go live
            return scheduleDateTime <= now;
          } catch (e) {
            console.warn("Failed to check if schedule date passed:", scheduleDate, e);
            return false;
          }
        };

        // Separate scheduled products into: past-scheduled (go live) and future-scheduled (stay scheduled)
        const scheduledTagged = [...scheduledFromApi, ...scheduledInRegular];
        const pastScheduled = scheduledTagged.filter(p => hasScheduleDatePassed(p));
        const futureScheduled = scheduledTagged.filter(p => !hasScheduleDatePassed(p));

        console.log("[BorrowerSourcing] Scheduled products separation:", {
          total: scheduledTagged.length,
          pastScheduled: pastScheduled.length, // These will go live
          futureScheduled: futureScheduled.length, // These stay scheduled
        });

        // Past-scheduled products: treat as live (no _isScheduled flag)
        const pastScheduledAsLive = pastScheduled.map((p) => ({
          ...p,
          _isScheduled: false, // Remove scheduled flag - they're now live
          // Keep original status or set to Active/Live
          status: p?.product_status || p?.status || "Active",
        }));

        // Future-scheduled products: keep as scheduled
        const futureScheduledTagged = futureScheduled.map((p) => ({
          ...p,
          _isScheduled: true,
          status: p?.product_schedule === 1 || p?.product_schedule === "1" ? "Scheduled" : (p?.status || "Scheduled"),
        }));

        const comparators = {
          fee: (a, b) => {
            const calcA = getEffectiveCalc(a, useFilters.repaymentType, fallbackCtx);
            const calcB = getEffectiveCalc(b, useFilters.repaymentType, fallbackCtx);
            return safeNum(calcA?.arrangement_fee) - safeNum(calcB?.arrangement_fee);
          },
          repayment: (a, b) => {
            const calcA = getEffectiveCalc(a, useFilters.repaymentType, fallbackCtx);
            const calcB = getEffectiveCalc(b, useFilters.repaymentType, fallbackCtx);
            return safeNum(calcA?.monthly_payment) - safeNum(calcB?.monthly_payment);
          },
          cost: (a, b) => {
            const calcA = getEffectiveCalc(a, useFilters.repaymentType, fallbackCtx);
            const calcB = getEffectiveCalc(b, useFilters.repaymentType, fallbackCtx);
            return safeNum(calcA?.total_cost) - safeNum(calcB?.total_cost);
          },
          rate: (a, b) => {
            const calcA = getEffectiveCalc(a, useFilters.repaymentType, fallbackCtx);
            const calcB = getEffectiveCalc(b, useFilters.repaymentType, fallbackCtx);
            return safeNum(calcA?.interest_rate) - safeNum(calcB?.interest_rate);
          },
        };

        // Filter out deactivated products
        const filterDeactivated = (p) => {
          const status = String(p?.product_status || p?.status || "").toLowerCase();
          return status !== "deactivated";
        };

        // Get DSC requirement for filtering
        const dscRequired = getDSCRequirement(useFilters);

        const normalizeFilterToken = (v) =>
          String(v ?? "")
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim();

        const splitCsvValues = (v) =>
          String(v ?? "")
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean);

        const getMultiValues = (product, listKey, ...fallbackKeys) => {
          const listVal = product?.[listKey];
          if (Array.isArray(listVal)) {
            return listVal.map((x) => String(x).trim()).filter(Boolean);
          }

          for (const k of fallbackKeys) {
            const raw = product?.[k];
            if (Array.isArray(raw)) return raw.map((x) => String(x).trim()).filter(Boolean);
            if (typeof raw === "string" && raw.trim()) return splitCsvValues(raw);
          }

          return [];
        };

        const matchesMulti = (product, selectedRaw, listKey, ...fallbackKeys) => {
          if (!selectedRaw || selectedRaw === "Select") return true;
          const selected = normalizeFilterToken(selectedRaw);
          const values = getMultiValues(product, listKey, ...fallbackKeys)
            .map(normalizeFilterToken);
          return values.includes(selected);
        };

        const matchesSourcingFilters = (product) => {
          const getNumericCandidates = (...vals) =>
            vals
              .flatMap((v) => {
                if (Array.isArray(v)) return v;
                if (typeof v === "string") return splitCsvValues(v);
                return v == null ? [] : [v];
              })
              .map((v) => {
                const n = Number(String(v).replace(/[^\d.]/g, ""));
                return Number.isFinite(n) ? n : NaN;
              })
              .filter(Number.isFinite);

          if (useFilters.homeowner && useFilters.homeowner !== "Select") {
            const selectedHomeowner = normalizeFilterToken(useFilters.homeowner);
            const productHomeowner = normalizeFilterToken(product?.home_owner ?? product?.homeOwner);

            // Business rule:
            // - "Yes" => show all products (no restriction)
            // - "No"  => only products explicitly marked as No
            if (selectedHomeowner === "no" && productHomeowner !== "no") {
              return false;
            }
          }

          if (useFilters.residency && useFilters.residency !== "Select") {
            if (!matchesResidencyFilter(useFilters.residency, product)) return false;
          }

          if (useFilters.epcGreen && useFilters.epcGreen !== "Select") {
            // EPC rule:
            // - A-C => show all products (no restriction)
            // - D-F => only products with backend epc_rating_green == "No"
            if (useFilters.epcGreen === "D-F") {
              const epcValue = normalizeFilterToken(product?.epc_rating_green);
              if (epcValue !== "no") return false;
            }
          }

          if (!matchesMulti(product, useFilters.productType, "product_type_list", "product_type", "productType")) {
            return false;
          }

          if (useFilters.productTerm && useFilters.productTerm !== "Select") {
            const selectedTerm = normalizeFilterToken(useFilters.productTerm);
            if (selectedTerm === "lifetime") {
              const productTermRaw = firstNonEmpty(
                product?.product_termsyears,
                product?.product_term_years,
                product?.product_term
              );
              if (normalizeFilterToken(productTermRaw) !== "lifetime") return false;
            } else {
            const productTermCandidates = [
              product?.product_termsyears,
              product?.product_term_years,
              product?.product_term,
            ]
              .map((v) => String(v ?? "").trim())
              .filter(Boolean)
              .map(normalizeFilterToken);
              if (!productTermCandidates.includes(selectedTerm)) return false;
            }
          }

          if (!matchesMulti(product, useFilters.loanPurpose, "loan_purpose_list", "loan_purpose", "loanPurpose", "loan_purpose_type")) {
            return false;
          }

          if (!matchesMulti(product, useFilters.borrowerType, "borrower_type_list", "borrower_type", "borrowerType")) {
            return false;
          }

          const btlExpSelected = useFilters.btlExp === "No" ? "0" : useFilters.btlExp;
          if (btlExpSelected && btlExpSelected !== "Select") {
            const btlExpValues = getMultiValues(
              product,
              "btl_experience_list",
              "btl_experience",
              "btl_experince_year"
            ).map(normalizeFilterToken);

            const hasZeroBtl = btlExpValues.includes(normalizeFilterToken("0"));
            const hasOneToFiveBtl = btlExpValues.includes(normalizeFilterToken("1-5"));

            // Business rule:
            // - 5+  => show all products
            // - 1-5 => show products with 0 or 1-5
            // - 0   => show only products with 0
            if (btlExpSelected === "0") {
              if (!hasZeroBtl) return false;
            } else if (btlExpSelected === "1-5") {
              if (!(hasZeroBtl || hasOneToFiveBtl)) return false;
            }
          }

          const creditMap = {
            Excellent: "Excellent 881+",
            Fair: "Fair 721-880",
            Poor: "Poor <550",
          };
          const creditSelected = creditMap[useFilters.credit] || useFilters.credit;
          if (!matchesMulti(product, creditSelected, "credit_worthiness_list", "credit_worthiness")) {
            return false;
          }

          if (!matchesMulti(product, useFilters.tenancyType, "tenancy_type_list", "tenancy_type", "tenancyType")) {
            return false;
          }

          if (!matchesMulti(product, useFilters.tenure, "fh_or_lh_list", "fh_or_lh")) {
            return false;
          }

          // Leasehold remaining term filter:
          // if borrower selects LEASEHOLD and enters years, only keep products
          // whose leasehold term requirement is satisfied by that input.
          if (normalizeFilterToken(useFilters.tenure) === "leasehold") {
            const selectedLeaseTerm = parseInt(String(useFilters.leaseTerm || "").trim(), 10);
            if (Number.isFinite(selectedLeaseTerm) && selectedLeaseTerm > 0) {
              const selectedMortgageTerm = parseInt(
                String(useFilters.mortgageTerm || "").replace(/[^\d]/g, ""),
                10
              );
              const mortgageTermYears = Number.isFinite(selectedMortgageTerm) && selectedMortgageTerm > 0
                ? selectedMortgageTerm
                : 0;
              const effectiveRemainingLease = selectedLeaseTerm - mortgageTermYears;

              if (effectiveRemainingLease <= 0) return false;

              const productLeaseTermCandidates = getNumericCandidates(
                product?.leasehold_remaining_termyears,
                product?.leasehold_remaining_term_years,
                product?.leasehold_remaining_term,
                product?.lease_term_years,
                product?.remaining_lease_term_years,
                product?.lease_term,
                product?.leasehold_remaining_termyears_list,
                product?._raw?.leasehold_remaining_termyears,
                product?._raw?.leasehold_remaining_term_years,
                product?._raw?.leasehold_remaining_term,
                product?._raw?.lease_term_years,
                product?._raw?.remaining_lease_term_years,
                product?._raw?.lease_term,
                product?._raw?.leasehold_remaining_termyears_list
              );

              if (productLeaseTermCandidates.length > 0) {
                // Apply borrower logic:
                // entered lease term - mortgage term = effective remaining lease.
                // Show products whose leasehold term is <= this effective value.
                const matchesEffectiveLease = productLeaseTermCandidates.some(
                  (termYears) => termYears <= effectiveRemainingLease
                );
                if (!matchesEffectiveLease) return false;
              }
            }
          }

          if (!matchesMulti(product, useFilters.depositOrigination, "deposit_originantion_list", "deposit_originantion")) {
            return false;
          }

          if (!matchesMulti(product, useFilters.day1Remortgage, "day_1_remortgage_list", "day_1_remortgage")) {
            return false;
          }

          return true;
        };

        // Filter live products (include past-scheduled products that have gone live)
        // Combine original live products with past-scheduled products
        const allLiveProducts = [...liveProducts, ...pastScheduledAsLive];

        const productMaxLtv = (p) =>
          normalizePercentValue(
            firstFinite(
              p?.ity_percent,
              p?.max_ltv,
              p?.max_ltv_percent,
              p?.ltv_cap,
              p?.loan_to_value,
              p?.ltv,
              p?.ltv_percent
            )
          );

        // Returns the first failed check ("ltv" | "loan" | "property" | "dsc") or null when eligible.
        const selectedPropertyType = useFilters.complexPropertyType;
        const failedProductCheck = (p) => {
          if (selectedPropertyType && !productMatchesPropertyType(p, selectedPropertyType)) return "property";
          if (!passesLtvCheck(productMaxLtv(p), calculatedLTV)) return "ltv";
          if (!passesLoanSizeCheck(p, mort)) return "loan";
          if (!productPassesSizeCheck(p, useFilters)) return "property";
          const calc = getEffectiveCalc(p, useFilters.repaymentType, fallbackCtx);
          if (!calc.dsc_passes) {
            if (calc.dsc_missing_fields?.length) {
              // eslint-disable-next-line no-console
              console.warn(`[BorrowerSourcing] ${p?.name} missing DSC fields:`, calc.dsc_missing_fields);
            }
            return "dsc";
          }
          return null;
        };

        let propertySizeHidden = 0;
        const dscOnlyFailed = [];

        // Filter live products (exclude deactivated and those failing property type, LTV, loan size, rooms/units or DSC)
        let liveFiltered = allLiveProducts.filter((p) => {
          // Exclude deactivated products
          if (!filterDeactivated(p)) return false;

          // Apply frontend-safe matching for multi-value backend fields.
          if (!matchesSourcingFilters(p)) return false;

          const failed = failedProductCheck(p);
          if (failed === "property") propertySizeHidden += 1;
          if (failed === "dsc") dscOnlyFailed.push(p);
          return !failed;
        });

        // Filter scheduled products (exclude deactivated and only show future scheduled products)
        let scheduledFiltered = futureScheduledTagged.filter((p) => {
          // Exclude deactivated products
          if (!filterDeactivated(p)) return false;

          // Apply frontend-safe matching for multi-value backend fields.
          if (!matchesSourcingFilters(p)) return false;

          // Only show scheduled products that are scheduled for a future date/time
          // Check multiple possible field names for schedule date
          // Backend uses 'date_time' field (as seen in lender portal: "15-01-2026 16:59:00")
          const scheduleDate = p?.date_time ||
            p?.product_schedule_at ||
            p?.schedule_at ||
            p?.scheduled_at ||
            p?.product_schedule_date ||
            p?.schedule_date ||
            p?.scheduled_date;

          if (scheduleDate) {
            try {
              // Handle different date formats from backend
              // Backend may return: "15-01-2026 16:59:00" (DD-MM-YYYY HH:MM:SS) or ISO format
              let dateStr = String(scheduleDate).trim();

              // Convert DD-MM-YYYY HH:MM:SS format to ISO if needed
              // Backend format: "15-01-2026 16:59:00"
              if (dateStr.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}:\d{2}$/)) {
                // Convert "DD-MM-YYYY HH:MM:SS" to "YYYY-MM-DDTHH:MM:SS"
                const parts = dateStr.split(' ');
                const datePart = parts[0].split('-');
                const timePart = parts[1] || '00:00:00';
                dateStr = `${datePart[2]}-${datePart[1]}-${datePart[0]}T${timePart}`;
              } else if (dateStr.includes(' ') && !dateStr.includes('T')) {
                // Replace space with T for ISO format (handles "YYYY-MM-DD HH:MM:SS")
                dateStr = dateStr.replace(' ', 'T');
              }

              const scheduleDateTime = new Date(dateStr);
              const now = new Date();

              // Validate that the date is valid
              if (isNaN(scheduleDateTime.getTime())) {
                // Invalid date, log for debugging and exclude this product
                console.warn(`[BorrowerSourcing] Invalid schedule date for product ${p?.name}:`, scheduleDate, "-> parsed as:", dateStr);
                return false;
              }

              // If schedule date is in the past or equal to now, exclude this product
              if (scheduleDateTime <= now) {
                console.log(`[BorrowerSourcing] Scheduled product ${p?.name} has past date:`, scheduleDate, "->", scheduleDateTime, "<= now:", now);
                return false;
              }

              // Log successful parsing for debugging
              console.log(`[BorrowerSourcing] Scheduled product ${p?.name} will show - date:`, scheduleDate, "-> parsed:", scheduleDateTime, "is future:", scheduleDateTime > now);
            } catch (e) {
              // If date parsing fails, exclude the product to be safe
              console.warn("Failed to parse schedule date:", scheduleDate, e);
              return false;
            }
          } else {
            // If no schedule date is provided, log and exclude the product
            // (we only want to show scheduled products with a valid future date)
            console.warn(`[BorrowerSourcing] Scheduled product ${p?.name} has no schedule date field. Available fields:`, Object.keys(p || {}));
            return false;
          }

          return !failedProductCheck(p);
        });

        // Debug: Log filtering results
        console.log("[BorrowerSourcing] Product filtering results:", {
          liveFiltered: liveFiltered.length,
          scheduledFiltered: scheduledFiltered.length,
          scheduledTagged: scheduledTagged.length,
          scheduledFilteredSample: scheduledFiltered.length > 0 ? {
            name: scheduledFiltered[0]?.name,
            date_time: scheduledFiltered[0]?.date_time,
            product_schedule: scheduledFiltered[0]?.product_schedule
          } : null
        });

        // Minimum rent calculation: runs when DSC filter is active but returns no products.
        // Uses all-filters-except-DSC products to find cheapest icr_rate product.
        let minimumRentRequired = null;
        const weeksBasedCase = isWeeksBasedPropertyType(useFilters.complexPropertyType);
        if (dscRequired > 0 && liveFiltered.length === 0 && !weeksBasedCase) {
          // Re-filter without DSC check to get the candidate pool
          const productsWithoutDSC = allLiveProducts.filter((p) => {
            if (!filterDeactivated(p)) return false;
            if (!matchesSourcingFilters(p)) return false;
            if (selectedPropertyType && !productMatchesPropertyType(p, selectedPropertyType)) return false;
            const min = safeNum(p?.min_loan);
            const max = safeNum(p?.max_loan);
            if (min > 0 && mort < min) return false;
            if (max > 0 && mort > max) return false;
            const productMaxLTV = normalizePercentValue(
              firstFinite(
                p?.ity_percent,
                p?.max_ltv,
                p?.max_ltv_percent,
                p?.ltv_cap,
                p?.loan_to_value,
                p?.ltv,
                p?.ltv_percent
              )
            );
            if (Number.isFinite(productMaxLTV) && productMaxLTV > 0) {
              if (productMaxLTV < calculatedLTV) return false;
            }
            return true;
          });

          // Find product with cheapest (lowest) icr_rate
          const cheapestIcrProduct = productsWithoutDSC.reduce((best, p) => {
            const rate = firstFinite(p?.icr_rate);
            if (rate == null || rate <= 0) return best;
            if (!best) return p;
            const bestRate = firstFinite(best?.icr_rate);
            return rate < bestRate ? p : best;
          }, null);

          if (cheapestIcrProduct) {
            const calc = getEffectiveCalc(cheapestIcrProduct, useFilters.repaymentType, fallbackCtx);
            const totalLoanAmount = safeNum(calc?.total_loan_amount) > 0
              ? safeNum(calc?.total_loan_amount)
              : mort + safeNum(calc?.arrangement_fee);
            const icrRate = safeNum(cheapestIcrProduct?.icr_rate);
            const dscMultiplier = dscRequired / 100; // e.g. 125 → 1.25, 140 → 1.40
            const isRepayment = String(useFilters?.repaymentType || "").toLowerCase() === "repayment";
            const mortgageTermYears = Math.max(1, safeNum(calc?.mortgage_term_years) || 25);
            const stressMonthlyRate = icrRate > 0 ? icrRate / 100 / 12 : 0;
            const totalMonths = Math.max(1, Math.round(mortgageTermYears * 12));

            // Minimum rent should mirror active repayment type:
            // - Interest only: stressed interest payment
            // - Repayment: stressed amortising payment over mortgage term
            let stressedMonthlyPayment = 0;
            if (isRepayment) {
              if (stressMonthlyRate === 0) {
                stressedMonthlyPayment = totalLoanAmount / totalMonths;
              } else {
                const powerTerm = Math.pow(1 + stressMonthlyRate, totalMonths);
                stressedMonthlyPayment = (totalLoanAmount * stressMonthlyRate * powerTerm) / (powerTerm - 1);
              }
            } else {
              stressedMonthlyPayment = totalLoanAmount * stressMonthlyRate;
            }

            minimumRentRequired = stressedMonthlyPayment * dscMultiplier;
          }
        }

        setMinimumRentRequired(minimumRentRequired);

        // Lowest minimum rent across Holiday let / Short term let products that failed on DSC alone.
        let lowestDscMinimumRent = null;
        if (weeksBasedCase) {
          for (const p of dscOnlyFailed) {
            const calc = getEffectiveCalc(p, useFilters.repaymentType, fallbackCtx);
            const modes = [
              ["Interest only", calc.dsc_interest_only],
              ["Repayment", calc.dsc_repayment],
            ];
            for (const [mode, result] of modes) {
              const minRent = result?.minimumRent;
              if (!minRent) continue;
              if (!lowestDscMinimumRent || minRent.minimumMonthlyRent < lowestDscMinimumRent.minimumMonthlyRent) {
                lowestDscMinimumRent = {
                  ...minRent,
                  mode,
                  productName: p?.product_name || p?.name,
                  lenderName: p?.lender_name || "",
                };
              }
            }
          }
        }
        setDscOnlyFailures({ count: dscOnlyFailed.length, lowest: lowestDscMinimumRent });
        setPropertySizeHiddenCount(propertySizeHidden);

        // Merge live and scheduled products together
        const allProducts = [...liveFiltered, ...scheduledFiltered];

        // Sort all products together
        const cmp = comparators[sortBy] || comparators.rate;
        const priority = new Map(
          allProducts.map((p) => [p, productMeetsPropertyCriteria(p, useFilters) ? 1 : 0])
        );
        allProducts.sort((a, b) => priority.get(b) - priority.get(a) || cmp(a, b));

        console.log("[BorrowerSourcing] Final products to display:", {
          total: allProducts.length,
          scheduled: allProducts.filter(p => p._isScheduled).length,
          live: allProducts.filter(p => !p._isScheduled).length
        });

        setCalculatedProducts(allProducts);
        // Keep scheduledProducts state for backward compatibility, but it's now included in calculatedProducts
        setScheduledProducts(scheduledFiltered);

        const end = performance.now();
        setCalcSeconds(((end - start) / 1000).toFixed(3));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("❌ Error:", err);
        // eslint-disable-next-line no-console
        console.error("Error details:", err.response?.data);
        reportError(err, { where: "fetchProducts" });
        if (requestId === requestRef.current) {
          setCalculatedProducts([]);
          setMinimumRentRequired(null);
          setDscOnlyFailures({ count: 0, lowest: null });
          setPropertySizeHiddenCount(0);
        }
      } finally {
        if (requestId === requestRef.current) {
          setIsLoading(false);
          setKeepProductsDuringLoad(false);
        }
      }
    },
    [qs, inputsReady, sortBy, reportError]
  );

  // Debounce filtersApplied updates
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setFiltersApplied(filtersDraft);
    }, 500);
    return () => clearTimeout(handler);
  }, [filtersDraft]);

  React.useEffect(() => {
    if (!inputsReady) {
      // Don't clear during LTV change so we never show sleepy bear flicker
      if (!keepProductsDuringLoad) {
        setCalculatedProducts([]);
      }
      return;
    }
    fetchProducts(filtersApplied);
  }, [
    qs.propertyValue,
    qs.mortgageAmount,
    qs.depositAmount,
    qs.estRent,
    filtersApplied,
    fetchProducts,
    inputsReady,
    keepProductsDuringLoad,
  ]);

  // eslint-disable-next-line no-unused-vars
  const onApplyFilters = () => {
    try {
      if (!inputsReady) throw new Error("Please fix validation errors above before applying filters.");
      setFiltersApplied(filtersDraft);
    } catch (err) {
      reportError(err, { where: "ApplyFilters.click" });
    }
  };

  const [shortlist, setShortlist] = React.useState(() => new Set());
  const [shortlistedDetailsMap, setShortlistedDetailsMap] = React.useState(() => new Map()); // id -> product data so shortlist persists across filter changes
  const [shortlistedDisplaySnapshots, setShortlistedDisplaySnapshots] = React.useState(() => new Map()); // id -> { ltv, rate, monthlyPayment, totalCost, icr, mortgageTermYears, repaymentType } at add time — keeps display stable when filters change
  const [cartOpen, setCartOpen] = React.useState(false);
  const [showCartMascot, setShowCartMascot] = React.useState(false);
  const cartMascotSuppressRef = React.useRef(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = React.useState(false);
  const [mobileSortOpen, setMobileSortOpen] = React.useState(false);
  const [openDetailsFor, setOpenDetailsFor] = React.useState(null);
  const [shortlistDetailsFor, setShortlistDetailsFor] = React.useState(null);
  const [shortlistViewMode, setShortlistViewMode] = React.useState("list"); // "list" or "compare"
  const [compareHighlight, setCompareHighlight] = React.useState(false); // blue-yellow ring around Compare for 2s when cart opens
  const [linkedProducts] = React.useState(new Set()); // Track products linked to ongoing applications

  const cartButtonRef = React.useRef(null);
  const cartPopoverRef = React.useRef(null);

  // When shortlist cart opens, highlight the Compare button for 2s then fade away
  React.useEffect(() => {
    if (!cartOpen) {
      setCompareHighlight(false);
      return;
    }
    setCompareHighlight(true);
    const t = setTimeout(() => setCompareHighlight(false), 12200); // 10s shine (2 full cycles) + 2s fade
    return () => clearTimeout(t);
  }, [cartOpen]);

  cartMascotSuppressRef.current = !!(cartOpen || tourOpen || showOnboarding || showSignupPrompt);

  const cartMascotHideTimerRef = React.useRef(null);
  const cartMascotRetryTimerRef = React.useRef(null);
  const shortlistSizePrevRef = React.useRef(null);

  const hasSeenCartMascot = React.useCallback(() => {
    try {
      return sessionStorage.getItem(cartMascotSeenKey) === "1";
    } catch {
      return false;
    }
  }, [cartMascotSeenKey]);

  const markCartMascotSeen = React.useCallback(() => {
    try {
      sessionStorage.setItem(cartMascotSeenKey, "1");
    } catch {
      /* ignore */
    }
  }, [cartMascotSeenKey]);

  // Polar bear peek/wave: once per session — 15s after landing OR when user adds to shortlist
  const revealCartMascot = React.useCallback(() => {
    if (hasSeenCartMascot()) return;
    if (cartMascotSuppressRef.current) {
      if (cartMascotRetryTimerRef.current) window.clearTimeout(cartMascotRetryTimerRef.current);
      cartMascotRetryTimerRef.current = window.setTimeout(() => {
        cartMascotRetryTimerRef.current = null;
        revealCartMascot();
      }, 1000);
      return;
    }
    setShowCartMascot(true);
    markCartMascotSeen();
    if (cartMascotHideTimerRef.current) window.clearTimeout(cartMascotHideTimerRef.current);
    cartMascotHideTimerRef.current = window.setTimeout(() => {
      cartMascotHideTimerRef.current = null;
      setShowCartMascot(false);
    }, 13000);
  }, [hasSeenCartMascot, markCartMascotSeen]);

  React.useEffect(() => {
    if (hasSeenCartMascot()) return undefined;
    const startTimer = window.setTimeout(() => {
      revealCartMascot();
    }, 15000);
    return () => {
      window.clearTimeout(startTimer);
      if (cartMascotHideTimerRef.current) window.clearTimeout(cartMascotHideTimerRef.current);
      if (cartMascotRetryTimerRef.current) window.clearTimeout(cartMascotRetryTimerRef.current);
    };
  }, [hasSeenCartMascot, revealCartMascot]);

  // Also peek when the user adds a product (ignore hydrate / initial size)
  React.useEffect(() => {
    const prev = shortlistSizePrevRef.current;
    shortlistSizePrevRef.current = shortlist.size;
    if (prev === null) return; // first run — don't treat existing/hydrated shortlist as an "add"
    if (shortlist.size > prev) revealCartMascot();
  }, [shortlist.size, revealCartMascot]);

  React.useEffect(() => {
    if (cartOpen) setShowCartMascot(false);
  }, [cartOpen]);

  const hydrateShortlistFromBackend = React.useCallback(() => {
    if (!isAuthenticated()) return;
    getShortlistedProducts({ limit: 20 })
      .then((res) => {
        if (!res.success || !Array.isArray(res.data)) return;
        const ids = new Set();
        const map = new Map();
        const snapshots = new Map();
        res.data.forEach((p) => {
          const id = (p.mortgage_product || p.product_id || p.id || p.name || "").toString().trim();
          if (id) {
            ids.add(id);
            map.set(id, p); // backend product shape; cart can use it for display
            const snap = buildDisplaySnapshotFromBackendItem(p);
            if (snap) snapshots.set(id, snap);
          }
        });
        setShortlist(ids);
        setShortlistedDetailsMap(map);
        setShortlistedDisplaySnapshots((prev) => {
          const next = new Map(prev);
          snapshots.forEach((snap, id) => {
            const existing = prev.get(id);
            // Keep local snapshot when it has valid LTV so filter changes don't replace it with wrong backend value
            if (existing && existing.ltv != null && Number.isFinite(Number(existing.ltv))) {
              next.set(id, existing);
            } else {
              next.set(id, snap);
            }
          });
          return next;
        });
      })
      .catch((err) => console.warn("Failed to load backend shortlist:", err));
  }, []);

  // When logged in, hydrate shortlist from backend on mount and when page becomes visible (e.g. after remove/clear on borrower portal)
  React.useEffect(() => {
    if (!isAuthenticated()) return;
    let cancelled = false;
    getShortlistedProducts({ limit: 20 })
      .then((res) => {
        if (cancelled || !res.success || !Array.isArray(res.data)) return;
        const ids = new Set();
        const map = new Map();
        res.data.forEach((p) => {
          const id = (p.mortgage_product || p.product_id || p.id || p.name || "").toString().trim();
          if (id) {
            ids.add(id);
            map.set(id, p);
          }
        });
        setShortlist(ids);
        setShortlistedDetailsMap(map);
      })
      .catch((err) => console.warn("Failed to load backend shortlist:", err));
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated()) return;
    const onVisible = () => { hydrateShortlistFromBackend(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [hydrateShortlistFromBackend]);

  // When shortlist cart opens, re-fetch from backend so we show current state (e.g. after remove/clear on borrower portal)
  React.useEffect(() => {
    if (cartOpen && isAuthenticated()) hydrateShortlistFromBackend();
  }, [cartOpen, isAuthenticated, hydrateShortlistFromBackend]);

  const toggleShortlist = async (id) => {
    try {
      const isAdding = !shortlist.has(id);
      // When adding we need the product from current results; when removing it may be only in shortlistedDetailsMap (e.g. after filter change)
      const product = visibleProducts.find((p) => p?.name === id) ?? shortlistedDetailsMap.get(id);
      if (isAdding && !product) {
        reportError(new Error("Product not found"), { where: "toggleShortlist", productId: id });
        return;
      }

      // If removing, check if product is linked to an ongoing application
      if (!isAdding && linkedProducts.has(id)) {
        setEphemeralError("This product cannot be removed because it is linked to an ongoing application.");
        return;
      }

      if (isAdding) {
        if (shortlist.size >= MAX_SHORTLIST) {
          setEphemeralError(`Your shortlist is limited to ${MAX_SHORTLIST} products.`);
          return;
        }

        // Prepare product data for localStorage
        // "Mortgage Term" = Total duration of loan (e.g. 9 years, 14 years, 25 years) - from Backend Product
        // Mortgage term comes from the backend product, not from filters or calculations
        let mortgageDuration = firstFinite(
          product.mortgage_term_years,
          product.mortgage_term_yrs,
          // Try to parse from mortgage_term string (e.g., "25 yrs" -> 25)
          product.mortgage_term ? parseInt(String(product.mortgage_term).replace(/[^\d]/g, ""), 10) : null
        );

        // Only use filter value as fallback if product doesn't have mortgage term
        if (!mortgageDuration || !Number.isFinite(mortgageDuration) || mortgageDuration <= 0) {
          mortgageDuration = parseInt(String(filtersApplied.mortgageTerm).replace(/[^\d]/g, ""), 10) || null;
        }

        // "Product Term" = Fixed/Variable rate period (e.g. 0.5, 2, 5 years) - from Product only (do NOT use term_years/termYears, those are often mortgage term)
        const productTerm = firstFinite(product.product_termsyears, product.product_term_years, product.product_term);

        const sourcingMoney = resolveSourcingMoneyInputs(qs, lastSearchInputsRef.current, product);

        // Ensure we have a valid mortgage amount for calculations
        const mortgageAmountForCalc =
          sourcingMoney.mortgage_amount ||
          safeNum(qs.mortgageAmount) ||
          safeNum(product.loan_amount) ||
          safeNum(product.mortgage_amount) ||
          safeNum(product.mortgageAmount) ||
          0;

        const fallbackCtx = {
          propertyValue: sourcingMoney.property_value || safeNum(qs.propertyValue),
          mortgageAmount: mortgageAmountForCalc,
          estimatedRent: sourcingMoney.est_rent || safeNum(qs.estRent),
          mortgageTermYears: mortgageDuration,
          ...calculatorCaseInputs(filtersApplied),
        };
        const calc = getEffectiveCalc(product, filtersApplied.repaymentType, fallbackCtx);
        const { ltvDisplay, mortgageTermDisplay, icrDisplay } = getBackendDisplayMetrics(
          product,
          calc,
          fallbackCtx
        );

        // If calc.monthly_payment is 0 or missing, but we have a valid mortgage amount and term, recalculate
        // This ensures monthly_payment is always calculated correctly
        let finalMonthlyPayment = calc?.monthly_payment;
        if ((!finalMonthlyPayment || finalMonthlyPayment === 0) && mortgageAmountForCalc > 0 && mortgageDuration > 0) {
          // Recalculate using the product's own data
          const recalcCtx = {
            propertyValue: safeNum(qs.propertyValue) || safeNum(product.property_value),
            mortgageAmount: mortgageAmountForCalc,
            estimatedRent: safeNum(qs.estRent) || safeNum(product.estimated_rent),
            mortgageTermYears: mortgageDuration,
            ...calculatorCaseInputs(filtersApplied),
          };
          const recalc = getEffectiveCalc(product, filtersApplied.repaymentType || "Repayment", recalcCtx);
          if (recalc?.monthly_payment && recalc.monthly_payment > 0) {
            finalMonthlyPayment = recalc.monthly_payment;
          }
        }

        const productData = {
          id: product.name,
          name: product.product_name || product.name,
          product_id: product.name,
          mortgage_product: product.name, // Required by backend API
          product_name: product.product_name || product.name,
          lender_id: product.lender_id || product.lenderId || "unknown",
          lender_name: product.lender_name || product.lenderName || "Unknown Lender",
          lenderId: product.lender_id || product.lenderId || "unknown",
          lenderName: product.lender_name || product.lenderName || "Unknown Lender",
          rate: calc?.interest_rate || product.interest_rate || product.rate,
          interest_rate: calc?.interest_rate || product.interest_rate || product.rate,
          rateType: product.rate_type || product.rateType,
          rate_type: product.rate_type || product.rateType,
          productType: product.product_type || product.productType,
          product_type: product.product_type || product.productType,
          category: (isComplexBtl || isComplexBtlProduct(product))
            ? "CBTL"
            : (product.product_category || product.category),
          product_category: (isComplexBtl || isComplexBtlProduct(product))
            ? "CBTL"
            : (product.product_category || product.category),
          ...pickComplexBtlPropertyFields(product),
          // Loan purpose from product (Purchase, Remortgage, Product Transfer)
          // Prioritize product data, then applied filters, then default to "Purchase"
          loan_purpose: product.loan_purpose || product.loanPurpose || product.loan_purpose_type || filtersApplied.loanPurpose || "Purchase",
          loanPurpose: product.loan_purpose || product.loanPurpose || product.loan_purpose_type || filtersApplied.loanPurpose || "Purchase",

          // Separate Product and Mortgage terms (send product-term fields explicitly so backend stores correctly)
          termYears: productTerm ?? mortgageDuration,
          term_years: mortgageDuration ?? productTerm, // mortgage term (API uses mortgage_term_years for term_years)
          product_term: productTerm ?? null,
          product_term_years: productTerm != null ? productTerm : null,
          // Always prefer resolved productTerm so backend gets correct value (e.g. 2), not raw product.product_termsyears which may be wrong (e.g. "0.5")
          product_termsyears: productTerm != null ? String(productTerm) : (product.product_termsyears != null && product.product_termsyears !== "" ? product.product_termsyears : null),

          termMonths: product.term_months || product.termMonths || (calc?.term_months || 0),
          term_months: product.term_months || product.termMonths || (calc?.term_months || 0),

          // Mortgage term comes from backend product, NOT from filters or calculations
          // Use product's mortgage_term if available, otherwise format mortgageDuration (from product or filter fallback)
          mortgage_term: (product.mortgage_term && product.mortgage_term.trim() !== "")
            ? product.mortgage_term
            : (mortgageDuration && Number.isFinite(mortgageDuration) && mortgageDuration > 0
              ? `${mortgageDuration} yrs`
              : null),
          mortgage_term_years: (mortgageDuration && Number.isFinite(mortgageDuration) && mortgageDuration > 0)
            ? mortgageDuration
            : null,
          ltv: Number.isFinite(ltvDisplay) ? Math.round(ltvDisplay) : (Number.isFinite(calc?.ltv) ? Math.round(calc.ltv) : (product.ltv != null ? Math.round(Number(product.ltv)) : null)),
          loan_amount: calc?.mortgage_amount ?? product.mortgage_amount ?? product.loan_amount ?? safeNum(qs.mortgageAmount) ?? 0,
          mortgageAmount: calc?.mortgage_amount ?? product.mortgage_amount ?? product.loan_amount ?? safeNum(qs.mortgageAmount) ?? 0,
          arrangementFee: calc?.arrangement_fee ?? product.arrangement_fee,
          arrangement_fee: calc?.arrangement_fee ?? product.arrangement_fee,
          arrangement_fee_type: product.product_fee_type ?? product.arrangement_fee_type ?? product.fee_type,
          product_fee_type: product.product_fee_type ?? product.arrangement_fee_type ?? product.fee_type,
          // Preserve 0 when fee is 0% (use ?? and explicit 0 so backend stores percentage type)
          arrangement_fee_percent: (() => {
            const feeType = (product.product_fee_type ?? product.arrangement_fee_type ?? product.fee_type) || "";
            const isPct = String(feeType).toLowerCase().includes("percent");
            const val = product.product_fee_percent ?? product.arrangement_fee_percent ?? product.fee_percent ??
              (isPct ? (product.product_fee_amount ?? product.arrangement_fee ?? null) : null);
            return val != null && val !== "" ? Number(val) : (isPct ? 0 : null);
          })(),
          min_arrangement_fee: product.min_arrangement_fee ?? product.min_fee ?? product.arrangement_min_fee ?? product.product_min_fee ?? null,
          // Use the recalculated monthly_payment if available, otherwise use calc or product value
          // Ensure we always have a calculated value if possible
          // Round to 2 decimal places for storage and display
          monthlyCost: (() => {
            const value = finalMonthlyPayment !== undefined && finalMonthlyPayment !== null
              ? finalMonthlyPayment
              : (calc?.monthly_payment !== undefined && calc?.monthly_payment !== null)
                ? calc.monthly_payment
                : (product.monthly_payment !== undefined && product.monthly_payment !== null ? product.monthly_payment : 0);
            return Number.isFinite(value) ? Math.round(value * 100) / 100 : 0; // Round to 2 decimal places
          })(),
          monthly_payment: (() => {
            const value = finalMonthlyPayment !== undefined && finalMonthlyPayment !== null
              ? finalMonthlyPayment
              : (calc?.monthly_payment !== undefined && calc?.monthly_payment !== null)
                ? calc.monthly_payment
                : (product.monthly_payment !== undefined && product.monthly_payment !== null ? product.monthly_payment : 0);
            return Number.isFinite(value) ? Math.round(value * 100) / 100 : 0; // Round to 2 decimal places
          })(),
          totalCost: calc?.total_cost || product.total_cost,
          total_cost: calc?.total_cost || product.total_cost,
          cost_over_product_term: calc?.total_cost || product.total_cost || calc?.cost_over_product_term || product.cost_over_product_term,
          // APRC
          aprc: product.aprc || product.aprc_rate || product.aprc_percent || calc?.aprc,
          aprc_rate: product.aprc || product.aprc_rate || product.aprc_percent || calc?.aprc,
          aprc_percent: product.aprc || product.aprc_rate || product.aprc_percent || calc?.aprc,
          // ICR from backend only - do NOT calculate
          icr: product.icr_rate || product.icr || product.icr_percent || null,
          icr_rate: product.icr_rate || product.icr || product.icr_percent || null,
          icr_percent: product.icr_percent || null,
          // Product fees
          // IMPORTANT: product_fee_amount should be the percentage value (e.g., 4) when type is "Percentage",
          // NOT the calculated amount (e.g., 40000). The calculated amount goes in arrangement_fee.
          // For percentage type, preserve the original product.product_fee_amount (percentage value)
          // For amount/min type, use the calculated amount
          product_fee_amount: (() => {
            const feeType = product.product_fee_type || product.arrangement_fee_type || product.fee_type || "";
            const isPercentage = String(feeType).toLowerCase().includes("percent");

            if (isPercentage) {
              // For percentage type, preserve the percentage value (e.g. 4 or 0 for 0%) - use ?? so 0 is kept
              const pct = product.product_fee_amount ?? product.arrangement_fee_percent ??
                product.product_fee_percent ?? product.fee_percent ?? null;
              return pct != null && pct !== "" ? Number(pct) : 0;
            } else {
              // For amount/min type, use the calculated amount
              return calc?.arrangement_fee ||
                product.arrangement_fee ||
                product.product_fee_amount ||
                0;
            }
          })(),
          total_fees: calc?.total_fees ?? (calc?.arrangement_fee ?? product.arrangement_fee ?? product.product_fee_amount ?? 0),
          totalFees: calc?.total_fees ?? (calc?.arrangement_fee ?? product.arrangement_fee ?? product.product_fee_amount ?? 0),
          // Property value - required for mortgage illustration document
          // Use explicit check to preserve 0 values and ensure we always have a number or null
          property_value: sourcingMoney.property_value || null,
          propertyValue: sourcingMoney.property_value || null,
          deposit_amount: sourcingMoney.deposit_amount,
          est_rent: sourcingMoney.est_rent,
          estimated_rent: sourcingMoney.est_rent,
          // Repayment Type - required for mortgage illustration document
          repayment_type: filtersApplied.repaymentType || "Repayment",
          repaymentType: filtersApplied.repaymentType || "Repayment",
          // ERC data - include all years 1-5 (including 0%) so shortlisted page shows them
          erc: (() => {
            if (product.erc && Array.isArray(product.erc)) {
              return ensureErcYears1To5(product.erc);
            }
            if (product.early_repayment_charges && Array.isArray(product.early_repayment_charges)) {
              return ensureErcYears1To5(product.early_repayment_charges);
            }
            if (product.shortlisted_erc && Array.isArray(product.shortlisted_erc)) {
              return ensureErcYears1To5(product.shortlisted_erc);
            }
            return buildErcFromYearFields(product);
          })(),
          early_repayment_charges: (() => {
            if (product.erc && Array.isArray(product.erc)) return ensureErcYears1To5(product.erc);
            if (product.early_repayment_charges && Array.isArray(product.early_repayment_charges)) return ensureErcYears1To5(product.early_repayment_charges);
            if (product.shortlisted_erc && Array.isArray(product.shortlisted_erc)) return ensureErcYears1To5(product.shortlisted_erc);
            return buildErcFromYearFields(product);
          })(),
          shortlisted_erc: (() => {
            if (product.erc && Array.isArray(product.erc)) return ensureErcYears1To5(product.erc);
            if (product.early_repayment_charges && Array.isArray(product.early_repayment_charges)) return ensureErcYears1To5(product.early_repayment_charges);
            if (product.shortlisted_erc && Array.isArray(product.shortlisted_erc)) return ensureErcYears1To5(product.shortlisted_erc);
            return buildErcFromYearFields(product);
          })(),
          addedAt: Date.now(),
          _raw: product,
        };

        const displaySnapshot = {
          ltv: productData.ltv != null ? (Number.isFinite(productData.ltv) ? Math.round(productData.ltv) : productData.ltv) : undefined,
          rate: productData.interest_rate ?? productData.rate,
          monthlyPayment: productData.monthly_payment,
          totalCost: productData.total_cost,
          icr: productData.icr_rate ?? productData.icr,
          mortgageTermYears: productData.mortgage_term_years ?? mortgageDuration,
          repaymentType: productData.repayment_type ?? filtersApplied.repaymentType,
        };

        // Logged in as borrower (have session + borrower email): send to backend. Otherwise use localStorage.
        const canUseBackendShortlist = isAuthenticated() && getBorrowerEmail();
        if (canUseBackendShortlist) {
          try {
            console.log("📌 [toggleShortlist] deposit/rent being sent:", {
              sourcingMoney,
              deposit_amount: productData.deposit_amount,
              est_rent: productData.est_rent,
              estimated_rent: productData.estimated_rent,
              qs,
              lastSearch: lastSearchInputsRef.current,
              property_value: productData.property_value,
              loan_amount: productData.loan_amount,
            });
            const result = await addShortlistedProduct(productData);
            if (result.success) {
              setShortlist((prev) => new Set(prev).add(id));
              setShortlistedDetailsMap((prev) => new Map(prev).set(id, product)); // keep so cart shows it after filter change
              setShortlistedDisplaySnapshots((prev) => new Map(prev).set(id, displaySnapshot)); // freeze display so it doesn't change with filters
              // Re-hydrate from backend so shortlist stays in sync (keeps all products after filter changes)
              hydrateShortlistFromBackend();
            } else {
              setEphemeralError(result.error || "Failed to add to shortlist");
            }
          } catch (err) {
            setEphemeralError(err?.message || "Failed to add to shortlist");
          }
          return;
        }

        // Not logged in: Store in localStorage (will be synced to backend on login)
        const LS_PENDING = STORAGE_KEYS.SHORTLIST_PENDING;

        const safeReadArray = (key) => {
          try {
            const raw = localStorage.getItem(key);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        };

        const safeWriteArray = (key, arr) => {
          try {
            localStorage.setItem(key, JSON.stringify(Array.isArray(arr) ? arr : []));
          } catch (e) {
            console.warn("Failed to write to localStorage:", e);
          }
        };

        const existing = safeReadArray(LS_PENDING);
        const updated = [...existing.filter((p) => p.id !== id), productData].slice(0, MAX_SHORTLIST);

        // Debug: Log what's being saved to localStorage
        console.log("💾 [BorrowerSourcing] Saving product to localStorage:", {
          productId: id,
          productName: productData.product_name,
          monthly_payment: productData.monthly_payment,
          monthlyCost: productData.monthlyCost,
          property_value: productData.property_value,
          propertyValue: productData.propertyValue,
          arrangement_fee_type: productData.arrangement_fee_type,
          product_fee_type: productData.product_fee_type,
          arrangement_fee_percent: productData.arrangement_fee_percent,
          product_fee_amount: productData.product_fee_amount,
          arrangement_fee: productData.arrangement_fee,
          hasMonthlyPayment: productData.monthly_payment !== undefined && productData.monthly_payment !== null,
          hasMonthlyCost: productData.monthlyCost !== undefined && productData.monthlyCost !== null,
          hasPropertyValue: (productData.property_value !== undefined && productData.property_value !== null) ||
            (productData.propertyValue !== undefined && productData.propertyValue !== null),
          finalMonthlyPayment_value: finalMonthlyPayment,
          calc_monthly_payment: calc?.monthly_payment,
          product_monthly_payment: product.monthly_payment,
          fullProductData: productData,
          productDataKeys: Object.keys(productData),
          monthlyPaymentInKeys: Object.keys(productData).includes('monthly_payment'),
          monthlyCostInKeys: Object.keys(productData).includes('monthlyCost')
        });

        safeWriteArray(LS_PENDING, updated);

        // Debug: Verify what was actually saved
        const verify = safeReadArray(LS_PENDING);
        const savedProduct = verify.find(p => p.id === id);
        if (savedProduct) {
          console.log("✅ [BorrowerSourcing] Verified saved product in localStorage:", {
            productId: savedProduct.id,
            monthly_payment: savedProduct.monthly_payment,
            monthlyCost: savedProduct.monthlyCost,
            property_value: savedProduct.property_value,
            propertyValue: savedProduct.propertyValue
          });
        }

        // Update local state so cart shows this product even after filter change
        setShortlist((prev) => {
          const next = new Set(prev);
          next.add(id);
          return next;
        });
        setShortlistedDetailsMap((prev) => new Map(prev).set(id, product));
        setShortlistedDisplaySnapshots((prev) => new Map(prev).set(id, displaySnapshot));

      } else {
        // Removing from shortlist (logged in as borrower: remove from backend)
        if (isAuthenticated() && getBorrowerEmail()) {
          try {
            const result = await removeShortlistedProductByMortgageProduct(id);
            if (result.success) {
              setShortlist((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
              });
              setShortlistedDetailsMap((prev) => {
                const next = new Map(prev);
                next.delete(id);
                return next;
              });
              const LS_PENDING = STORAGE_KEYS.SHORTLIST_PENDING;
              try {
                const raw = localStorage.getItem(LS_PENDING);
                const parsed = raw ? JSON.parse(raw) : [];
                if (Array.isArray(parsed) && parsed.some((p) => (p.id || p.product_id || p.mortgage_product) === id)) {
                  const updated = parsed.filter((p) => (p.id || p.product_id || p.mortgage_product) !== id);
                  localStorage.setItem(LS_PENDING, JSON.stringify(updated));
                }
              } catch { /* ignore */ }
              // Re-hydrate so UI matches backend (e.g. borrower portal will show same after refresh)
              hydrateShortlistFromBackend();
            } else {
              setEphemeralError(result.error || "Failed to remove from shortlist");
            }
          } catch (err) {
            setEphemeralError(err?.message || "Failed to remove from shortlist");
          }
          return;
        }

        const LS_PENDING = STORAGE_KEYS.SHORTLIST_PENDING;
        const safeReadArray = (key) => {
          try {
            const raw = localStorage.getItem(key);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        };

        const safeWriteArray = (key, arr) => {
          try {
            localStorage.setItem(key, JSON.stringify(Array.isArray(arr) ? arr : []));
          } catch (e) {
            console.warn("Failed to write to localStorage:", e);
          }
        };

        const existing = safeReadArray(LS_PENDING);
        const updated = existing.filter((p) => p.id !== id);
        safeWriteArray(LS_PENDING, updated);

        setShortlist((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setShortlistedDetailsMap((prev) => {
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
        setShortlistedDisplaySnapshots((prev) => {
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
      }
    } catch (err) {
      reportError(err, { where: "toggleShortlist", productId: id });
      setEphemeralError("Failed to update shortlist");
    }
  };

  React.useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;

      if (
        cartButtonRef.current?.contains(t) ||
        cartPopoverRef.current?.contains(t)
      ) {
        return;
      }

      if (
        t.closest?.('[data-role="details-popover"]') ||
        t.closest?.('[data-role="details-toggle"]')
      ) {
        return;
      }

      setOpenDetailsFor(null);
      setCartOpen(false);
      setShortlistDetailsFor(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (shortlistDetailsFor) return setShortlistDetailsFor(null);
      if (cartOpen) return setCartOpen(false);
      if (openDetailsFor) return setOpenDetailsFor(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [shortlistDetailsFor, cartOpen, openDetailsFor]);

  React.useEffect(() => {
    const lock = cartOpen || !!shortlistDetailsFor;
    if (!lock) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [cartOpen, shortlistDetailsFor]);

  const isVisibleProduct = (p) => {
    const status = String(
      p?.product_status || p?.status || (p?._isScheduled ? "scheduled" : "")
    ).toLowerCase();
    // Filter out deactivated products
    if (status === "deactivated") return false;
    if (!status) return true;
    return status === "active" || status === "live" || status === "scheduled";
  };

  const visibleProducts = React.useMemo(
    () => calculatedProducts.filter(isVisibleProduct),
    [calculatedProducts]
  );

  const selectedPropertyType = filtersApplied?.complexPropertyType || "";
  const hasPropertyTypeMatches = React.useMemo(
    () =>
      !selectedPropertyType ||
      visibleProducts.some((p) => productMeetsPropertyCriteria(p, filtersApplied)),
    [visibleProducts, filtersApplied, selectedPropertyType]
  );
  const showNoPropertyTypeProducts = visibleProducts.length > 0 && !hasPropertyTypeMatches;
  const shownProductCount = showNoPropertyTypeProducts ? 0 : visibleProducts.length;
  const noPropertyTypeProductsMessage = (
    <div className="rounded-xl border border-slate-200 bg-white">
      <SleepyPolarBearEmptyState
        title={`No products for ${selectedPropertyType}.`}
        subtitle="Try a different property type or adjust the additional filters."
      />
    </div>
  );

  // Shortlist is never cleared when filters change — we keep shortlist + shortlistedDetailsMap so all shortlisted products remain after filter change. Build list from map first, then visible results.
  // Attach _shortlistId (the key in the shortlist Set) so Remove uses it — backend products may have p.name as doc name, which differs from the Set key (mortgage_product).
  const shortlistedArray = React.useMemo(() => {
    const out = [];
    shortlist.forEach((id) => {
      const fromMap = shortlistedDetailsMap.get(id);
      const fromVisible = visibleProducts.find((p) => p?.name === id);
      const product = fromMap || fromVisible;
      if (product) out.push({ ...product, _shortlistId: id });
    });
    return out.slice(0, MAX_SHORTLIST);
  }, [shortlist, shortlistedDetailsMap, visibleProducts]);

  const shortlistIsFull = shortlist.size >= MAX_SHORTLIST;

  const onApplyShortlist = () => {
    try {
      if (shortlistedArray.length === 0) throw new Error("Add up to 5 products to proceed.");

      if (isAuthenticated()) {
        navigate(shortlistPathForCategory("BTL"), { replace: false });
        return;
      }

      const termYears =
        parseInt(String(filtersApplied.mortgageTerm).replace(/[^\d]/g, ""), 10) || 25;

      const fallbackCtx = {
        propertyValue: safeNum(qs.propertyValue),
        mortgageAmount: safeNum(qs.mortgageAmount),
        estimatedRent: safeNum(qs.estRent),
        mortgageTermYears: termYears,
        ...calculatorCaseInputs(filtersApplied),
      };

      navigate("/borrower/signup", {
        state: {
          shortlistedProducts: shortlistedArray.map((p) => {
            const calc = getEffectiveCalc(p, filtersApplied.repaymentType, fallbackCtx);
            const { ltvDisplay } = getBackendDisplayMetrics(p, calc, fallbackCtx);
            const ltvOut = Number.isFinite(ltvDisplay)
              ? Math.round(ltvDisplay)
              : Math.round(safeNum(calc?.ltv));

            return {
              ...p, // Pass all raw fields for robustness
              id: p.name,
              product_name: p.product_name,
              rate: calc?.interest_rate,
              productTerm: parseFloat(p.product_termsyears || p.product_term_years || "0"),
              productType: p.product_type,
              ltv: ltvOut,
              repayment: calc?.monthly_payment,
              fee: calc?.arrangement_fee,
              totalCost: calc?.total_cost,
              mortgageAmount: safeNum(qs.mortgageAmount), // Pass context for fee calc
              property_value: safeNum(qs.propertyValue),
              propertyValue: safeNum(qs.propertyValue),
              repayment_type: filtersApplied.repaymentType || "Repayment",
              repaymentType: filtersApplied.repaymentType || "Repayment",
            };
          }),
          sourcingInputs: { ...qs },
          filters: { ...filtersApplied },
        },
        replace: false,
      });
    } catch (err) {
      reportError(err, { where: "onApplyShortlist" });
    }
  };

  const trackClickForProduct = React.useCallback(async (p) => {
    try {
      await trackProductClick({
        product_id: p?.name,
        // Backend resolves Mortgage Product by document name, not display title.
        product_name: p?.name || p?.product_name || "",
        source: "borrower_sourcing",
      });
      trackEngagementEvent("product_view", { product: p?.name });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Click tracking failed:", err?.message || err);
    }
  }, [trackEngagementEvent]);

  const moneyInput = (label, key) => {
    const hasErr = !!errors[key];
    const errId = `${key}-error`;
    const showMinimumRentValidation =
      key === "estRent" &&
      requiredDSC > 0 &&
      minimumRentRequired != null &&
      minimumRentRequired > 0 &&
      safeNum(qs.estRent) < minimumRentRequired;
    return (
      <InputShell
        key={key}
        label={label}
        labelClassName="text-white font-semibold text-[16px] md:text-[18px]"
      >
        <div className={"relative" + (shakingQuickFields[key] ? " quick-field-shake" : "")}>
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-black text-[16px] md:text-[18px]">
            £
          </span>
          <input
            type="number"
            min={0}
            inputMode="decimal"
            aria-invalid={hasErr}
            aria-describedby={hasErr ? errId : undefined}
            className={
              "no-spinner w-full rounded-xl border " +
              (hasErr ? "border-red-500 ring-1 ring-red-300" : "border-sky-200 ring-1 ring-slate-200") +
              " bg-white pl-8 pr-10 py-3 text-[16px] md:text-[18px] focus:outline-none focus:ring-1 focus:ring-sky-300"
            }
            value={qs[key]}
            onKeyDown={onQuickKeyDown}
            onPaste={(e) => onQuickPaste(key, e)}
            onChange={(e) => onQuickChange(key, e)}
            onBlur={() => {
              const nextErrors = validateAll(qs);
              setErrors(nextErrors);
              const related =
                key === "depositAmount" || key === "mortgageAmount"
                  ? [key, key === "depositAmount" ? "mortgageAmount" : "depositAmount"]
                  : [key];
              const invalid = related.filter(
                (k) =>
                  qs[k] !== "" &&
                  (nextErrors[k] || (k === "estRent" && showMinimumRentValidation))
              );
              shakeInvalidQuickFields(invalid);
            }}
            placeholder="0"
          />
          <button
            type="button"
            aria-label="Clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-sky-200 bg-sky-100 text-sky-700 hover:bg-sky-200 text-[18px] leading-none"
            onClick={() => {
              setQs((prev) => {
                const next = { ...prev, [key]: "" };
                setErrors(validateAll(next));
                return next;
              });
            }}
          >
            ×
          </button>
        </div>
        {hasErr ? (
          <div id={errId} role="alert" aria-live="polite" className="mt-1 text-xs font-bold text-white">
            {errors[key]}
          </div>
        ) : null}
        {showMinimumRentValidation ? (
          <div className="mt-1 text-xs font-bold text-white">
            Minimum required rent for {requiredDSC}% DSC is £
            {minimumRentRequired.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            /month.
            <div className="mt-0.5 text-[11px] font-semibold text-white/90">
              Calculated on {filtersApplied?.repaymentType === "Repayment" ? "Repayment" : "Interest only"} basis.
            </div>
          </div>
        ) : null}
      </InputShell>
    );
  };


  const termYearsForApplied =
    parseInt(String(filtersApplied.mortgageTerm).replace(/[^\d]/g, ""), 10) || 25;

  const requiredDSC = getDSCRequirement(filtersApplied);
  const residencyLabel = getResidencyChipLabel(filtersApplied.residency);

  const fallbackParamsForCards = {
    propertyValue: safeNum(qs.propertyValue),
    mortgageAmount: safeNum(qs.mortgageAmount),
    estimatedRent: safeNum(qs.estRent),
    mortgageTermYears: termYearsForApplied,
    ...calculatorCaseInputs(filtersApplied),
  };

  const isGuest = !isAuthenticated();
  // Don't gate mid-tour, so the "add to shortlist" step keeps working on real cards.
  const gateActive = isGuest && !tourOpen && visibleProducts.length > GUEST_FREE_PRODUCTS;
  const shownProducts = gateActive ? visibleProducts.slice(0, GUEST_FREE_PRODUCTS) : visibleProducts;
  const hiddenProducts = gateActive ? visibleProducts.slice(GUEST_FREE_PRODUCTS) : [];

  const renderResultsList = () => (
    <>
      {shownProducts.map((p) => (
        <ProductCard
          key={p?.name ?? Math.random().toString(36).slice(2)}
          p={p}
          isOpen={openDetailsFor === p?.name}
          onToggle={() => setOpenDetailsFor((cur) => (cur === p?.name ? null : p?.name))}
          shortlisted={shortlist.has(p?.name)}
          shortlistIsFull={shortlistIsFull}
          onToggleShortlist={() => toggleShortlist(p?.name)}
          repaymentType={filtersApplied.repaymentType}
          fallbackParams={fallbackParamsForCards}
          onTrackClick={trackClickForProduct}
          requiredDSC={requiredDSC}
          residencyLabel={residencyLabel}
          displaySnapshot={shortlistedDisplaySnapshots.get(p?.name)}
        />
      ))}
      {gateActive && (
        <LockedResultsGate
          hiddenProducts={hiddenProducts}
          hiddenCount={hiddenProducts.length}
          totalCount={visibleProducts.length}
          onUnlock={() => {
            setSignupPromptStartOnForm(true);
            setShowSignupPrompt(true);
          }}
          repaymentType={filtersApplied.repaymentType}
          fallbackParams={fallbackParamsForCards}
          requiredDSC={requiredDSC}
          residencyLabel={residencyLabel}
        />
      )}
    </>
  );

  const shortlistDetailsProduct = React.useMemo(() => {
    if (!shortlistDetailsFor) return null;
    return shortlistedArray.find((p) => p?.name === shortlistDetailsFor) || null;
  }, [shortlistDetailsFor, shortlistedArray]);

  const tourFiltersChanged = React.useMemo(() => {
    const base = tourFiltersBaselineRef.current;
    if (!base) return false;
    return TOUR_FILTER_KEYS.some((k) => String(filtersDraft?.[k] ?? "") !== String(base?.[k] ?? ""));
  }, [filtersDraft]);

  const tourCanNext = React.useMemo(() => {
    switch (tourStepId) {
      case "quickbar":
        return inputsReady;
      case "results-panel":
        return inputsReady && visibleProducts.length > 0;
      case "filters":
        return tourFiltersChanged;
      case "add-to-shortlist":
        return shortlist.size > 0;
      case "shortlist-cart-window":
        return true;
      default:
        return true;
    }
  }, [tourStepId, inputsReady, visibleProducts.length, tourFiltersChanged, shortlist.size]);

  const tourNextHint = React.useMemo(() => {
    if (tourCanNext) return "";
    const mobile = isMobileTourViewport();
    switch (tourStepId) {
      case "quickbar":
        return "Enter property value, deposit, mortgage and rent.";
      case "results-panel":
        return "Wait for products to load.";
      case "filters":
        return mobile
          ? "Change a filter, then tap Show products."
          : "Change at least one filter.";
      case "add-to-shortlist":
        return "Add a product to your shortlist.";
      default:
        return "";
    }
  }, [tourCanNext, tourStepId]);

  const activeFilterCount = React.useMemo(
    () => countActiveSourcingFilters(filtersDraft),
    [filtersDraft]
  );

  const clearAllFiltersDraft = React.useCallback(() => {
    setFiltersDraft({
      repaymentType: "Interest only",
      ltv: "75%",
      epcGreen: "A-C",
      mortgageTerm: "25 yrs",
      productTerm: "5",
      productType: "Fixed",
      borrowerType: "Ltd Co.",
      loanPurpose: "Purchase",
      homeowner: "",
      residency: "",
      btlExp: "",
      credit: "",
      tenancyType: "",
      tenure: "",
      leaseTerm: "",
      exAuthority: "",
      depositOrigination: "",
      day1Remortgage: "",
      purchaseType: "",
      taxpayerType: "Basic Rate (125%)",
      ...propertyFilterDefaults(isComplexBtl),
    });
  }, [isComplexBtl]);

  const handleMobileShowProducts = React.useCallback(() => {
    setFiltersApplied(filtersDraft);
    setMobileFiltersOpen(false);
  }, [filtersDraft]);

  return (
    <ErrorBoundary onError={reportError}>
      <ErrorCtx.Provider value={{ reportError }}>
        <ComingSoonStyles />
        <ErrorBanner message={ephemeralError} onClose={() => setEphemeralError("")} />
        <PolarBearOnboarding
          open={showOnboarding}
          productLabel={productLabel}
          onStartTour={() => {
            setShowOnboarding(false);
            tourFiltersBaselineRef.current = { ...filtersDraft };
            setTourStepIndex(0);
            setTourOpen(true);
            try {
              localStorage.setItem(onboardingSeenKey, "1");
            } catch {
              // ignore
            }
          }}
          onSkip={() => {
            setShowOnboarding(false);
            try {
              localStorage.setItem(onboardingSeenKey, "1");
            } catch {
              // ignore
            }
          }}
        />

        {/* Timed soft prompt (30s) or guest results-gate CTA. Dismiss closes for this session. */}
        <SourcingSignupPrompt
          open={showSignupPrompt && !showOnboarding && !tourOpen}
          onClose={dismissSignupPrompt}
          onSignupSuccess={handleSignupPromptSuccess}
          startOnForm={signupPromptStartOnForm}
        />

        <BtlTour
          open={tourOpen}
          stepIndex={tourStepIndex}
          cartOpen={cartOpen}
          layoutKey={`${mobileFiltersOpen}-${cartOpen}`}
          onNext={() => {
            if (tourStepId === "filters") setMobileFiltersOpen(false);
            setTourStepIndex((prev) => {
              const next = prev + 1;
              if (next > 4) {
                setTourOpen(false);
                setMobileFiltersOpen(false);
                try {
                  localStorage.setItem(tourDoneKey, "1");
                } catch {
                  // ignore
                }
                return prev;
              }
              return next;
            });
          }}
          onPrev={() => {
            if (tourStepId === "filters") setMobileFiltersOpen(false);
            if (tourStepId === "shortlist-cart-window") setCartOpen(false);
            setTourStepIndex((prev) => Math.max(prev - 1, 0));
          }}
          onClose={() => {
            setTourOpen(false);
            setMobileFiltersOpen(false);
            try {
              localStorage.setItem(tourDoneKey, "1");
            } catch {
              // ignore
            }
          }}
          onEnsureStep={(id) => {
            if (id === "shortlist-cart-window") setCartOpen(true);
            if (id === "filters" && isMobileTourViewport()) {
              setMobileFiltersOpen(true);
            }
          }}
          canNext={tourCanNext}
          nextHint={tourNextHint}
        />

        <TopNav transparentOnTop={false} centered />

        <section className="relative w-full overflow-hidden hero-landscape-responsive sourcing-hero-responsive min-h-[280px] h-[min(52vh,24rem)] md:h-[480px] md:max-h-none md:min-h-[280px]">
          <img
            src="/Sourcing_FINAL.webp"
            alt="To Let board"
            className="sourcing-hero-img absolute inset-0 h-full w-full object-cover object-[58%_22%] md:object-center"
            loading="eager"
            fetchPriority="high"
            onError={(e) => {
              e.currentTarget.alt = "Hero image not available";
              e.currentTarget.style.opacity = "0.2";
            }}
          />
          <div className="absolute inset-0 bg-slate-900/50 md:bg-slate-900/45" />

          <div className="relative z-10 h-full sourcing-hero-inner hero-inner">
            <div className="max-w-screen-xl mx-auto h-full px-4">
              <div className="flex h-full flex-col justify-end gap-3 md:grid md:grid-cols-2 md:items-start md:gap-8 md:justify-start min-h-0 pt-0 md:pt-28">
                <h1 className="text-white text-[1.65rem] leading-[1.15] sm:text-[30px] md:text-[40px] font-extrabold md:leading-[1.1]">
                  <span className="md:hidden">Welcome to Your Personalised Sourcing Platform</span>
                  <span className="hidden md:inline">
                    Welcome to Your <br /> Personalised Sourcing <br /> Platform
                  </span>
                </h1>

                <p className="text-white/95 text-sm sm:text-[16px] md:text-[17px] leading-relaxed max-w-[560px] md:mt-0">
                  Effortlessly compare and secure the best{" "}
                  {isComplexBtl ? "complex buy-to-let" : "buy-to-let"} mortgage products across the UK
                  market. Register now to unlock powerful tools that guide you through every step—from
                  tailored rate comparisons and rental yield calculations to real-time application
                  tracking—so you stay informed and in control.
                </p>

                <div className="md:col-span-2 mt-1 md:mt-8 flex flex-wrap items-center gap-x-3 gap-y-1 pb-0 md:pb-0">
                  <button
                    type="button"
                    onClick={() => {
                      setTourOpen(false);
                      setTourStepIndex(0);
                      setShowOnboarding(true);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600/90 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-sky-600 md:rounded-2xl md:gap-2 md:px-6 md:py-4 md:text-base md:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1"
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5 md:h-5 md:w-5 shrink-0" />
                    Tour guide
                  </button>
                  <Link
                    to="/explore-borrower-journey"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-white/50 bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-white backdrop-blur-sm hover:bg-white/20 md:rounded-2xl md:border-2 md:border-white/70 md:gap-2 md:px-6 md:py-4 md:text-base md:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900/80"
                  >
                    <Route className="h-3.5 w-3.5 md:h-5 md:w-5 shrink-0" />
                    Explore borrower journey
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div id="quickbar" className="mt-0 bg-sky-400 relative z-10 quickbar-landscape-responsive">
          <div className="max-w-screen-xl mx-auto px-4 py-6 sm:py-10 relative z-30 transform-gpu -translate-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
              {[
                ["Property value", "propertyValue"],
                ["Deposit amount", "depositAmount"],
                ["Mortgage amount", "mortgageAmount"],
                ["Est. rent", "estRent"],
              ].map(([label, key]) => moneyInput(label, key))}
            </div>
          </div>
        </div>

        {/* Mobile: sticky Filters / Sort bar — top-20 clears fixed TopNav (top-4 + h-16) */}
        <div className="md:hidden sticky top-20 z-40 bg-gray-100 border-b border-slate-200">
          <div className="max-w-screen-xl mx-auto px-4 py-2.5">
            <div className="flex gap-2">
              <button
                type="button"
                id="mobile-filters-trigger"
                onClick={() => setMobileFiltersOpen(true)}
                className="relative flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
              >
                <SlidersHorizontal className="h-4 w-4 text-slate-600" />
                Filters
                {activeFilterCount > 0 ? (
                  <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-sky-500 px-1.5 text-[11px] font-bold text-white">
                    {activeFilterCount}
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                onClick={() => setMobileSortOpen(true)}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
              >
                <SlidersHorizontal className="h-4 w-4 text-slate-600" />
                Sort
              </button>
              <button
                type="button"
                onClick={openReviewPrompt}
                className={SOURCING_FEEDBACK_MOBILE_BAR_BUTTON_CLASS}
              >
                <MessageSquareHeart className="h-4 w-4 shrink-0" />
                Feedback
              </button>
            </div>
          </div>
        </div>

          <div className="max-w-screen-xl mx-auto px-4 -mt-4 md:-mt-6 relative z-20 min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-[320px,minmax(0,1fr)] gap-4 pb-24 md:pb-24">
            <div id="filters" className="hidden md:block rounded-2xl bg-gray-100 border border-slate-200 p-3.5 shrink-0 md:max-h-[85vh] md:overflow-y-auto">
              <div className="text-[16px] md:text-[18px] font-semibold text-slate-900 mb-2.5">
                Repayment
              </div>

              <div className="flex items-center gap-4">
                <label className="inline-flex items-center gap-2 text-[15px] md:text-[16px]">
                  <input
                    type="radio"
                    name="repayment"
                    checked={filtersDraft.repaymentType === "Interest only"}
                    onChange={() => setFilterDraft("repaymentType", "Interest only")}
                  />
                  Interest only
                </label>
                <label className="inline-flex items-center gap-2 text-[15px] md:text-[16px]">
                  <input
                    type="radio"
                    name="repayment"
                    checked={filtersDraft.repaymentType === "Repayment"}
                    onChange={() => setFilterDraft("repaymentType", "Repayment")}
                  />
                  Repayment
                </label>
              </div>

              <ComplexPropertyTypePanel
                filtersDraft={filtersDraft}
                setFilterDraft={setFilterDraft}
                defaultType={isComplexBtl ? "HMO" : "BTL"}
              />

              <div className="mt-4 grid grid-cols-2 md:grid-cols-1 gap-3">
                {[
                  ["LTV", "ltv", ["", "60%", "65%", "70%", "75%", "80%", "85%"]],
                  ["EPC rating", "epcGreen", ["", "A-C", "D-F"]],
                  ["Mortgage term", "mortgageTerm", ["", "5 yrs", "10 yrs", "15 yrs", "20 yrs", "25 yrs", "30 yrs", "40 yrs"]],
                  ["Product term", "productTerm", ["", "0.5", "1", "2", "3", "5", "10", "Lifetime"]],
                  ["Product type", "productType", ["", "Fixed", "Variable", "Tracker", "Discounted"]],
                  ["Loan purpose", "loanPurpose", ["", "Purchase", "Remortgage"]],
                  ["Borrower type", "borrowerType", ["", "Individual/partnership", "Ltd Co.", "Pension/Trust"]],
                  ["Home owner", "homeowner", ["", "No", "Yes"]],
                  ["Residency", "residency", ["", "UK", "International", "Expat"]],
                  [`${productLabel} experience`, "btlExp", ["", "0", "1-5", "5+"]],
                  ["Credit worthiness", "credit", ["", "Excellent", "Fair", "Poor"]],
                  ["Tenancy Type", "tenancyType", ["", "AST", "Lease", "Licence"]],
                  ["Taxpayer type (DSC)", "taxpayerType", ["", "Basic Rate (125%)", "Higher Rate (140%)"]],
                ].map(([label, key, options]) => (
                  <InputShell
                    key={key}
                    label={label}
                    labelClassName="text-slate-900 font-semibold text-[15px] md:text-[16px]"
                  >
                    <Select
                      value={filtersDraft[key]}
                      onChange={(e) => {
                        if (key === "ltv") {
                          onLTVFilterChange(e.target.value);
                        } else {
                          setFilterDraft(key, e.target.value);
                        }
                      }}
                    >
                      {options.map((o, i) => (
                        <option key={i} value={o}>
                          {getSourcingFilterOptionLabel(key, o)}
                        </option>
                      ))}
                    </Select>
                  </InputShell>
                ))}

                <InputShell label="F/H or L/H" labelClassName="text-slate-900 font-semibold text-[15px] md:text-[16px]">
                  <Select value={filtersDraft.tenure} onChange={(e) => setFilterDraft("tenure", e.target.value)}>
                    {["", "FREEHOLD", "LEASEHOLD"].map((o, i) => (
                      <option key={i} value={o}>
                        {o || "Select"}
                      </option>
                    ))}
                  </Select>
                </InputShell>

                {filtersDraft.tenure === "LEASEHOLD" && (
                  <div className="col-span-2 md:col-span-1">
                    <InputShell
                      label="Leasehold remaining term"
                      labelClassName="text-slate-900 font-semibold text-[15px] md:text-[16px]"
                    >
                      <input
                        type="number"
                        min={0}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[15px] md:text-[16px] ring-1 ring-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-300"
                        placeholder="Years"
                        value={filtersDraft.leaseTerm}
                        onChange={(e) => setFilterDraft("leaseTerm", e.target.value)}
                      />
                    </InputShell>
                  </div>
                )}

                {[
                  ["Ex Local Authority", "exAuthority", ["", "No", "Yes"]],
                  ["Deposit Origination", "depositOrigination", ["", "Savings", "Gifted", "Equity"]],
                  ["Day 1 Remortgage", "day1Remortgage", ["", "Pound for Pound", "Market Value", "Refurb"]],
                ].map(([label, key, options]) => (
                  <InputShell
                    key={key}
                    label={label}
                    labelClassName="text-slate-900 font-semibold text-[15px] md:text-[16px]"
                  >
                    <Select value={filtersDraft[key]} onChange={(e) => setFilterDraft(key, e.target.value)}>
                      {options.map((o, i) => (
                        <option key={i} value={o}>
                          {o || "Select"}
                        </option>
                      ))}
                    </Select>
                  </InputShell>
                ))}

                <button
                  className="col-span-2 md:col-span-1 mt-3.5 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-sky-300 text-sky-700 font-semibold px-4 py-2.5 text-[15px] transition-colors hover:bg-sky-50"
                  onClick={() => {
                    localStorage.removeItem("ds_sourcing_qs");
                    localStorage.removeItem(filtersStorageKey);
                    window.location.reload();
                  }}
                >
                  Reset All Filters
                </button>
              </div>
            </div>

            <div id="results-panel" className="rounded-2xl bg-gray-100 border border-slate-200 p-3 pb-28 md:pb-3">
              {!inputsReady && !keepProductsDuringLoad ? (
                allInputsEmpty ? (
                  <div className="rounded-xl border border-slate-200 bg-white">
                    <SleepyPolarBearEmptyState />
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
                    Enter valid values for all four fields above to see matching products.
                  </div>
                )
              ) : isLoading && keepProductsDuringLoad ? (
                <div className="relative">
                  <div className="absolute inset-0 z-20 rounded-xl border border-slate-200 bg-white/80 backdrop-blur-sm p-10 flex flex-col items-center justify-center">
                    <Loader2 className="h-12 w-12 animate-spin text-sky-500 mb-4" />
                    <p className="text-slate-600">Updating products...</p>
                  </div>
                  <div className="opacity-50">
                    {visibleProducts.length === 0 ? (
                      <div className="rounded-xl border border-slate-200 bg-white">
                        <SleepyPolarBearEmptyState />
                      </div>
                    ) : showNoPropertyTypeProducts ? (
                      noPropertyTypeProductsMessage
                    ) : (
                      <>
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <SourcingLiveCount count={shownProductCount} />
                          <div className="hidden md:block">
                            <SortControl sortBy={sortBy} setSortBy={setSortBy} />
                          </div>
                        </div>
                        {renderResultsList()}
                      </>
                    )}
                  </div>
                </div>
              ) : isLoading ? (
                <div className="rounded-xl border border-slate-200 bg-white p-10 flex flex-col items-center justify-center">
                  <Loader2 className="h-12 w-12 animate-spin text-sky-500 mb-4" />
                  <p className="text-slate-600">Updating products...</p>
                </div>
              ) : (
                <>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <SourcingLiveCount
                      count={shownProductCount}
                      calcSeconds={calcSeconds}
                      extra={
                        requiredDSC > 0 ? (
                          <span className="px-1.5 py-px md:px-2 md:py-0.5 bg-sky-100 text-sky-700 rounded-full font-semibold text-[10px] md:text-xs leading-tight whitespace-nowrap">
                            • Filtering by {requiredDSC}% DSC
                          </span>
                        ) : null
                      }
                    />
                    <div className="hidden md:block">
                      <SortControl sortBy={sortBy} setSortBy={setSortBy} />
                    </div>
                  </div>

                  {visibleProducts.length === 0 ? (
                    dscOnlyFailures.lowest ? (
                      <div className="rounded-xl border border-slate-200 bg-white">
                        <div className="px-6 py-6 text-center">
                          <div className="rounded-2xl bg-sky-50 border border-sky-200 p-4">
                            <p className="text-sm font-semibold text-slate-700 mb-1">
                              No products meet the current DSC requirement.
                            </p>
                            <p className="text-sm text-slate-600 mb-3">
                              The lowest minimum monthly rent needed to qualify is:
                            </p>
                            <div className="text-[28px] font-extrabold text-sky-600">
                              {formatMoney(dscOnlyFailures.lowest.minimumMonthlyRent)}
                              <span className="text-base font-semibold text-slate-500 ml-1">/month</span>
                            </div>
                            <p className="text-sm font-semibold text-slate-600">
                              {formatMoney(dscOnlyFailures.lowest.weeklyEquivalent)}/week equivalent
                            </p>
                            <p className="mt-2 text-xs text-slate-500">
                              {dscOnlyFailures.lowest.productName}
                              {dscOnlyFailures.lowest.lenderName ? ` (${dscOnlyFailures.lowest.lenderName})` : ""}
                              {" "}· {dscOnlyFailures.lowest.mode}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              Your current rent: {formatMoney(safeNum(qs.estRent))}/month
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : propertySizeHiddenCount > 0 && dscOnlyFailures.count === 0 ? (
                      noPropertyTypeProductsMessage
                    ) : requiredDSC > 0 ? (
                      <div className="rounded-xl border border-slate-200 bg-white">
                        <div className="px-6 py-6 text-center">
                          <div className="rounded-2xl bg-sky-50 border border-sky-200 p-4">
                            <p className="text-sm font-semibold text-slate-700 mb-1">
                              No products meet the current DSC requirement.
                            </p>
                            <p className="text-sm text-slate-600 mb-3">
                              Based on the cheapest available rate, the minimum monthly rent needed to qualify is:
                            </p>
                            <div className="text-[28px] font-extrabold text-sky-600">
                              £{(minimumRentRequired ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              <span className="text-base font-semibold text-slate-500 ml-1">/month</span>
                            </div>
                            <p className="mt-2 text-xs text-slate-500">
                              Your current rent: £{safeNum(qs.estRent).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/month
                              {" "}({requiredDSC}% DSC applied)
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-slate-200 bg-white">
                        <SleepyPolarBearEmptyState />
                      </div>
                    )
                  ) : showNoPropertyTypeProducts ? (
                    noPropertyTypeProductsMessage
                  ) : (
                    renderResultsList()
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <footer
          className="border-t border-black bg-black"
          aria-label="Regulatory disclaimer"
        >
          <div className="max-w-screen-xl mx-auto px-4 py-8 md:py-10 pb-28 md:pb-10">
            <p className="text-xs leading-relaxed text-white">
              Dynesourcer Ltd is authorised and regulated by the Financial Conduct Authority under firm
              reference number 1050262. The information on this website is provided for general information
              only and does not constitute mortgage advice, investment advice, legal advice, tax advice, or a
              personal recommendation unless explicitly stated otherwise. You should carry out your own due
              diligence and obtain independent legal, tax, and financial advice before proceeding with any
              purchase or finance arrangement. Property investment and buy-to-let involve risk, and returns
              are not guaranteed.
            </p>
          </div>
        </footer>

        {/* Shortlist FAB — desktop only; mobile uses bottom bar */}
        <CartMascotNudge open={showCartMascot} />
        <button
          ref={cartButtonRef}
          type="button"
          onClick={() => setCartOpen((v) => !v)}
          className={`
            !fixed !bottom-4 !right-3 md:!bottom-24 md:!right-6 z-[50]
            hidden md:inline-flex items-center justify-center
            rounded-full md:rounded-2xl bg-white p-2 md:px-6 md:py-4
            shadow-md md:shadow-xl hover:shadow-lg md:hover:shadow-2xl transition-all
            border-2 border-sky-400
            ${shortlistIsFull ? "ring-2 ring-amber-400" : ""}
            cart-shiny-border
          `}
          title="View shortlist"
          aria-label="View shortlist"
        >
          <span className="relative inline-flex items-center gap-1.5 md:gap-3 z-10">
            <ShoppingCart className="h-4 w-4 md:h-7 md:w-7 text-slate-700" />
            <span className="cart-text-fade hidden md:inline text-base font-semibold text-slate-800" data-text="Apply with Shortlist">
              Apply with Shortlist
            </span>
            <span className="inline-flex h-4 min-w-[1rem] md:h-7 md:min-w-[1.75rem] items-center justify-center rounded-full bg-sky-500 px-1 md:px-2.5 text-[10px] md:text-sm font-bold text-white">
              {shortlist.size}
            </span>
          </span>
        </button>

        {/* Mobile: floating Apply with Shortlist bar */}
        <div id="mobile-shortlist-bar" className="md:hidden fixed inset-x-0 bottom-0 z-[50] border-t border-slate-200 bg-white shadow-[0_-4px_20px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]">
          <button
            type="button"
            onClick={() => setCartOpen((v) => !v)}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-semibold text-slate-800 cart-shiny-border ${shortlistIsFull ? "ring-2 ring-inset ring-amber-300" : ""}`}
            aria-label="Apply with shortlist"
          >
            <ShoppingCart className="h-5 w-5 text-slate-700" />
            <span className="cart-text-fade" data-text="Apply with Shortlist">
              Apply with Shortlist
            </span>
            <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-sky-500 px-2 text-xs font-bold text-white">
              {shortlist.size}
            </span>
          </button>
        </div>

        {/* Shortlist Popover */}
        {cartOpen && (
          <div
            id="shortlist-cart-window"
            ref={cartPopoverRef}
            className={`fixed bottom-[4.5rem] right-4 left-4 md:bottom-32 md:right-6 md:left-auto z-[50] rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden ${shortlistViewMode === "compare" ? "w-[min(95vw,1200px)]" : "w-[min(520px,95vw)]"
              }`}
            style={{ maxHeight: "80vh" }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="font-semibold text-slate-900">Shortlisted products (max 5)</div>
              <div className="flex items-center gap-3">
                {shortlistedArray.length > 0 && (
                  <div className="flex items-center gap-2 border border-slate-200 rounded-lg p-0.5">
                    <button
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${shortlistViewMode === "list"
                        ? "bg-sky-600 text-white"
                        : "text-slate-600 hover:bg-slate-50"
                        }`}
                      onClick={() => setShortlistViewMode("list")}
                    >
                      List
                    </button>
                    <span className={compareHighlight ? "compare-highlight-ring" : ""}>
                      <button
                        className={`relative z-10 px-3 py-1 text-xs font-semibold rounded-md transition-colors ${shortlistViewMode === "compare"
                          ? "bg-sky-600 text-white"
                          : "text-slate-600 hover:bg-slate-50"
                          }`}
                        onClick={() => setShortlistViewMode("compare")}
                      >
                        Compare
                      </button>
                    </span>
                  </div>
                )}
                <button
                  className="text-xs font-semibold text-slate-600 underline underline-offset-2 disabled:opacity-40"
                  onClick={async () => {
                    try {
                      if (shortlist.size === 0) return;
                      if (isAuthenticated()) {
                        const result = await deleteAllShortlistedProducts();
                        if (!result.success) {
                          setEphemeralError(result.error || "Failed to clear shortlist");
                          return;
                        }
                        try {
                          localStorage.setItem(STORAGE_KEYS.SHORTLIST_PENDING, JSON.stringify([]));
                          localStorage.setItem(STORAGE_KEYS.SHORTLIST, JSON.stringify([]));
                        } catch { /* ignore */ }
                      } else {
                        try {
                          localStorage.setItem(STORAGE_KEYS.SHORTLIST_PENDING, JSON.stringify([]));
                        } catch { /* ignore */ }
                      }
                      setShortlist(new Set());
                      setShortlistedDetailsMap(new Map());
                      setShortlistDetailsFor(null);
                    } catch (err) {
                      reportError(err, { where: "Shortlist.clearAll" });
                    }
                  }}
                  disabled={shortlist.size === 0}
                >
                  Clear all
                </button>
                <button
                  type="button"
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300"
                  onClick={() => setCartOpen(false)}
                  aria-label="Close shortlist"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {shortlistDetailsProduct && (
              <div className="absolute inset-0 z-50 bg-white flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50"
                    onClick={() => setShortlistDetailsFor(null)}
                  >
                    ← Back
                  </button>

                  <div className="min-w-0 text-sm font-semibold text-slate-900 truncate">
                    Shortlist • Full details
                  </div>

                  <button
                    type="button"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 hover:bg-slate-50"
                    onClick={() => setShortlistDetailsFor(null)}
                    aria-label="Close"
                    title="Close"
                  >
                    <X className="h-4 w-4 text-slate-700" />
                  </button>
                </div>

                <div className="flex-1 overflow-auto p-3 min-h-0">
                  {(() => {
                    const fallbackCtx = fallbackParamsForCards;
                    const p = shortlistDetailsProduct;
                    const snapshot = shortlistDetailsFor ? shortlistedDisplaySnapshots.get(shortlistDetailsFor) : null;
                    const calc = getEffectiveCalc(p, filtersApplied.repaymentType, fallbackCtx) || {};
                    const { ltvDisplay, mortgageTermDisplay, icrDisplay } = getBackendDisplayMetrics(p, calc, fallbackCtx);

                    const productId = p?.name || p?.product_id || "—";
                    const productTermYears = parseFloat(p?.product_termsyears || p?.product_term_years || "0");
                    const ltvShown = snapshot?.ltv != null ? Math.round(Number(snapshot.ltv)) : (Number.isFinite(ltvDisplay) ? Math.round(ltvDisplay) : undefined);
                    const termShown = snapshot?.mortgageTermYears != null ? Number(snapshot.mortgageTermYears) : (Number.isFinite(mortgageTermDisplay) ? mortgageTermDisplay : NaN);
                    const icrShown = snapshot?.icr != null ? Number(snapshot.icr) : (Number.isFinite(icrDisplay) ? icrDisplay : NaN);
                    const monthlyPaymentShown = snapshot?.monthlyPayment != null ? safeNum(snapshot.monthlyPayment) : calc?.monthly_payment;
                    const totalCostShown = snapshot?.totalCost != null ? safeNum(snapshot.totalCost) : calc?.total_cost;
                    return (
                      <div className="space-y-3">
                        <div className="flex flex-col gap-1 min-w-0">
                          <div className="min-w-0 overflow-x-auto overflow-y-hidden hide-scrollbar touch-pan-x overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                            <div className="flex w-max max-w-none flex-nowrap items-center gap-2">
                              <ChipRow term={productTermYears} type={p?.product_type} ltv={ltvShown} productTermRaw={firstNonEmpty(p?.product_termsyears, p?.product_term_years, p?.product_term)} freeValuation={productHasFreeValuation(p)} nowrap compact />
                            </div>
                          </div>
                          <div className="text-[12px] text-slate-600">
                            Product ID: <span className="font-semibold text-slate-900">{productId}</span>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                          <div className="px-4 py-3 md:px-6 md:py-4 border-b border-slate-200">
                            <div className="text-xs font-semibold uppercase tracking-wide text-sky-600">
                              Product details
                            </div>
                            <div className="mt-1 text-sm md:text-lg font-semibold text-slate-900 break-words">
                              {p?.product_name || "Mortgage product"}
                            </div>
                          </div>

                          <div className="px-4 py-3 md:px-6 md:py-4">
                            <dl className="space-y-1.5 text-sm md:text-base">
                              <DetailRow label="APRC" value={formatAprcValue(getAprcValue(p))} strong />
                              {(() => {
                                const propertyValue = safeNum(fallbackCtx?.propertyValue) || safeNum(p?.property_value);
                                return propertyValue > 0 ? (
                                  <DetailRow label="Property value" value={`£${Num(propertyValue)}`} strong />
                                ) : null;
                              })()}
                              <DetailRow
                                label="Mortgage term"
                                value={Number.isFinite(termShown) ? `${termShown} yrs` : "—"}
                              />
                              <DetailRow
                                label="Monthly payment p/m"
                                value={`£${Num(monthlyPaymentShown)}`}
                                strong
                              />
                              <DetailRow
                                label="Cost over product term"
                                value={`£${Num(totalCostShown)}`}
                              />
                              {filtersApplied.repaymentType === "Repayment" && Number.isFinite(calc?.remaining_balance) ? (
                                <DetailRow
                                  label="Remaining balance"
                                  value={`£${Num(calc.remaining_balance)}`}
                                />
                              ) : null}
                              <DetailRow label="Product fee" value={formatProductFeeDisplay(p)} />
                              <DetailRow label="Total fees" value={`£${Num(calc?.total_fees)}`} />
                              <DetailRow
                                label="DSC / ICR"
                                value={formatIcrPercent(icrShown)}
                                strong
                              />
                            </dl>
                            {(() => {
                              const ercSource = (p.shortlisted_erc && Array.isArray(p.shortlisted_erc))
                                ? ensureErcYears1To5(p.shortlisted_erc)
                                : (p.erc && Array.isArray(p.erc))
                                  ? ensureErcYears1To5(p.erc)
                                  : buildErcFromYearFields(p);
                              const ercYears = ercSource.map((e) => ({
                                year: e.year,
                                value: e.charge_percent ?? e.charge ?? 0,
                              }));
                              return (
                                <div className="mt-4 pt-4 border-t border-slate-200">
                                  <div className="text-sm font-semibold text-slate-900 mb-2">
                                    Early repayment charges
                                  </div>
                                  <dl className="grid grid-cols-2 md:grid-cols-1 gap-x-2 gap-y-1.5 [&>*]:flex [&>*]:flex-col [&>*]:items-start [&>*]:gap-0.5 [&>*]:min-w-0 [&_dd]:text-left">
                                    {ercYears.map((erc) => (
                                      <DetailRow
                                        key={erc.year}
                                        label={`Year ${erc.year}`}
                                        value={formatErcPercent(erc.value)}
                                      />
                                    ))}
                                  </dl>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            <div className={`${shortlistViewMode === "compare" ? "max-h-[60vh]" : "max-h-[50vh]"} overflow-auto min-h-0 ${shortlistViewMode === "list" ? "divide-y divide-slate-100" : ""}`}>
              {shortlistedArray.length === 0 ? (
                <div className="p-4 text-sm text-slate-500">No products shortlisted yet.</div>
              ) : shortlistViewMode === "compare" ? (
                // Comparison Table View - horizontal scroll on mobile, responsive
                <div className="p-2 md:p-4">
                  <div className="overflow-x-auto overflow-y-hidden -mx-2 px-2 md:-mx-4 md:px-4 touch-pan-x" style={{ WebkitOverflowScrolling: "touch" }}>
                    <table className="w-full border-collapse min-w-[320px]">
                      <thead>
                        <tr className="border-b-2 border-slate-200">
                          <th className="text-left py-2 px-2 md:py-3 md:px-3 text-[11px] md:text-xs font-semibold text-slate-600 uppercase tracking-wide sticky left-0 bg-white z-10 min-w-[80px] md:min-w-[100px]">
                            Feature
                          </th>
                          {shortlistedArray.map((p, idx) => {
                            const sid = p?._shortlistId ?? p?.name;
                            const snapshot = shortlistedDisplaySnapshots.get(sid);
                            const fallbackCtx = fallbackParamsForCards;
                            const calc = getEffectiveCalc(p, filtersApplied.repaymentType, fallbackCtx);
                            const productTermYears = parseFloat(p.product_termsyears || p.product_term_years || "0");
                            const { ltvDisplay } = getBackendDisplayMetrics(p, calc, fallbackCtx);
                            const ltvShown = snapshot?.ltv != null ? Math.round(Number(snapshot.ltv)) : (Number.isFinite(ltvDisplay) ? Math.round(ltvDisplay) : undefined);

                            return (
                              <th
                                key={p?.name || idx}
                                className="text-center py-2 px-1.5 md:py-3 md:px-3 text-[11px] md:text-xs font-semibold text-slate-900 min-w-[90px] sm:min-w-[120px] md:min-w-[180px] border-l border-slate-200"
                              >
                                <div className="flex flex-col items-center gap-1 md:gap-2">
                                  <div className="font-semibold text-xs md:text-sm text-slate-900 truncate max-w-full">
                                    {p?.product_name || "Product"}
                                  </div>
                                  <div className="w-full min-w-0 overflow-x-auto overflow-y-hidden hide-scrollbar touch-pan-x overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                                    <div className="flex w-max max-w-none justify-center">
                                      <ChipRow term={productTermYears} type={p?.product_type} ltv={ltvShown} productTermRaw={firstNonEmpty(p?.product_termsyears, p?.product_term_years, p?.product_term)} freeValuation={productHasFreeValuation(p)} nowrap compact />
                                    </div>
                                  </div>
                                  {linkedProducts.has(p?._shortlistId ?? p?.name) ? (
                                    <div className="mt-1 flex flex-col items-center gap-1">
                                      <button
                                        type="button"
                                        disabled
                                        className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700 cursor-not-allowed opacity-75"
                                        title="This product is linked to an ongoing application and cannot be removed"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                        Remove
                                      </button>
                                      <span className="text-[10px] text-amber-600 font-medium">Linked to application</span>
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      className="mt-1 inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
                                      onClick={() => toggleShortlist(p?._shortlistId ?? p?.name)}
                                      title="Remove from shortlist"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const rows = [];
                          const fallbackCtx = fallbackParamsForCards;

                          // Interest Rate
                          rows.push(
                            <tr key="rate" className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10">
                                Interest Rate
                              </td>
                              {shortlistedArray.map((p, idx) => {
                                const sid = p?._shortlistId ?? p?.name;
                                const snapshot = shortlistedDisplaySnapshots.get(sid);
                                const calc = getEffectiveCalc(p, filtersApplied.repaymentType, fallbackCtx);
                                const rateVal = snapshot?.rate != null ? safeNum(snapshot.rate) : safeNum(calc?.interest_rate);
                                return (
                                  <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200">
                                    <span className="font-semibold">{rateVal.toFixed(2)}%</span>
                                  </td>
                                );
                              })}
                            </tr>
                          );

                          // APRC
                          rows.push(
                            <tr key="aprc" className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10">
                                APRC
                              </td>
                              {shortlistedArray.map((p, idx) => (
                                <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200">
                                  <span className="font-semibold">{formatAprcValue(getAprcValue(p))}</span>
                                </td>
                              ))}
                            </tr>
                          );

                          // Property Value
                          rows.push(
                            <tr key="property_value" className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10">
                                Property Value
                              </td>
                              {shortlistedArray.map((p, idx) => {
                                const propertyValue = safeNum(fallbackCtx?.propertyValue) || safeNum(p?.property_value);
                                return (
                                  <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200">
                                    {propertyValue > 0 ? (
                                      <span className="font-semibold">£{Num(propertyValue)}</span>
                                    ) : (
                                      <span className="text-slate-400">—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );

                          // Loan Amount
                          rows.push(
                            <tr key="loan_amount" className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10">
                                Loan Amount
                              </td>
                              {shortlistedArray.map((p, idx) => {
                                const calc = getEffectiveCalc(p, filtersApplied.repaymentType, fallbackCtx);
                                const loanAmount = safeNum(calc?.loan_amount ?? fallbackCtx?.mortgageAmount ?? p?.mortgage_amount ?? 0);
                                return (
                                  <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200">
                                    {loanAmount > 0 ? (
                                      <span className="font-semibold">£{Num(loanAmount)}</span>
                                    ) : (
                                      <span className="text-slate-400">—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );

                          // LTV
                          rows.push(
                            <tr key="ltv" className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10">
                                LTV
                              </td>
                              {shortlistedArray.map((p, idx) => {
                                const calc = getEffectiveCalc(p, filtersApplied.repaymentType, fallbackCtx);
                                const { ltvDisplay } = getBackendDisplayMetrics(p, calc, fallbackCtx);
                                const ltvShown = Number.isFinite(ltvDisplay) ? Math.round(ltvDisplay) : undefined;
                                return (
                                  <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200">
                                    {ltvShown != null ? (
                                      <span className="font-semibold">{ltvShown}%</span>
                                    ) : (
                                      <span className="text-slate-400">—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );

                          // Mortgage Term
                          rows.push(
                            <tr key="mortgage_term" className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10">
                                Mortgage Term
                              </td>
                              {shortlistedArray.map((p, idx) => {
                                const calc = getEffectiveCalc(p, filtersApplied.repaymentType, fallbackCtx);
                                const { mortgageTermDisplay } = getBackendDisplayMetrics(p, calc, fallbackCtx);
                                const termShown = Number.isFinite(mortgageTermDisplay) ? mortgageTermDisplay : null;
                                return (
                                  <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200">
                                    {termShown != null ? (
                                      <span className="font-semibold">{termShown} yrs</span>
                                    ) : (
                                      <span className="text-slate-400">—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );

                          // Monthly Payment
                          rows.push(
                            <tr key="monthly_payment" className="border-b border-slate-100 hover:bg-slate-50 bg-sky-50">
                              <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-semibold text-slate-900 sticky left-0 bg-sky-50 z-10">
                                Monthly Payment
                              </td>
                              {shortlistedArray.map((p, idx) => {
                                const sid = p?._shortlistId ?? p?.name;
                                const snapshot = shortlistedDisplaySnapshots.get(sid);
                                const calc = getEffectiveCalc(p, filtersApplied.repaymentType, fallbackCtx);
                                const val = snapshot?.monthlyPayment != null ? safeNum(snapshot.monthlyPayment) : calc?.monthly_payment;
                                return (
                                  <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm font-bold text-slate-900 border-l border-slate-200 bg-sky-50">
                                    £{Num(val)}
                                  </td>
                                );
                              })}
                            </tr>
                          );

                          // Arrangement Fee
                          rows.push(
                            <tr key="arrangement_fee" className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10">
                                Arrangement Fee
                              </td>
                              {shortlistedArray.map((p, idx) => (
                                <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200">
                                  <span className="font-semibold">{formatProductFeeDisplay(p)}</span>
                                </td>
                              ))}
                            </tr>
                          );

                          // Cost over Product Term
                          rows.push(
                            <tr key="total_cost" className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10">
                                Cost over Product Term
                              </td>
                              {shortlistedArray.map((p, idx) => {
                                const sid = p?._shortlistId ?? p?.name;
                                const snapshot = shortlistedDisplaySnapshots.get(sid);
                                const calc = getEffectiveCalc(p, filtersApplied.repaymentType, fallbackCtx);
                                const val = snapshot?.totalCost != null ? safeNum(snapshot.totalCost) : calc?.total_cost;
                                return (
                                  <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200">
                                    <span className="font-semibold">£{Num(val)}</span>
                                  </td>
                                );
                              })}
                            </tr>
                          );

                          // Total Fees
                          rows.push(
                            <tr key="total_fees" className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10">
                                Total Fees
                              </td>
                              {shortlistedArray.map((p, idx) => {
                                const calc = getEffectiveCalc(p, filtersApplied.repaymentType, fallbackCtx);
                                return (
                                  <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200">
                                    <span className="font-semibold">£{Num(calc?.total_fees)}</span>
                                  </td>
                                );
                              })}
                            </tr>
                          );

                          // DSC / ICR
                          rows.push(
                            <tr key="icr" className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10">
                                DSC / ICR
                              </td>
                              {shortlistedArray.map((p, idx) => {
                                const calc = getEffectiveCalc(p, filtersApplied.repaymentType, fallbackCtx);
                                const { icrDisplay } = getBackendDisplayMetrics(p, calc, fallbackCtx);
                                const icrShown = Number.isFinite(icrDisplay) ? icrDisplay : null;
                                return (
                                  <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200">
                                    {icrShown != null ? (
                                      <span className="font-semibold">{formatIcrPercent(icrShown)}</span>
                                    ) : (
                                      <span className="text-slate-400">—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );

                          // Product Term
                          rows.push(
                            <tr key="product_term" className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10">
                                Product Term
                              </td>
                              {shortlistedArray.map((p, idx) => {
                                const productTermRaw = firstNonEmpty(
                                  p?.product_termsyears,
                                  p?.product_term_years,
                                  p?.product_term
                                );
                                const productTermYears = parseFloat(productTermRaw || "0");
                                const isLifetimeProduct = normalizeFilterToken(productTermRaw) === "lifetime";
                                return (
                                  <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200">
                                    {isLifetimeProduct ? (
                                      <span className="font-semibold">Lifetime</span>
                                    ) : productTermYears > 0 ? (
                                      <span className="font-semibold">{productTermYears} yrs</span>
                                    ) : (
                                      <span className="text-slate-400">—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );

                          // ERC Year 1-5
                          [1, 2, 3, 4, 5].forEach((yearNum) => {
                            rows.push(
                              <tr key={`erc_${yearNum}`} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10">
                                  ERC Year {yearNum}
                                </td>
                                {shortlistedArray.map((p, idx) => {
                                  const ercArr = (p.shortlisted_erc && Array.isArray(p.shortlisted_erc))
                                    ? ensureErcYears1To5(p.shortlisted_erc)
                                    : (p.erc && Array.isArray(p.erc))
                                      ? ensureErcYears1To5(p.erc)
                                      : buildErcFromYearFields(p);
                                  const ercYear = ercArr.find((e) => e.year === yearNum);
                                  const pct = ercYear?.charge_percent ?? ercYear?.charge ?? 0;
                                  return (
                                    <td key={p?.name || idx} className="py-2 px-1.5 md:py-3 md:px-3 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200">
                                      <span className="font-semibold">{formatErcPercent(pct)}</span>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          });

                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                // List View (existing)
                shortlistedArray.map((p) => {
                  const sid = p?._shortlistId ?? p?.name;
                  const snapshot = shortlistedDisplaySnapshots.get(sid);
                  const fallbackCtx = fallbackParamsForCards;
                  const calc = getEffectiveCalc(p, filtersApplied.repaymentType, fallbackCtx);
                  const productTermYears = parseFloat(p.product_termsyears || p.product_term_years || "0");
                  const { ltvDisplay } = getBackendDisplayMetrics(p, calc, fallbackCtx);
                  const ltvShown = snapshot?.ltv != null ? Math.round(Number(snapshot.ltv)) : (Number.isFinite(ltvDisplay) ? Math.round(ltvDisplay) : undefined);
                  const rateShown = snapshot?.rate != null ? safeNum(snapshot.rate) : safeNum(calc?.interest_rate);
                  const monthlyShown = snapshot?.monthlyPayment != null ? safeNum(snapshot.monthlyPayment) : calc?.monthly_payment;
                  const totalCostShown = snapshot?.totalCost != null ? safeNum(snapshot.totalCost) : calc?.total_cost;
                  const icrShown = snapshot?.icr != null ? Number(snapshot.icr) : safeNum(calc?.icr);

                  return (
                    <div key={p?.name} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">
                            {p?.product_name || "Mortgage product"}
                          </div>
          <div className="mt-1 min-w-0 overflow-x-auto overflow-y-hidden hide-scrollbar touch-pan-x overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                            <div className="flex w-max max-w-none flex-nowrap items-center gap-2">
                              <ChipRow term={productTermYears} type={p?.product_type} ltv={ltvShown} productTermRaw={firstNonEmpty(p?.product_termsyears, p?.product_term_years, p?.product_term)} freeValuation={productHasFreeValuation(p)} nowrap compact />
                            </div>
                          </div>
                          <div className="mt-2 text-sm text-slate-700">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">Rate</span>
                              <span className="font-semibold">{rateShown.toFixed(2)}%</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">Monthly</span>
                              <span className="font-semibold">£{Num(monthlyShown)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">Total cost</span>
                              <span className="font-semibold">£{Num(totalCostShown)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">DSC / ICR</span>
                              <span className="font-semibold">{formatIcrPercent(icrShown)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50"
                            onClick={() => setShortlistDetailsFor(p?.name)}
                          >
                            Full details
                          </button>

                          {linkedProducts.has(p?._shortlistId ?? p?.name) ? (
                            <div className="flex flex-col items-end gap-1">
                              <button
                                type="button"
                                disabled
                                className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 cursor-not-allowed opacity-75"
                                title="This product is linked to an ongoing application and cannot be removed"
                              >
                                <Trash2 className="h-4 w-4" />
                                Remove
                              </button>
                              <span className="text-xs text-amber-600 font-medium">Linked to application</span>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
                              onClick={() => toggleShortlist(p?._shortlistId ?? p?.name)}
                              title="Remove from shortlist"
                            >
                              <Trash2 className="h-4 w-4" />
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="border-t border-slate-200 p-3">
              <button
                id="continue-shortlist"
                type="button"
                onClick={onApplyShortlist}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
                disabled={shortlistedArray.length === 0}
              >
                Continue with shortlisted products
              </button>
            </div>
          </div>
        )}

        <MobileFiltersDrawer
          open={mobileFiltersOpen}
          onClose={() => setMobileFiltersOpen(false)}
          filtersDraft={filtersDraft}
          setFilterDraft={setFilterDraft}
          onLTVFilterChange={onLTVFilterChange}
          onClearAll={clearAllFiltersDraft}
          onShow={handleMobileShowProducts}
          productCount={visibleProducts.length}
          isComplexBtl={isComplexBtl}
        />

        <MobileSortDrawer
          open={mobileSortOpen}
          onClose={() => setMobileSortOpen(false)}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />

        <button
          type="button"
          onClick={openReviewPrompt}
          className={SOURCING_FEEDBACK_BUTTON_CLASS}
        >
          <MessageSquareHeart className="h-4 w-4" />
          Feedback
        </button>

        <ReviewPromptModal
          open={reviewPromptOpen}
          onClose={dismissReviewPrompt}
          productCategory="BTL"
          contextLabel={`How was ${productLabel} sourcing today?`}
          defaultType="General Feedback"
        />
      </ErrorCtx.Provider>
    </ErrorBoundary>
  );
}
